import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { Mail, MapPin, ArrowLeft, Trash2, Star, ChevronRight, ChevronDown, ExternalLink, Pencil, SlidersHorizontal, ChevronsUpDown, AlertTriangle, ImagePlus, Film, Crop, Download, RefreshCw } from 'lucide-react';
import JSZip from 'jszip';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, rectSortingStrategy, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { commsInsert, commsUpdate, commsDelete, commsUpsertSetting, requireSession } from './commsUtils';
import { TeamBiosSection } from './teamBios';
import { GoogleSatelliteMap, parseCoordinates, queueGeocode } from '../projects/ProjectMapPage';
import { S, GOLD, statusClass, Chip, FilterBtn, BlockTitle, NoteArea, InlineInput, LockableField, LinkBadge, AddLine, BtnGhost, BtnPrimary, BackBtn, EmptyNote, Toast, ConfirmDialog, CommsModal, Field, ProjectCodesPicker } from './commsUi';
import { useCommsFieldVersions } from '../../hooks/useCommsFieldVersions';
import { PitchesTab } from './pitchTypes';
import { FolderAlertsPanel, FolderDriftBanner } from './folderAlertsPanel';
import { BRIO_PRESS_BASE, PLATFORMS, PRESS_KIT_DEFAULT_SCHEMA, callEdgeFunction, clearListingCache, fetchPressFileBlob, invalidateListing, Lightbox, KitCard, AssignToPostModal, TagAssetsModal, RankAssetsModal, SHORTLIST_MIN_RANK, useAssetTags, useAssetRanks, useRankMenu, updateAssetRanks, usePressFolderBrowser, useAssetSelection, SelectBar, slugTag  } from './pressAssets';
import { ShareAssetsModal, SharesPanel, ContactSharesPanel } from './pressShares';
import { ReviewMode } from './reviewMode';
import { VERSION_OPTIONS, readVersionPref, writeVersionPref } from './assetVariants';
import { ThemeAssetsSection, AssignToThemeModal } from './themeAssets';
import { koppenLabel, siteTypeLabel } from '../../utils/siteClassification';
import { NO_SPECIALISM, PHOTO_SPECIALISMS, effectiveSpecialism, specialismToStore, specialismRows, sortSpecialismOptions, searchVendors, collaboratorGroup, vendorInGroup } from '../../utils/vendorProjectLinks';
import { bedrockSourceTerms, resolveSoil, resolveBedrock } from '../../utils/subsurface';

const comms = supabase.schema('comms');
// ── Data hook ─────────────────────────────────────────────────────────────

function useCommsData(tab, rev) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const prevTab = useRef(null);

  useEffect(() => {
    // Only show the full loading spinner on tab changes, not on background refreshes (rev bumps)
    if (tab !== prevTab.current) {
      setLoading(true);
      prevTab.current = tab;
    }
    const fetches = {};

    if (tab === 'comms-practice') {
      fetches.themes = comms.from('themes').select('*').order('sort_order');
      fetches.programmes = comms.from('programmes').select('*').order('name');
      fetches.exhibitions = comms.from('exhibitions').select('*').order('date_start', { ascending: false });
      fetches.lectures = comms.from('lectures').select('*').order('date', { ascending: false });
      fetches.awards = comms.from('awards').select('*').order('year', { ascending: false });
      fetches.publications = comms.from('publications').select('*').order('date', { ascending: false });
      fetches.founders = comms.from('founders').select('*').order('comms_id');
      fetches.collaborators = comms.from('collaborators').select('*').order('name');
      fetches.materials = comms.from('materials').select('*').order('name');
      fetches.practice_settings = comms.from('practice_settings').select('*');
      fetches.monograph_settings = comms.from('monograph_settings').select('*');
      fetches.projects = comms.from('projects').select('comms_id, name, themes');
      fetches.pub_projects = supabase.from('projects').select('id, name, project_code').eq('comms_enabled', true).is('deleted_at', null).order('project_code');
      fetches.pub_contacts = supabase.from('vendors').select('id, company_name').eq('category', 'Media').eq('comms_enabled', true).order('company_name');
      // Scope to press people only (contacts under Media + comms-enabled outlets, ~155 rows).
      // Fetching all vendor_contacts hit Supabase's 1000-row cap and silently dropped the
      // alphabetical tail (e.g. Rutaraj Parikh, Stephen Crafti, Unnati Saini).
      fetches.pub_people = supabase.from('vendor_contacts').select('id, vendor_id, name, designation, vendors!inner(category, comms_enabled)').eq('vendors.category', 'Media').eq('vendors.comms_enabled', true).order('name');
      // Team bios pick the member from the app's own employee list. Both sides
      // of the active / non-active toggle are fetched, so `is_active` is not
      // filtered here the way the pitches tab filters it.
      fetches.users = supabase.from('users').select('id, name, email, job_role, is_active, status, office_location, profile_intro, deleted_at').is('deleted_at', null).order('name');
      fetches.user_profiles = supabase.from('user_profiles').select('user_id, profile_intro, profile_photo_rect, designation, current_job_role');
      fetches.team_bios = comms.from('team_bios').select('user_id, bio');
    } else if (tab === 'comms-projects') {
      fetches.pub_projects = supabase.from('projects').select('id, name, project_code, category, status, stage_timeline, city, country').eq('comms_enabled', true).order('project_code');
      fetches.projects = comms.from('projects').select('*');
      // The project page lists comms.awards rows whose project_codes contain the project.
      fetches.awards = comms.from('awards').select('*').order('year', { ascending: false });
      fetches.publications = comms.from('publications').select('comms_id, title, magazine, date, year, url, is_interview, author_contact_id, project_codes, notes').order('date', { ascending: false });
      fetches.themes = comms.from('themes').select('*').order('sort_order');
      fetches.contacts = supabase.from('vendors').select('id, company_name, specialism, instagram, company_email, category, comms_enabled').eq('category', 'Media').eq('comms_enabled', true).order('company_name');
      fetches.vendor_contacts = supabase.from('vendor_contacts').select('id, vendor_id, name, email, designation');
      // Press people only (Media + comms-enabled), used to resolve publication author bylines
      // without hitting the 1000-row cap that drops the alphabetical tail of all vendor_contacts.
      fetches.pub_people = supabase.from('vendor_contacts').select('id, vendor_id, name, designation, vendors!inner(category, comms_enabled)').eq('vendors.category', 'Media').eq('vendors.comms_enabled', true).order('name');
      fetches.social_cal = comms.from('social_cal').select('comms_id, title, post_date, status, platforms, platform').order('post_date', { ascending: false });
    } else if (tab === 'comms-social') {
      fetches.social_cal = comms.from('social_cal').select('*').order('post_date');
      fetches.post_images = comms.from('social_post_images').select('*').order('sort_order');
      fetches.practice_settings = comms.from('practice_settings').select('*');
      fetches.ig_accounts = supabase.from('vendors').select('id, company_name, instagram').not('instagram', 'is', null).neq('instagram', '').order('company_name');
    } else if (tab === 'comms-calendar') {
      fetches.events = comms.from('events').select('*').order('date_start');
      fetches.awards = comms.from('awards').select('*').order('year', { ascending: false });
      fetches.opencalls = comms.from('opencalls').select('*').order('deadline');
      // Awards link to public.projects by code; this is the list the project picker offers.
      fetches.pub_projects = supabase.from('projects').select('id, name, project_code').eq('comms_enabled', true).is('deleted_at', null).order('project_code');
    } else if (tab === 'comms-contacts') {
      fetches.contacts = supabase.from('vendors').select('*').eq('category', 'Media').eq('comms_enabled', true).order('company_name');
      fetches.vendor_contacts = supabase.from('vendor_contacts').select('id, vendor_id, name, email, designation');
      // Press people only (Media + comms-enabled), used to resolve publication author bylines
      // without hitting the 1000-row cap that drops the alphabetical tail of all vendor_contacts.
      fetches.pub_people = supabase.from('vendor_contacts').select('id, vendor_id, name, designation, vendors!inner(category, comms_enabled)').eq('vendors.category', 'Media').eq('vendors.comms_enabled', true).order('name');
      fetches.comms_profiles = comms.from('contacts').select('*').not('vendor_id', 'is', null);
      fetches.projects = comms.from('projects').select('comms_id, name, project_code, themes').order('project_code');
      fetches.themes = comms.from('themes').select('*');
      fetches.publications = comms.from('publications').select('comms_id, title, magazine, date, year, url, is_interview, contact_ids, author_contact_id, project_codes').order('date', { ascending: false });
    } else if (tab === 'comms-pitches') {
      fetches.pitch_types = comms.from('pitch_types').select('*').order('sort_order');
      fetches.pitch_decks = comms.from('pitch_decks').select('*').order('updated_at', { ascending: false });
      fetches.themes = comms.from('themes').select('comms_id, label, description, sort_order').order('sort_order');
      fetches.project_categories = supabase.from('project_categories').select('id, label, sort_order').eq('is_active', true).order('sort_order');
      // Merged below into data.projects (project_code, name, typology, confidential) for the chapter pickers.
      fetches.pub_projects = supabase.from('projects').select('id, name, project_code, category, status, city, country, project_size, plot_area, site_types, climate_zone, elevation_m').eq('comms_enabled', true).is('deleted_at', null).order('project_code');
      fetches.projects = comms.from('projects').select('comms_id, project_code, name, priority, confidential, headline, website_text, year_start, year_complete');
      // The selection engine's inputs (src/pitch/selectPitchContent.js).
      fetches.asset_ranks = comms.from('asset_ranks').select('dropbox_file_id, dropbox_path, file_name, thumb_url, is_video, project_code, rank');
      fetches.asset_tags = comms.from('asset_tags').select('dropbox_file_id, dropbox_path, file_name, thumb_url, is_video, project_code, tags');
      fetches.awards = comms.from('awards').select('comms_id, award_body, category, year, result, project_codes').order('year', { ascending: false });
      // `date` carries the real year (YYYY-MM on every row); `year` is filled in
      // on 14 of 78. Without it the renderer's fallback left most of the
      // publications list with a blank year column.
      fetches.publications = comms.from('publications').select('comms_id, title, magazine, date, year, project_codes');
      fetches.theme_assets = comms.from('theme_assets').select('comms_id, theme_id, dropbox_file_id, dropbox_path, file_name, thumb_url, caption, credit, sort_order');
      // The renderer's practice pages: studio texts, founders, team portraits.
      fetches.practice_settings = comms.from('practice_settings').select('*');
      fetches.founders = comms.from('founders').select('*').order('comms_id');
      fetches.users = supabase.from('users').select('id, name, job_role, status, office_location, deleted_at').is('deleted_at', null).eq('status', 'active').order('name');
      fetches.user_profiles = supabase.from('user_profiles').select('user_id, profile_photo_rect');
      // Where each member's portrait folder sits in Dropbox, so Prepare images can
      // take the uncropped original rather than the app's small crop.
      fetches.team_portrait_folders = comms.from('team_portrait_folders').select('user_id, bucket, folder_name, portrait_file');
      fetches.inquiries = supabase.from('inquiries').select('id, project_name, project_type, estimated_fee, plot_area, client_id, client_name_override, client_org_name_override, location_city, brief_notes, status, inquiry_date').eq('status', 'active').order('inquiry_date', { ascending: false });
      fetches.clients = supabase.from('clients').select('id, name, display_name, client_type');
      // Latest EUR to INR rate: inquiry fees are INR, the fee bands are EUR.
      fetches.fx_rates = supabase.from('fx_rates_monthly').select('month, avg_rate').eq('pair', 'EURINR').order('month', { ascending: false }).limit(1);
    } else if (tab === 'comms-inbox') {
      fetches.inbox = comms.from('inbox').select('*').order('received_at', { ascending: false });
      fetches.rtasks = comms.from('rtasks').select('*').order('due_date');
      fetches.contacts = supabase.from('vendors').select('id, company_name').eq('category', 'Media').eq('comms_enabled', true).order('company_name');
    }

    const keys = Object.keys(fetches);
    if (keys.length === 0) { setLoading(false); return; }

    Promise.all(keys.map(k => fetches[k])).then(results => {
      const merged = {};
      keys.forEach((k, i) => { merged[k] = results[i].data || []; });
      if (merged.practice_settings) {
        merged.practice = {};
        merged.practice_settings.forEach(row => {
          try { merged.practice[row.key] = JSON.parse(row.value); } catch { merged.practice[row.key] = row.value; }
        });
      }
      if (merged.monograph_settings) {
        merged.monograph = {};
        merged.monograph_settings.forEach(row => {
          try { merged.monograph[row.key] = JSON.parse(row.value); } catch { merged.monograph[row.key] = row.value; }
        });
      }
      // Merge public.projects (comms_enabled=true) with comms.projects enrichment
      if (merged.pub_projects !== undefined) {
        const enrichMap = {};
        (merged.projects || []).forEach(cp => { if (cp.project_code) enrichMap[cp.project_code] = cp; });
        merged.projects = (merged.pub_projects || []).map(pp => {
          const enrich = enrichMap[pp.project_code] || {};
          return {
            comms_id: pp.project_code,
            name: pp.name,
            // Location lives on public.projects now — it is registry data, and
            // it is the only place in the app you can edit it. The comms value
            // stays as a fallback so projects filled in before the move keep
            // their city until someone re-enters it.
            city: pp.city || enrich.city || '',
            country: pp.country || enrich.country || '',
            typology: pp.category || enrich.typology || '',
            year_start: enrich.year_start || '',
            year_complete: enrich.year_complete || '',
            byline: enrich.byline || '',
            headline: enrich.headline || '',
            nts: enrich.nts || {},
            kit: enrich.kit || {},
            coverage: enrich.coverage || [],
            awards_list: enrich.awards_list || [],
            comms_ideas: enrich.comms_ideas || '',
            website_text: enrich.website_text || '',
            themes: enrich.themes || [],
            priority: !!enrich.priority,
            confidential: !!enrich.confidential,
            project_code: pp.project_code,
            status: pp.status || 'active',
            stage_timeline: pp.stage_timeline || {},
            // Site facts for the deck's project closing page.
            project_size: pp.project_size ?? null,
            plot_area: pp.plot_area ?? null,
            site_types: pp.site_types || [],
            climate_zone: pp.climate_zone || '',
            elevation_m: pp.elevation_m ?? null,
            _pub_id: pp.id,
            _has_enrich: !!enrichMap[pp.project_code],
          };
        });
        merged.projects.sort((a, b) => (a.project_code || '').localeCompare(b.project_code || ''));
      }
      setData(merged);
      setLoading(false);
    });
  }, [tab, rev]);

  return { data, loading };
}

// ── PRACTICE TAB ──────────────────────────────────────────────────────────

function PracticeTab({ data, canEdit, refresh, showToast, onOpenTheme, onOpenProgramme }) {
  const [sub, setSub] = useState('themes');
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');

  const themes = data.themes || [];
  const projects = data.projects || [];

  const themeProjectCount = themeId => projects.filter(p => Array.isArray(p.themes) && p.themes.includes(themeId)).length;

  const filteredThemes = themes.filter(t =>
    !search || t.label?.toLowerCase().includes(search.toLowerCase()) || t.description?.toLowerCase().includes(search.toLowerCase())
  );

  const addTheme = async () => {
    if (!newName.trim()) return;
    try {
      await commsInsert('themes', { comms_id: 'th-' + Date.now(), label: newName.trim(), sort_order: themes.length + 1 });
      setNewName(''); refresh(); showToast('Theme added');
    } catch (e) { showToast(e.message || 'Error', 'error'); }
  };

  const addProgramme = async () => {
    if (!newName.trim()) return;
    try {
      await commsInsert('programmes', { comms_id: 'prg-' + Date.now(), name: newName.trim(), status: 'Idea' });
      setNewName(''); refresh(); showToast('Programme added');
    } catch (e) { showToast(e.message || 'Error', 'error'); }
  };

  const addExhibition = async () => {
    if (!newName.trim()) return;
    try {
      await commsInsert('exhibitions', { comms_id: 'exh-' + Date.now(), title: newName.trim() });
      setNewName(''); refresh(); showToast('Exhibition added');
    } catch (e) { showToast(e.message || 'Error', 'error'); }
  };

  const addLecture = async () => {
    if (!newName.trim()) return;
    try {
      await commsInsert('lectures', { comms_id: 'lec-' + Date.now(), title: newName.trim() });
      setNewName(''); refresh(); showToast('Lecture added');
    } catch (e) { showToast(e.message || 'Error', 'error'); }
  };

  const addAward = async () => {
    if (!newName.trim()) return;
    try {
      await commsInsert('awards', { comms_id: 'aw-' + Date.now(), award_body: newName.trim() });
      setNewName(''); refresh(); showToast('Award added');
    } catch (e) { showToast(e.message || 'Error', 'error'); }
  };

  const cycleProgStatus = async (item) => {
    const cycle = { 'Idea': 'Plan', 'Plan': 'Running', 'Running': 'Idea' };
    const next = cycle[item.status] || 'Idea';
    try { await commsUpdate('programmes', 'comms_id', item.comms_id, { status: next }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const saveExhibNote = async (item, notes) => {
    try { await commsUpdate('exhibitions', 'comms_id', item.comms_id, { notes }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const del = async (table, id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await commsDelete(table, 'comms_id', id); refresh(); showToast('Deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const programmes = data.programmes || [];
  const exhibitions = data.exhibitions || [];
  const lectures = data.lectures || [];
  const awards = data.awards || [];
  const publications = data.publications || [];

  const addPublication = async () => {
    if (!newName.trim()) return;
    try {
      await commsInsert('publications', { comms_id: 'pub-' + Date.now(), magazine: newName.trim() });
      setNewName(''); refresh(); showToast('Publication added');
    } catch (e) { showToast(e.message || 'Error', 'error'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...S.eyebrow, marginBottom: 6 }}>Practice stream</div>
        <h1 style={S.h1}>Practice</h1>
        <p style={{ ...S.body, marginTop: 10, maxWidth: 560 }}>How BRIO builds a reputation beyond individual projects — the themes it wants to be known for, the programmes it hosts, where it has exhibited, and the lectures and talks it gives. Search across themes to find common ground.</p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['themes','programmes','exhibitions','lectures','awards','publications'].map(s => (
            <FilterBtn key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} active={sub === s} onClick={() => { setSub(s); setNewName(''); setSearch(''); }} />
          ))}
        </div>
        {canEdit && (() => {
          const cfg = {
            themes:       { placeholder: 'New theme…' ,        action: addTheme },
            programmes:   { placeholder: 'New programme…',     action: addProgramme },
            exhibitions:  { placeholder: 'New exhibition…',    action: addExhibition },
            lectures:     { placeholder: 'New lecture title…', action: addLecture },
            awards:       { placeholder: 'Award body…',        action: addAward },
            publications: { placeholder: 'Magazine name…',     action: addPublication },
          }[sub];
          return cfg ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <InlineInput value={newName} onChange={setNewName} placeholder={cfg.placeholder} style={{ maxWidth: 200 }} />
              <BtnGhost onClick={cfg.action}>+ Add {sub.replace(/s$/, '')}</BtnGhost>
            </div>
          ) : null;
        })()}
      </div>

      {sub === 'themes' && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search themes…" style={{ ...S.inputStyle, maxWidth: 260 }} />
          </div>
          <div style={S.grid2}>
            {filteredThemes.map(t => (
              <div key={t.comms_id} style={{ ...S.card, marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
                  <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => onOpenTheme(t.comms_id)}>
                    <div style={{ ...S.eyebrow, marginBottom: 4 }}>Practice theme</div>
                    <InlineInput
                      value={t.label}
                      onBlurSave={async v => { try { await commsUpdate('themes', 'comms_id', t.comms_id, { label: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
                      placeholder="Theme name…"
                      style={{ fontSize: 18, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0', width: '100%' }}
                    />
                  </div>
                  {canEdit && <button onClick={e => { e.stopPropagation(); del('themes', t.comms_id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>}
                </div>
                {t.description && <p style={{ ...S.body, marginTop: 4, marginBottom: 8 }}>{t.description}</p>}
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => onOpenTheme(t.comms_id)}>
                  <Chip label={`${themeProjectCount(t.comms_id)} project${themeProjectCount(t.comms_id) !== 1 ? 's' : ''}`} cls="line" />
                  <ChevronRight size={14} color="#6E6C66" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sub === 'programmes' && (
        <div style={S.grid2}>
          {programmes.map(p => (
            <div key={p.comms_id} style={{ ...S.card, marginBottom: 0, border: '1px solid rgba(27,27,25,.12)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0, cursor: canEdit ? 'default' : 'pointer' }} onClick={canEdit ? undefined : () => onOpenProgramme(p.comms_id)}>
                  <div style={{ ...S.eyebrow, marginBottom: 4 }}>Creative programme</div>
                  {canEdit
                    ? <InlineInput
                        value={p.name}
                        onBlurSave={async v => { try { await commsUpdate('programmes', 'comms_id', p.comms_id, { name: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
                        placeholder="Programme name…"
                        style={{ fontSize: 18, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0', width: '100%' }}
                      />
                    : <h3 style={{ ...S.h3, marginBottom: 0 }}>{p.name}</h3>}
                </div>
                {canEdit && <button onClick={e => { e.stopPropagation(); del('programmes', p.comms_id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0 }}><Trash2 size={13} /></button>}
              </div>
              {canEdit
                ? <InlineInput value={p.why || ''} onBlurSave={async v => { try { await commsUpdate('programmes', 'comms_id', p.comms_id, { why: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="A one-line description…" style={{ marginBottom: 8, width: '100%' }} />
                : p.why && <p style={{ ...S.body, marginBottom: 8 }}>{p.why}</p>}
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => onOpenProgramme(p.comms_id)}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Chip label={p.status || 'Idea'} cls={statusClass(p.status)} />
                  {p.freq && <Chip label={p.freq} cls="line" />}
                </div>
                <ChevronRight size={14} color="#6E6C66" />
              </div>
            </div>
          ))}
        </div>
      )}

      {sub === 'exhibitions' && (
        <div>
          <div style={S.grid2}>
            {exhibitions.map(ex => (
              <div key={ex.comms_id} style={{ ...S.card, marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                    <LinkBadge value={ex.url} onSave={async v => { try { await commsUpdate('exhibitions', 'comms_id', ex.comms_id, { url: v }); } catch {} }} />
                    <InlineInput
                      value={ex.title}
                      onBlurSave={async v => { try { await commsUpdate('exhibitions', 'comms_id', ex.comms_id, { title: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
                      placeholder="Exhibition title…"
                      style={{ flex: 1, fontSize: 15, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0' }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <Chip label={ex.status || 'Upcoming'} cls={ex.status === 'Past' ? 'pub' : 'pot'} onClick={async () => {
                      const next = ex.status === 'Past' ? 'Upcoming' : 'Past';
                      try { await commsUpdate('exhibitions', 'comms_id', ex.comms_id, { status: next }); refresh(); } catch (e) { showToast(e.message, 'error'); }
                    }} />
                    {canEdit && <button onClick={() => del('exhibitions', ex.comms_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>}
                  </div>
                </div>
                <InlineInput value={ex.venue} onBlurSave={async v => { try { await commsUpdate('exhibitions', 'comms_id', ex.comms_id, { venue: v }); } catch {} }} placeholder="Venue…" style={{ marginBottom: 4 }} />
                <InlineInput value={ex.date_start} onBlurSave={async v => { try { await commsUpdate('exhibitions', 'comms_id', ex.comms_id, { date_start: v }); } catch {} }} placeholder="Date…" style={{ marginBottom: 6 }} />
                <NoteArea value={ex.notes} onChange={v => saveExhibNote(ex, v)} placeholder="Notes…" minHeight={48} />
              </div>
            ))}
          </div>
        </div>
      )}

      {sub === 'lectures' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {lectures.map(lec => (
              <div key={lec.comms_id} style={{ ...S.card, marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                    <LinkBadge value={lec.url} onSave={async v => { try { await commsUpdate('lectures', 'comms_id', lec.comms_id, { url: v }); } catch {} }} />
                    <InlineInput
                      value={lec.title}
                      onBlurSave={async v => { try { await commsUpdate('lectures', 'comms_id', lec.comms_id, { title: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
                      placeholder="Title…"
                      style={{ flex: 1, fontSize: 15, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0' }}
                    />
                  </div>
                  {canEdit && <button onClick={() => del('lectures', lec.comms_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>}
                </div>
                <InlineInput value={lec.who} onBlurSave={async v => { try { await commsUpdate('lectures', 'comms_id', lec.comms_id, { who: v }); } catch {} }} placeholder="Who…" style={{ marginBottom: 4 }} />
                <InlineInput value={lec.where} onBlurSave={async v => { try { await commsUpdate('lectures', 'comms_id', lec.comms_id, { where: v }); } catch {} }} placeholder="Where…" style={{ marginBottom: 4 }} />
                <InlineInput value={lec.date} onBlurSave={async v => { try { await commsUpdate('lectures', 'comms_id', lec.comms_id, { date: v }); } catch {} }} placeholder="When…" style={{ marginBottom: 6 }} />
                <NoteArea value={lec.notes} onChange={async v => { try { await commsUpdate('lectures', 'comms_id', lec.comms_id, { notes: v }); } catch {} }} placeholder="Notes…" minHeight={36} saveOnBlur />
              </div>
            ))}
          </div>
        </div>
      )}

      {sub === 'awards' && (
        <div>
          <div style={S.grid2}>
            {awards.map(aw => (
              <div key={aw.comms_id} style={{ ...S.card, marginBottom: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                    <LinkBadge value={aw.url} onSave={async v => { try { await commsUpdate('awards', 'comms_id', aw.comms_id, { url: v }); } catch {} }} />
                    <InlineInput
                      value={aw.award_body}
                      onBlurSave={async v => { try { await commsUpdate('awards', 'comms_id', aw.comms_id, { award_body: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
                      placeholder="Award name…"
                      style={{ flex: 1, fontSize: 15, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0' }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {canEdit
                      ? <InlineInput value={aw.year || ''} onBlurSave={async v => { try { await commsUpdate('awards', 'comms_id', aw.comms_id, { year: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Year…" style={{ ...S.eyebrow, width: 56, textAlign: 'right' }} />
                      : <span style={{ ...S.eyebrow }}>{aw.year}</span>
                    }
                    {canEdit && <button onClick={() => del('awards', aw.comms_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>}
                  </div>
                </div>
                <div style={{ marginBottom: 6 }}>
                  <ProjectCodesPicker codes={aw.project_codes} projects={data.pub_projects || []} canEdit={canEdit} onSave={async codes => { try { await commsUpdate('awards', 'comms_id', aw.comms_id, { project_codes: codes }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} />
                </div>
                <InlineInput value={aw.category} onBlurSave={async v => { try { await commsUpdate('awards', 'comms_id', aw.comms_id, { category: v }); } catch {} }} placeholder="Category…" style={{ marginBottom: 6 }} />
                <NoteArea value={aw.notes} onChange={async v => { try { await commsUpdate('awards', 'comms_id', aw.comms_id, { notes: v }); } catch {} }} placeholder="Notes…" minHeight={36} saveOnBlur />
              </div>
            ))}
          </div>
        </div>
      )}

      {sub === 'publications' && (
        <div>
          <div style={S.grid2}>
            {publications.map(pub => {
              const pubProjectCodes = Array.isArray(pub.project_codes) ? pub.project_codes : [];
              const availableProjects = (data.pub_projects || []).filter(p => !pubProjectCodes.includes(p.project_code));
              const savePubProjectCodes = async (codes) => {
                try { await commsUpdate('publications', 'comms_id', pub.comms_id, { project_codes: codes }); refresh(); } catch (e) { showToast(e.message, 'error'); }
              };
              const pubContactIds = Array.isArray(pub.contact_ids) ? pub.contact_ids.map(Number) : [];
              const availableContacts = (data.pub_contacts || []).filter(c => !pubContactIds.includes(c.id));
              const savePubContactIds = async (ids) => {
                try { await commsUpdate('publications', 'comms_id', pub.comms_id, { contact_ids: ids }); refresh(); } catch (e) { showToast(e.message, 'error'); }
              };
              const pubAuthorId = pub.author_contact_id != null ? Number(pub.author_contact_id) : null;
              const authorPerson = (data.pub_people || []).find(pp => pp.id === pubAuthorId) || null;
              const savePubAuthor = async (e) => {
                const v = e.target.value;
                try { await commsUpdate('publications', 'comms_id', pub.comms_id, { author_contact_id: v ? Number(v) : null }); refresh(); } catch (err) { showToast(err.message, 'error'); }
              };
              return (
              <div key={pub.comms_id} style={{ ...S.card, marginBottom: 0, padding: 0, overflow: 'hidden' }}>
                {pub.image_url && (
                  <div style={{ width: '100%', height: 160, overflow: 'hidden', background: '#E6E4DD' }}>
                    <img src={pub.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    {pubContactIds.map(cid => {
                      const contact = (data.pub_contacts || []).find(c => c.id === cid);
                      return (
                        <span key={cid} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 2, padding: '1px 0', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', flexShrink: 0 }}>
                          <span style={{ color: GOLD }}>{contact ? contact.company_name : cid}</span>
                          {canEdit && <button onClick={() => savePubContactIds(pubContactIds.filter(x => x !== cid))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C8C4BB', padding: '0 0 0 2px', lineHeight: 1, fontSize: 12 }} title="Remove">×</button>}
                        </span>
                      );
                    })}
                    {canEdit && availableContacts.length > 0 && (
                      <select
                        value=""
                        onChange={async e => { if (e.target.value) await savePubContactIds([...pubContactIds, Number(e.target.value)]); }}
                        style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: pubContactIds.length === 0 ? '#C8C4BB' : '#9A988F', background: 'transparent', border: 'none', padding: '1px 0', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', width: pubContactIds.length === 0 ? 120 : 20, flexShrink: 0, letterSpacing: pubContactIds.length === 0 ? '0.08em' : 0 }}
                        title="Link outlet"
                      >
                        <option value="">{pubContactIds.length === 0 ? 'OUTLET / MAGAZINE…' : '+'}</option>
                        {availableContacts.map(c => (
                          <option key={c.id} value={c.id}>{c.company_name}</option>
                        ))}
                      </select>
                    )}
                    {canEdit && (
                      <button
                        onClick={async () => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { is_interview: !pub.is_interview }); refresh(); } catch {} }}
                        title={pub.is_interview ? 'Marked as interview — click to unmark' : 'Mark as interview'}
                        style={{ flexShrink: 0, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: pub.is_interview ? '#33473F' : 'transparent', color: pub.is_interview ? '#fff' : '#9A988F', border: `1px solid ${pub.is_interview ? '#33473F' : '#D0CEC8'}`, borderRadius: 2, padding: '2px 6px', cursor: 'pointer' }}
                      >
                        Interview
                      </button>
                    )}
                    {!canEdit && pub.is_interview && (
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', background: '#33473F', color: '#fff', borderRadius: 2, padding: '2px 6px' }}>Interview</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                      <LinkBadge value={pub.url} onSave={async v => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { url: v }); } catch {} }} placeholder="Publication page link…" title="Page link" />
                      <InlineInput
                        value={pub.title}
                        onBlurSave={async v => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { title: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
                        placeholder="Project / article title…"
                        style={{ flex: 1, fontSize: 15, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: '2px 0' }}
                      />
                    </div>
                    {canEdit && <button onClick={() => del('publications', pub.comms_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0 }}><Trash2 size={13} /></button>}
                  </div>
                  {/* Author — the writer (a contact person from the contacts directory), grouped
                      under their outlet so the magazine itself is never selectable; sits under the
                      title, above the date */}
                  {(canEdit || authorPerson) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2, minWidth: 0 }}>
                      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9A988F', flexShrink: 0 }}>By</span>
                      {canEdit ? (
                        <select
                          value={pubAuthorId || ''}
                          onChange={savePubAuthor}
                          title="Select the article's author (a writer from the contacts directory)"
                          style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, fontStyle: pubAuthorId ? 'normal' : 'italic', color: pubAuthorId ? '#1B1B19' : '#9A988F', background: 'transparent', border: 'none', padding: '1px 0', cursor: 'pointer', maxWidth: '100%', minWidth: 0, appearance: 'none', WebkitAppearance: 'none' }}
                        >
                          <option value="">Select author…</option>
                          {(data.pub_contacts || []).map(outlet => {
                            const people = (data.pub_people || []).filter(pp => pp.vendor_id === outlet.id);
                            if (people.length === 0) return null;
                            return (
                              <optgroup key={outlet.id} label={outlet.company_name}>
                                {people.map(pp => (
                                  <option key={pp.id} value={pp.id}>{pp.name}{pp.designation ? ` — ${pp.designation}` : ''}</option>
                                ))}
                              </optgroup>
                            );
                          })}
                        </select>
                      ) : (
                        <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13, color: '#1B1B19', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{authorPerson ? authorPerson.name : ''}</span>
                      )}
                    </div>
                  )}
                  <InlineInput
                    value={pub.date}
                    onBlurSave={async v => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { date: v }); refresh(); } catch {} }}
                    placeholder="YYYY-MM…"
                    style={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: '#6E6C66', border: 'none', background: 'transparent', padding: '2px 0', marginBottom: 8 }}
                  />
                  <NoteArea
                    value={pub.summary}
                    onChange={async v => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { summary: v }); } catch {} }}
                    placeholder="Summary / description…"
                    minHeight={56}
                    saveOnBlur
                  />
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                      <LinkBadge value={pub.image_url} onSave={async v => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { image_url: v }); refresh(); } catch {} }} placeholder="Cover image URL…" title="Cover image" />
                      <LinkBadge value={pub.pdf_url} onSave={async v => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { pdf_url: v }); } catch {} }} placeholder="PDF link…" title="PDF" />
                    </div>
                    <NoteArea value={pub.notes} onChange={async v => { try { await commsUpdate('publications', 'comms_id', pub.comms_id, { notes: v }); } catch {} }} placeholder="Publication text…" minHeight={160} saveOnBlur />
                    {/* Project + contact tags — single row */}
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      {pubProjectCodes.map(code => {
                        const proj = (data.pub_projects || []).find(p => p.project_code === code);
                        return (
                          <span key={code} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#E6E4DD', borderRadius: 2, padding: '2px 7px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11 }}>
                            <span style={{ color: '#33473F', fontWeight: 600 }}>{code}</span>
                            <span style={{ color: '#6E6C66' }}>{proj ? proj.name : ''}</span>
                            {canEdit && (
                              <button onClick={() => savePubProjectCodes(pubProjectCodes.filter(c => c !== code))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 0, lineHeight: 1, fontSize: 13 }} title="Remove">×</button>
                            )}
                          </span>
                        );
                      })}
                      {canEdit && availableProjects.length > 0 && (
                        <select
                          value=""
                          onChange={async e => { if (e.target.value) await savePubProjectCodes([...pubProjectCodes, e.target.value]); }}
                          style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: '#9A988F', background: 'transparent', border: '1px solid transparent', borderRadius: 2, padding: '1px 5px', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', width: 24, flexShrink: 0 }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#D0CEC8'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
                          title="Link project"
                        >
                          <option value="">+</option>
                          {availableProjects.map(p => (
                            <option key={p.project_code} value={p.project_code}>{p.project_code} — {p.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── PROGRAMME DETAIL ──────────────────────────────────────────────────────

function ProgrammeDetail({ id, data, onBack, canEdit, refresh, showToast }) {
  const programme = (data.programmes || []).find(p => p.comms_id === id);
  const [newEvent, setNewEvent] = useState({ date: '', note: '' });

  if (!programme) return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All programmes" />
      <EmptyNote text="Programme not found." />
    </div>
  );

  const upsert = async (fields) => {
    try { await commsUpdate('programmes', 'comms_id', id, fields); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const events = Array.isArray(programme.events) ? programme.events : [];

  const addEvent = async () => {
    if (!newEvent.date.trim()) return;
    const updated = [...events, { id: Date.now(), date: newEvent.date.trim(), note: newEvent.note.trim() }];
    await upsert({ events: updated });
    setNewEvent({ date: '', note: '' });
    showToast('Session added');
  };

  const deleteEvent = async (eid) => {
    await upsert({ events: events.filter(e => e.id !== eid) });
  };

  const saveEventField = async (eid, field, val) => {
    const updated = events.map(e => e.id === eid ? { ...e, [field]: val } : e);
    await upsert({ events: updated });
  };

  const cycleStatus = async () => {
    const cycle = { 'Idea': 'Plan', 'Plan': 'Running', 'Running': 'Idea' };
    await upsert({ status: cycle[programme.status] || 'Idea' });
    showToast('Status updated');
  };

  const sortedEvents = [...events].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All programmes" />
      <div style={{ ...S.eyebrow, marginBottom: 6 }}>Creative programme</div>
      <h1 style={{ ...S.h1, marginBottom: 6 }}>
        {canEdit
          ? <InlineInput value={programme.name} onBlurSave={async v => upsert({ name: v })} placeholder="Programme name…" style={{ fontSize: 32, fontFamily: "'Spectral', Georgia, serif", border: 'none', backgroundColor: 'transparent', borderRadius: 0, padding: 0, width: '100%' }} />
          : programme.name
        }
      </h1>
      {canEdit
        ? <InlineInput value={programme.why || ''} onBlurSave={async v => upsert({ why: v })} placeholder="A one-line description…" style={{ fontFamily: "'Spectral', Georgia, serif", fontStyle: 'italic', fontSize: 17, backgroundColor: 'transparent', border: 'none', borderBottom: '1px dashed #9F902A', borderRadius: 0, padding: '0 0 2px', marginBottom: 12, maxWidth: '64ch', color: '#6E6C66', display: 'block' }} />
        : <p style={{ ...S.body, marginBottom: 12, fontStyle: 'italic' }}>{programme.why}</p>
      }
      <div style={{ marginBottom: 28 }}>
        <Chip label={`${programme.status || 'Idea'}${canEdit ? ' ↻' : ''}`} cls={statusClass(programme.status)} onClick={canEdit ? cycleStatus : undefined} />
      </div>

      <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 28 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ ...S.eyebrow, marginBottom: 6 }}>How often</div>
          {canEdit
            ? <InlineInput value={programme.freq || ''} onBlurSave={async v => upsert({ freq: v })} placeholder="Bi-weekly, Quarterly…" />
            : <span style={S.body}>{programme.freq || '—'}</span>
          }
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ ...S.eyebrow, marginBottom: 6 }}>Where</div>
          {canEdit
            ? <InlineInput value={programme.venue || ''} onBlurSave={async v => upsert({ venue: v })} placeholder="Venue…" />
            : <span style={S.body}>{programme.venue || '—'}</span>
          }
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ ...S.eyebrow, marginBottom: 6 }}>Reaches</div>
          {canEdit
            ? <InlineInput value={programme.reach || ''} onBlurSave={async v => upsert({ reach: v })} placeholder="Who is this for…" />
            : <span style={S.body}>{programme.reach || '—'}</span>
          }
        </div>
      </div>

      {programme.notes || canEdit ? (
        <div style={{ marginBottom: 28 }}>
          <BlockTitle left="Notes" />
          <NoteArea value={programme.notes || ''} onChange={async v => upsert({ notes: v })} placeholder="Owner, partners, next step…" saveOnBlur />
        </div>
      ) : null}

      <div style={{ marginBottom: 28 }}>
        <BlockTitle left="Programme calendar" right="sessions & events" />
        {sortedEvents.length === 0 && <EmptyNote text="No sessions scheduled yet." />}
        {sortedEvents.map(ev => (
          <div key={ev.id} style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '7px 0', borderBottom: '1px solid rgba(27,27,25,.07)' }}>
            {canEdit
              ? <input type="date" defaultValue={ev.date} onBlur={e => { if (e.target.value !== ev.date) saveEventField(ev.id, 'date', e.target.value); }} style={{ ...S.meta, ...S.inputStyle, width: 136, flexShrink: 0, padding: '1px 4px' }} />
              : <span style={{ ...S.meta, flexShrink: 0 }}>{ev.date}</span>
            }
            {canEdit
              ? <InlineInput value={ev.note || ''} onBlurSave={v => saveEventField(ev.id, 'note', v)} placeholder="Session note…" style={{ flex: 1, ...S.body }} />
              : <span style={{ ...S.body, flex: 1 }}>{ev.note}</span>
            }
            {canEdit && (
              <button onClick={() => deleteEvent(ev.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <AddLine>
            <input
              type="date"
              value={newEvent.date}
              onChange={e => setNewEvent(x => ({ ...x, date: e.target.value }))}
              style={{ ...S.inputStyle, width: 160 }}
            />
            <input
              value={newEvent.note}
              onChange={e => setNewEvent(x => ({ ...x, note: e.target.value }))}
              placeholder="Session note…"
              style={{ ...S.inputStyle, flex: 1 }}
            />
            <BtnGhost onClick={addEvent}>+ Add</BtnGhost>
          </AddLine>
        )}
      </div>
    </div>
  );
}

// ── THEME DETAIL ──────────────────────────────────────────────────────────

function ThemeDetail({ id, data, onBack, canEdit, canConfigure, refresh, showToast, currentUser }) {
  const theme = (data.themes || []).find(t => t.comms_id === id);
  const [subTab, setSubTab] = useState('theme');
  const [commsIdeas, setCommsIdeas] = useState('');
  const [actionText, setActionText] = useState('');
  const [actionChannel, setActionChannel] = useState('');
  const [matFilter, setMatFilter] = useState('materials');
  const [parallelsQuery, setParallelsQuery] = useState('');

  useEffect(() => {
    if (theme) setCommsIdeas(theme.comms_ideas || '');
  }, [theme]);

  if (!theme) return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All themes" />
      <EmptyNote text="Theme not found." />
    </div>
  );

  const saveCommsIdeas = async () => {
    try { await commsUpdate('themes', 'comms_id', id, { comms_ideas: commsIdeas }); refresh(); showToast('Saved'); } catch (e) { showToast(e.message, 'error'); }
  };

  const addAction = async () => {
    if (!actionText.trim()) return;
    const actions = Array.isArray(theme.actions) ? theme.actions : [];
    const newActions = [...actions, { id: Date.now(), channel: actionChannel, text: actionText, status: 'Idea' }];
    try { await commsUpdate('themes', 'comms_id', id, { actions: newActions }); refresh(); setActionText(''); setActionChannel(''); showToast('Action added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const projects = (data.projects || []).filter(p => Array.isArray(p.themes) && p.themes.includes(id));
  const materials = (data.materials || []).filter(m => !m.cat || m.cat === matFilter);
  const actions = Array.isArray(theme.actions) ? theme.actions : [];
  const doneActions = actions.filter(a => a.status === 'Done');
  const activeActions = actions.filter(a => a.status !== 'Done');

  // Special themes
  if (id === 'practice') {
    return <PracticeSubPage data={data} onBack={onBack} canEdit={canEdit} canConfigure={canConfigure} refresh={refresh} showToast={showToast} />;
  }

  if (id === 'collaborators') {
    return <CollaboratorsPage data={data} onBack={onBack} canEdit={canEdit} refresh={refresh} showToast={showToast} />;
  }

  return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All themes" />
      <div style={{ ...S.eyebrow, marginBottom: 6 }}>Practice theme</div>
      <h1 style={{ ...S.h1, marginBottom: 10 }}>{theme.label}</h1>
      {canEdit
        ? <InlineInput value={theme.description || ''} onChange={async v => { try { await commsUpdate('themes', 'comms_id', id, { description: v }); refresh(); } catch {} }} placeholder="Description…" style={{ fontSize: 14, color: '#6E6C66', border: 'none', background: 'transparent', padding: '0', marginBottom: 24, width: '100%' }} />
        : <p style={{ ...S.body, marginBottom: 24 }}>{theme.description}</p>
      }

      <div style={S.filterRow}>
        <FilterBtn label="Theme" active={subTab === 'theme'} onClick={() => setSubTab('theme')} />
        <FilterBtn label="Archive" active={subTab === 'archive'} onClick={() => setSubTab('archive')} />
      </div>

      {subTab === 'theme' && (
        <div>
          {id !== 'material' && (
            <ThemeAssetsSection themeId={id} projects={data.projects || []} canEdit={canEdit} showToast={showToast} />
          )}
          {id === 'material' && (
            <div style={{ marginBottom: 28 }}>
              <BlockTitle left="Materials library" />
              <div style={S.filterRow}>
                {['materials','details','craft'].map(f => (
                  <FilterBtn key={f} label={f} active={matFilter === f} onClick={() => setMatFilter(f)} />
                ))}
              </div>
              <ThemeAssetsSection key={matFilter} themeId="material" section={matFilter} projects={data.projects || []} canEdit={canEdit} showToast={showToast} />
              {materials.length === 0 && <EmptyNote text={`No ${matFilter} entries yet.`} />}
              <div style={S.grid2}>
                {materials.map(m => {
                  const saveMat = async (field, val) => { try { await commsUpdate('materials', 'comms_id', m.comms_id, { [field]: val }); refresh(); } catch (e) { showToast(e.message, 'error'); } };
                  return (
                    <div key={m.comms_id} style={{ ...S.card, marginBottom: 0, position: 'relative' }}>
                      {canEdit && (
                        <button onClick={async () => { if (!window.confirm('Delete this entry?')) return; try { await commsDelete('materials', 'comms_id', m.comms_id); refresh(); } catch (e) { showToast(e.message, 'error'); } }} style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={12} /></button>
                      )}
                      {canEdit
                        ? <InlineInput value={m.name} onBlurSave={v => saveMat('name', v)} placeholder="Material name…" style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }} />
                        : <h3 style={{ ...S.h3, marginBottom: 6 }}>{m.name}</h3>
                      }
                      <div style={{ ...S.eyebrow, marginBottom: 2 }}>How used</div>
                      {canEdit
                        ? <NoteArea value={m.used || ''} onChange={v => saveMat('used', v)} placeholder="Where and how this material was used…" minHeight={36} saveOnBlur />
                        : m.used ? <p style={{ ...S.body, marginBottom: 6 }}>{m.used}</p> : null
                      }
                      <div style={{ ...S.eyebrow, marginBottom: 2, marginTop: 6 }}>Who we worked with</div>
                      {canEdit
                        ? <NoteArea value={m.who || ''} onChange={v => saveMat('who', v)} placeholder="Maker, supplier, artisan…" minHeight={36} saveOnBlur />
                        : m.who ? <p style={{ ...S.body, marginBottom: 6 }}>{m.who}</p> : null
                      }
                      <div style={{ ...S.eyebrow, marginBottom: 2, marginTop: 6 }}>Issues</div>
                      {canEdit
                        ? <NoteArea value={m.issues || ''} onChange={v => saveMat('issues', v)} placeholder="Challenges, sourcing problems…" minHeight={36} saveOnBlur />
                        : m.issues ? <p style={{ ...S.body, marginBottom: 6 }}>{m.issues}</p> : null
                      }
                      <div style={{ ...S.eyebrow, marginBottom: 2, marginTop: 6 }}>Future opportunities</div>
                      {canEdit
                        ? <NoteArea value={m.future || ''} onChange={v => saveMat('future', v)} placeholder="Reuse potential, next projects…" minHeight={36} saveOnBlur />
                        : m.future ? <p style={S.body}>{m.future}</p> : null
                      }
                    </div>
                  );
                })}
              </div>
              {canEdit && (
                <AddLine>
                  <InlineInput value="" onChange={() => {}} placeholder={`New ${matFilter} entry…`} onBlurSave={async v => { if (!v.trim()) return; try { await commsInsert('materials', { comms_id: 'mat-' + Date.now(), name: v.trim(), cat: matFilter }); refresh(); showToast('Entry added'); } catch (e) { showToast(e.message, 'error'); } }} />
                </AddLine>
              )}
            </div>
          )}

          <div style={{ marginBottom: 24 }}>
            <BlockTitle left="Related projects" right={`${projects.length} linked`} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: canEdit ? 8 : 0 }}>
              {projects.length === 0 && !canEdit && <EmptyNote text="No projects linked to this theme yet." />}
              {projects.map(p => (
                <span key={p.comms_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Chip label={`${p.project_code} — ${p.name}`} cls="line" />
                  {canEdit && (
                    <button onClick={async () => { try { await commsUpdate('projects', 'comms_id', p.comms_id, { themes: (p.themes || []).filter(t => t !== id) }); refresh(); showToast('Unlinked'); } catch (e) { showToast(e.message, 'error'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: '0 2px', fontSize: 11, lineHeight: 1 }}>×</button>
                  )}
                </span>
              ))}
            </div>
            {canEdit && (() => {
              const unlinked = (data.projects || []).filter(p => !Array.isArray(p.themes) || !p.themes.includes(id));
              if (!unlinked.length) return null;
              return (
                <select
                  defaultValue=""
                  key={projects.length}
                  onChange={async e => {
                    const proj = unlinked.find(p => p.comms_id === e.target.value);
                    if (!proj) return;
                    try { await commsUpdate('projects', 'comms_id', proj.comms_id, { themes: [...(proj.themes || []), id] }); refresh(); showToast('Project linked'); } catch (err) { showToast(err.message, 'error'); }
                  }}
                  style={{ ...S.inputStyle, maxWidth: 260, marginTop: 4 }}
                >
                  <option value="">+ Link a project…</option>
                  {unlinked.map(p => <option key={p.comms_id} value={p.comms_id}>{p.project_code} — {p.name}</option>)}
                </select>
              );
            })()}
          </div>

          <div style={{ marginBottom: 24 }}>
            <BlockTitle left="Parallels" right="find common ground" />
            <input
              value={parallelsQuery}
              onChange={e => setParallelsQuery(e.target.value)}
              placeholder="Type a keyword to find projects that share this ground…"
              style={{ ...S.inputStyle, marginBottom: 10 }}
            />
            {parallelsQuery.trim() && (() => {
              const ql = parallelsQuery.trim().toLowerCase();
              const hits = (data.projects || []).filter(p =>
                [p.project_code, p.name, p.byline, p.description, p.headline, p.typology, p.city].some(f => f && f.toLowerCase().includes(ql))
              );
              return hits.length === 0
                ? <EmptyNote text="No projects match this keyword." />
                : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {hits.map(p => <Chip key={p.comms_id} label={`${p.project_code} — ${p.name}`} cls={projects.some(lp => lp.comms_id === p.comms_id) ? 'pipe' : 'line'} />)}
                  </div>;
            })()}
          </div>

          <div style={{ marginBottom: 24 }}>
            <BlockTitle left="Comms ideas" />
            <NoteArea value={commsIdeas} onChange={async v => { setCommsIdeas(v); try { await commsUpdate('themes', 'comms_id', id, { comms_ideas: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Ideas for sharing this theme…" minHeight={80} saveOnBlur />
          </div>

          <div style={{ marginBottom: 24 }}>
            <BlockTitle left="How do you want to share this?" />
            {activeActions.length === 0 && <EmptyNote text="No actions yet." />}
            {activeActions.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
                {a.channel && <Chip label={a.channel} cls="line" />}
                {canEdit
                  ? <InlineInput value={a.text} onBlurSave={async v => { const newActions = (theme.actions || []).map(x => x.id === a.id ? { ...x, text: v } : x); try { await commsUpdate('themes', 'comms_id', id, { actions: newActions }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Describe the action…" style={{ flex: 1 }} />
                  : <span style={{ ...S.body, flex: 1 }}>{a.text}</span>
                }
                <Chip label={a.status} cls={statusClass(a.status)} onClick={async () => {
                  const cycle = { 'Idea': 'In progress', 'In progress': 'Done', 'Done': 'Idea', 'Plan': 'In progress' };
                  const next = cycle[a.status] || 'Idea';
                  const newActions = (theme.actions || []).map(x => x.id === a.id ? { ...x, status: next } : x);
                  try { await commsUpdate('themes', 'comms_id', id, { actions: newActions }); refresh(); } catch (e) { showToast(e.message, 'error'); }
                }} />
                {canEdit && (
                  <button onClick={async () => { const newActions = (theme.actions || []).filter(x => x.id !== a.id); try { await commsUpdate('themes', 'comms_id', id, { actions: newActions }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0 }}><Trash2 size={12} /></button>
                )}
              </div>
            ))}
            {canEdit && (
              <AddLine>
                <select value={actionChannel} onChange={e => setActionChannel(e.target.value)} style={{ ...S.inputStyle, width: 140 }}>
                  <option value="">Channel…</option>
                  {['Website','Socials','LinkedIn','Lecture','Talk','Event'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <InlineInput value={actionText} onChange={setActionText} placeholder="Describe the action…" />
                <BtnGhost onClick={addAction}>+ Add</BtnGhost>
              </AddLine>
            )}
          </div>
        </div>
      )}

      {subTab === 'archive' && (
        <div>
          <BlockTitle left="Done" />
          {doneActions.length === 0 ? <EmptyNote text="Nothing archived yet." /> : doneActions.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
              {a.channel && <Chip label={a.channel} cls="line" />}
              <span style={{ ...S.body, flex: 1, marginLeft: 4 }}>{a.text}</span>
              {canEdit && (
                <button onClick={async () => { const newActions = (theme.actions || []).filter(x => x.id !== a.id); try { await commsUpdate('themes', 'comms_id', id, { actions: newActions }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0 }}><Trash2 size={12} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MONO_PROMPTS = [
  'Key projects across the 20 years',
  'Milestones & turning points',
  'Photography to gather (old and new)',
  'Essays / voices to commission',
  'Interview subjects — clients, makers, team',
  'Film footage & recorded interviews',
  'Publication, film — or both?',
];

function PracticeSubPage({ data, onBack, canEdit, canConfigure, refresh, showToast }) {
  const [sub, setSub] = useState('studio');
  const ps = data.practice || {};
  const ms = data.monograph || {};
  const [schema, setSchema] = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [newSubfolderDrafts, setNewSubfolderDrafts] = useState({});
  const [newSubfolderTemplateDrafts, setNewSubfolderTemplateDrafts] = useState({});
  const [newSectionDraft, setNewSectionDraft] = useState('');
  const [approvedSubfolders, setApprovedSubfolders] = useState({});
  // `kind` names the running operation ('projects' | 'practice') so the two
  // buttons sharing this state don't both read as busy — only the one that was
  // actually clicked shows a progress label.
  const [folderOp, setFolderOp] = useState({ loading: false, kind: null, results: null, error: null });
  // The team-portrait run keeps its own state rather than sharing folderOp:
  // it walks ~200 members in chunks and reports per member, which is a
  // different shape from the per-project results the block below renders.
  const [portraitOp, setPortraitOp] = useState({ loading: false, mode: null, results: null, error: null, progress: null });
  const [hashtags, setHashtags] = useState(null);
  const [newHashtag, setNewHashtag] = useState('');
  const [practiceSchema, setPracticeSchema] = useState(null);
  const [newPracticeSection, setNewPracticeSection] = useState('');
  const [newPracticeSubDrafts, setNewPracticeSubDrafts] = useState({});

  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'commsAssetHashtags').maybeSingle().then(({ data: row }) => {
      try {
        const v = typeof row?.value === 'string' ? JSON.parse(row.value) : row?.value;
        setHashtags(Array.isArray(v) ? v : []);
      } catch { setHashtags([]); }
    });
    supabase.from('app_settings').select('value').eq('key', 'pressKitPracticeSchema').maybeSingle().then(({ data: row }) => {
      try {
        const v = typeof row?.value === 'string' ? JSON.parse(row.value) : row?.value;
        setPracticeSchema(Array.isArray(v) ? v : []);
      } catch { setPracticeSchema([]); }
    });
  }, []);

  const saveHashtags = async (list) => {
    setHashtags(list);
    try { await supabase.from('app_settings').upsert({ key: 'commsAssetHashtags', value: list }); showToast('Hashtags saved'); } catch (e) { showToast(e.message, 'error'); }
  };

  const addHashtag = () => {
    const t = slugTag(newHashtag);
    if (!t) return;
    if ((hashtags || []).includes(t)) { showToast('Already in the list', 'error'); return; }
    setNewHashtag('');
    saveHashtags([...(hashtags || []), t]);
  };

  const savePracticeSchema = async (s) => {
    setPracticeSchema(s);
    try { await supabase.from('app_settings').upsert({ key: 'pressKitPracticeSchema', value: s }); showToast('Practice tree saved'); } catch (e) { showToast(e.message, 'error'); }
  };

  const addPracticeSection = () => {
    const name = newPracticeSection.trim();
    if (!name) return;
    if ((practiceSchema || []).some(s => s.folder.toLowerCase() === name.toLowerCase())) { showToast('That section already exists', 'error'); return; }
    setNewPracticeSection('');
    savePracticeSchema([...(practiceSchema || []), { folder: name, subfolders: [] }]);
  };

  const addPracticeSubfolder = (si) => {
    const name = (newPracticeSubDrafts[si] || '').trim();
    if (!name) return;
    setNewPracticeSubDrafts(d => ({ ...d, [si]: '' }));
    savePracticeSchema(practiceSchema.map((s, i) => i === si ? { ...s, subfolders: [...(s.subfolders || []), name] } : s));
  };

  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'pressKitFolderSchema').maybeSingle().then(({ data: row }) => {
      if (row?.value) {
        try {
          const v = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
          setSchema(Array.isArray(v) && v.length ? v : PRESS_KIT_DEFAULT_SCHEMA);
        } catch { setSchema(PRESS_KIT_DEFAULT_SCHEMA); }
      } else {
        setSchema(PRESS_KIT_DEFAULT_SCHEMA);
      }
      setSchemaLoading(false);
    });
    supabase.from('app_settings').select('value').eq('key', 'pressKitApprovedSubfolders').maybeSingle().then(({ data: row }) => {
      if (row?.value) {
        try {
          const v = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
          if (v && typeof v === 'object') setApprovedSubfolders(v);
        } catch {}
      }
    });
  }, []);

  const saveSchema = async (newSchema) => {
    setSchema(newSchema);
    try {
      await supabase.from('app_settings').upsert({ key: 'pressKitFolderSchema', value: newSchema });
      showToast('Schema saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const addSubfolder = (sectionKey) => {
    const name = (newSubfolderDrafts[sectionKey] || '').trim();
    if (!name) return;
    const newSchema = schema.map(s => s.key === sectionKey ? { ...s, subfolders: [...s.subfolders, name] } : s);
    setNewSubfolderDrafts(d => ({ ...d, [sectionKey]: '' }));
    saveSchema(newSchema);
  };

  const removeSubfolder = (sectionKey, idx) => {
    const newSchema = schema.map(s => s.key === sectionKey ? { ...s, subfolders: s.subfolders.filter((_, i) => i !== idx) } : s);
    saveSchema(newSchema);
  };

  const addSubfolderTemplate = (sectionKey) => {
    const name = (newSubfolderTemplateDrafts[sectionKey] || '').trim();
    if (!name) return;
    const newSchema = schema.map(s => s.key === sectionKey
      ? { ...s, subfolderTemplate: [...(s.subfolderTemplate || []), name] }
      : s);
    setNewSubfolderTemplateDrafts(d => ({ ...d, [sectionKey]: '' }));
    saveSchema(newSchema);
  };

  const removeSubfolderTemplate = (sectionKey, idx) => {
    const newSchema = schema.map(s => s.key === sectionKey
      ? { ...s, subfolderTemplate: (s.subfolderTemplate || []).filter((_, i) => i !== idx) }
      : s);
    saveSchema(newSchema);
  };

  // "Allow any subfolder": the section is free-form, so nothing found directly
  // under it is reported as unexpected by the audit or the Dropbox webhook.
  // Read by _shared/pressAudit.ts on the server and by KitCard here.
  const toggleAllowAnySubfolders = (sectionKey) => {
    const newSchema = schema.map(s => s.key === sectionKey
      ? { ...s, allowAnySubfolders: !s.allowAnySubfolders }
      : s);
    saveSchema(newSchema);
  };

  const DEFAULT_SECTION_KEYS = new Set(PRESS_KIT_DEFAULT_SCHEMA.map(s => s.key));

  const addSection = () => {
    const name = newSectionDraft.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || `section_${Date.now()}`;
    if ((schema || []).some(s => s.key === key || s.folder.toLowerCase() === name.toLowerCase())) {
      showToast('A section with that name already exists', 'error');
      return;
    }
    const newSection = { key, label: name, folder: name, subfolders: [], subfolderTemplate: [] };
    setNewSectionDraft('');
    saveSchema([...(schema || []), newSection]);
  };

  const removeSection = (sectionKey) => {
    saveSchema((schema || []).filter(s => s.key !== sectionKey));
  };

  const removeApproved = async (sectionFolder, name) => {
    const newApproved = { ...approvedSubfolders };
    if (newApproved[sectionFolder]) {
      newApproved[sectionFolder] = newApproved[sectionFolder].filter(n => n !== name);
      if (newApproved[sectionFolder].length === 0) delete newApproved[sectionFolder];
    }
    setApprovedSubfolders(newApproved);
    try {
      await supabase.from('app_settings').upsert({ key: 'pressKitApprovedSubfolders', value: newApproved });
      showToast('Removed');
    } catch (e) { showToast(e.message, 'error'); }
  };

  /**
   * Walks every comms-enabled project through create-press-folders.
   *
   * `mode` is what separates the two buttons:
   *   provision — create whatever is missing. Projects whose recorded schema
   *               fingerprint still matches are skipped server-side without
   *               touching Dropbox, so a routine run over an unchanged estate
   *               costs nothing.
   *   verify    — read-only. Never skips, never writes; reports the gaps.
   *
   * `force` re-reads Dropbox for every project even when the recorded state
   * says it is current, which is how you catch a folder deleted by hand.
   */
  const runFolderOp = async ({ mode = 'provision', force = false } = {}) => {
    const allProjects = (data.projects || [])
      .filter(p => p.project_code && p.name)
      .map(p => ({ project_code: p.project_code, name: p.name }));
    if (allProjects.length === 0) { showToast('No comms-enabled projects found'); return; }
    // Batched so the progress counter moves and no single invocation can run
    // away. A batch of skipped projects returns immediately.
    const BATCH = 15;
    const kind = mode === 'verify' ? 'verify' : force ? 'force' : 'projects';
    setFolderOp({ loading: true, kind, results: null, progress: { done: 0, total: allProjects.length } });
    const results = [];
    let syncedCount = 0;
    let syncFailures = 0;
    try {
      for (let i = 0; i < allProjects.length; i += BATCH) {
        const chunk = allProjects.slice(i, i + BATCH);
        const json = await callEdgeFunction('create-press-folders', { projects: chunk, mode, force });
        const chunkResults = json.results || [];
        results.push(...chunkResults);
        // Newly created folders have no shared link yet, so their contents won't
        // show in the project cards — sync links for just those projects. A
        // project whose folder was just renamed is re-synced too: its shared
        // links are id-based and survive the move, but the sync re-reads them
        // at the new path and re-stamps the card's record of the folder name.
        const needSync = chunkResults.filter(r => r.created.length > 0 || r.projectRenamed)
          .map(r => ({ project_code: r.project_code, name: r.name }));
        if (needSync.length > 0) {
          try {
            await callEdgeFunction('sync-press-folder-links', { projects: needSync });
            syncedCount += needSync.length;
          } catch { syncFailures += needSync.length; }
        }
        setFolderOp({ loading: true, kind, results: null, progress: { done: Math.min(i + BATCH, allProjects.length), total: allProjects.length } });
      }
      setFolderOp({ loading: false, kind, results });
      const created = results.reduce((acc, r) => acc + r.created.length, 0);
      const missing = results.reduce((acc, r) => acc + (r.missing?.length || 0), 0);
      const skipped = results.filter(r => r.skipped).length;
      const mismatched = results.filter(r => r.folderNameMismatch).length;
      const foldersRenamed = results.filter(r => r.projectRenamed).length;
      // A mismatch after a provisioning run means the rename was attempted and
      // refused (the row says why), so it is worded as a folder left behind,
      // not as an observation.
      const nameNote = mismatched
        ? (mode === 'verify'
          ? `, ${mismatched} under an unexpected folder name`
          : `, ${mismatched} folder(s) could not be renamed`)
        : '';
      const renameNote = foldersRenamed ? `, ${foldersRenamed} project folder(s) renamed in Dropbox` : '';
      if (mode === 'verify') {
        showToast(missing === 0 && !mismatched
          ? `Checked ${results.length} project(s) — everything matches the schema`
          : `${missing} folder(s) missing across ${results.filter(r => r.missing?.length).length} project(s)${nameNote}`);
      } else {
        const linkNote = `${syncedCount ? `, links synced for ${syncedCount} project(s)` : ''}${syncFailures ? `, link sync failed for ${syncFailures}` : ''}`;
        showToast(created === 0 && skipped === results.length && skipped > 0
          // Nothing was read, so "nothing to do" would be a guess, not a finding.
          ? `Skipped all ${skipped} project(s) as unchanged — Dropbox was not read. Use “Full re-read” to check it.`
          : `Done — ${created} new folder(s) created${renameNote}${skipped ? `, ${skipped} skipped as unchanged` : ''}${nameNote}${linkNote}`);
      }
      // A rename re-stamps the card's record of the folder name too, so the
      // cards re-read even when no links were synced.
      if (syncedCount || foldersRenamed) refresh();
      // Every cached folder listing under a renamed project names the old path.
      if (foldersRenamed) clearListingCache();
    } catch (e) {
      setFolderOp({ loading: false, kind, results: results.length ? results : null, error: e.message });
    }
  };

  const createAllFolders = () => runFolderOp({ mode: 'provision' });
  const verifyAllFolders = () => runFolderOp({ mode: 'verify' });
  const forceSyncAllFolders = () => runFolderOp({ mode: 'provision', force: true });

  const createPracticeFolders = async () => {
    setFolderOp({ loading: true, kind: 'practice', results: null });
    try {
      const json = await callEdgeFunction('create-press-folders', { practice: true });
      const r = json.results?.[0];
      setFolderOp({ loading: false, kind: 'practice', results: json.results || [] });
      showToast(r
        ? `00_Practice — ${r.created.length} created, ${r.existing.length} existing${r.moved ? ` · ${r.moved}` : ''}`
        : 'Done');
    } catch (e) {
      setFolderOp({ loading: false, kind: 'practice', results: null, error: e.message });
    }
  };

  /**
   * Walks the whole roster through sync-team-portraits.
   *
   * Chunked by the function's own page size rather than a count chosen here:
   * each response says how many members there are in total and whether it was
   * the last page, so the loop follows the server's pagination instead of
   * duplicating the rule. Around 200 members is roughly eight calls, which is
   * what keeps any one invocation well inside its wall clock — a full run
   * uploads photos, and photos are real bytes moving through the function.
   *
   *   sync    creates missing folders, moves the folder of anyone whose
   *           active/non-active status changed, uploads changed portraits.
   *   verify  read-only: reports what a sync would do and writes nothing.
   *
   * `force` re-uploads every photo even when its Storage etag says the bytes
   * have not changed — the escape hatch for a file deleted in Dropbox by hand.
   */
  const runTeamPortraits = async ({ mode = 'sync', force = false } = {}) => {
    setPortraitOp({ loading: true, mode, results: null, error: null, progress: { done: 0, total: 0 } });
    const results = [];
    try {
      let offset = 0;
      for (;;) {
        // One retry per chunk. The sync is idempotent and records its progress
        // per member, so a retry re-reads what is already done and redoes only
        // what is not — which makes retrying strictly better than failing on a
        // transient edge-function error.
        let json;
        try {
          json = await callEdgeFunction('sync-team-portraits', { mode, force, offset });
        } catch {
          await new Promise(r => setTimeout(r, 2000));
          json = await callEdgeFunction('sync-team-portraits', { mode, force, offset });
        }
        results.push(...(json.results || []));
        const total = json.total || results.length;
        setPortraitOp({ loading: true, mode, results: null, error: null, progress: { done: results.length, total } });
        if (json.done || !json.results?.length) break;
        offset += json.limit || json.results.length;
        // A server that stops advancing would spin here forever; the roster is
        // finite, so reaching it means something is wrong with the paging.
        if (offset > total) break;
      }
      setPortraitOp({ loading: false, mode, results, error: null, progress: null });

      const created = results.filter(r => r.action === 'create').length;
      const moved = results.filter(r => r.action === 'move').length;
      const renamed = results.filter(r => r.action === 'rename').length;
      const uploaded = results.reduce((n, r) => n + (r.uploaded?.length || 0), 0);
      const failed = results.filter(r => r.errors?.length).length;
      if (mode === 'verify') {
        showToast(created + moved + renamed === 0
          ? `Checked ${results.length} team member(s) — every folder is where it should be`
          : `${created} folder(s) missing, ${moved} in the wrong half, ${renamed} named differently`);
      } else {
        showToast(`Portraits — ${created} folder(s) created, ${moved} moved, ${uploaded} photo(s) uploaded${failed ? `, ${failed} with errors` : ''}`);
      }
    } catch (e) {
      // Everything already done is recorded server-side, so a failure part-way
      // is a pause rather than a loss — say so, because the natural reading of
      // a red box is that the whole run has to start again.
      const note = results.length
        ? ` — ${results.length} team member(s) were completed and saved; run this again to carry on from there.`
        : '';
      setPortraitOp({ loading: false, mode, results: results.length ? results : null, error: e.message + note, progress: null });
    }
  };

  // comms.founders keeps the two founders' texts. Team members are no longer
  // free-typed rows here — they come from the app's employee list, keyed by
  // user id in comms.team_bios (see TeamBiosSection).
  const founders = (data.founders || []).filter(f => !f.type || f.type === 'founder');

  const [newAssetName, setNewAssetName] = useState('');
  const [newAssetLink, setNewAssetLink] = useState('');
  const [newMonoDoc, setNewMonoDoc] = useState('');

  const saveSetting = async (table, key, value) => {
    try { await commsUpsertSetting(table, key, JSON.stringify(value)); refresh(); showToast('Saved'); } catch (e) { showToast(e.message, 'error'); }
  };

  const assets   = Array.isArray(ps.comms_assets) ? ps.comms_assets : [];
  const monoDocs = Array.isArray(ms.mono_docs)    ? ms.mono_docs    : [];

  const addAsset = async () => {
    if (!newAssetName.trim()) return;
    const updated = [...assets, { id: 'as-' + Date.now(), name: newAssetName.trim(), kind: 'Asset', link: newAssetLink.trim() }];
    await saveSetting('practice_settings', 'comms_assets', updated);
    setNewAssetName(''); setNewAssetLink('');
  };

  const removeAsset = async (i) => {
    await saveSetting('practice_settings', 'comms_assets', assets.filter((_, j) => j !== i));
  };

  const addMonoDoc = async () => {
    if (!newMonoDoc.trim()) return;
    await saveSetting('monograph_settings', 'mono_docs', [...monoDocs, { name: newMonoDoc.trim(), link: newMonoDoc.trim() }]);
    setNewMonoDoc('');
  };

  const removeMonoDoc = async (i) => {
    await saveSetting('monograph_settings', 'mono_docs', monoDocs.filter((_, j) => j !== i));
  };

  const bioCard = (person) => (
    <div key={person.comms_id} style={{ ...S.card, marginBottom: 0 }}>
      <InlineInput value={person.name} onBlurSave={async v => { try { await commsUpdate('founders', 'comms_id', person.comms_id, { name: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Name…" style={{ ...S.h3, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: 0 }} />
      <InlineInput value={person.role} onBlurSave={async v => { try { await commsUpdate('founders', 'comms_id', person.comms_id, { role: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Role…" style={{ ...S.meta, border: 'none', background: 'transparent', padding: 0, marginBottom: 8 }} />
      <NoteArea value={person.bio} onChange={async v => { try { await commsUpdate('founders', 'comms_id', person.comms_id, { bio: v }); } catch {} }} placeholder="Bio…" minHeight={64} />
    </div>
  );

  const deckRow = (label, sub2, link, onRemove) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid rgba(27,27,25,.06)' }}>
      <div>
        <div style={{ fontSize: 14, color: '#1B1B19' }}>{label}</div>
        {sub2 && <div style={{ ...S.meta, marginTop: 2 }}>{sub2}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {link && <a href={link} target="_blank" rel="noreferrer" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.04em', color: '#21302A', textDecoration: 'none', border: '1px solid #33473F', borderRadius: 2, padding: '4px 10px' }}>Open</a>}
        {canEdit && <button onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>}
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All themes" />
      <h1 style={{ ...S.h1, marginBottom: 24 }}>Practice</h1>
      <div style={S.filterRow}>
        <FilterBtn label="Studio & bios" active={sub === 'studio'} onClick={() => setSub('studio')} />
        <FilterBtn label="Comms assets" active={sub === 'assets'} onClick={() => setSub('assets')} />
        <FilterBtn label="Monograph" active={sub === 'monograph'} onClick={() => setSub('monograph')} />
      </div>

      {sub === 'studio' && (
        <div>
          <ThemeAssetsSection themeId="practice" section="studio" projects={data.projects || []} canEdit={canEdit} showToast={showToast} />
          <div style={S.card}>
            <BlockTitle left="Mission statement" />
            <NoteArea value={ps.mission || ''} onChange={v => saveSetting('practice_settings', 'mission', v)} placeholder="Our mission is…" minHeight={60} />
          </div>
          <div style={S.card}>
            <BlockTitle left="Boilerplate" right="the standard 'about' paragraph" />
            <NoteArea value={ps.studio_bio || ''} onChange={v => saveSetting('practice_settings', 'studio_bio', v)} placeholder="Standard practice description…" minHeight={80} />
          </div>
          <div style={{ marginBottom: 14 }}><BlockTitle left="Founders" /></div>
          <div style={{ ...S.grid2, marginBottom: 24 }}>
            {founders.map(f => bioCard(f))}
          </div>
          <TeamBiosSection
            users={data.users || []}
            profiles={data.user_profiles || []}
            bios={data.team_bios || []}
            canEdit={canEdit}
            showToast={showToast}
          />
        </div>
      )}

      {sub === 'assets' && (
        <div>
          <div style={S.card}>
            <BlockTitle left="Comms assets" right="logo, templates, guidelines — downloadable" />
            {assets.length === 0 && <EmptyNote text="No assets yet — add links to press folders, brand files, and media kits." />}
            {assets.map((a, i) => deckRow(a.name, a.kind, a.link, () => removeAsset(i)))}
            {canEdit && (
              <AddLine>
                <InlineInput value={newAssetName} onChange={setNewAssetName} placeholder="Asset name…" style={{ flex: 1 }} />
                <InlineInput value={newAssetLink} onChange={setNewAssetLink} placeholder="Link / path…" style={{ flex: 1 }} />
                <BtnGhost onClick={addAsset}>+ Add</BtnGhost>
              </AddLine>
            )}
          </div>
          <div style={S.card}>
            <BlockTitle left="Miro board" />
            <div style={{ ...S.body, marginBottom: 10 }}>Social content board URL — paste the full link from Miro.</div>
            <InlineInput
              value={ps.miro_board_url || ''}
              onBlurSave={v => saveSetting('practice_settings', 'miro_board_url', v)}
              placeholder="https://miro.com/app/board/…"
            />
          </div>

          {/* Global press-kit configuration below writes public.app_settings,
              which RLS restricts to admins — so it is gated on canConfigure, not
              canEdit. Content editors (all authenticated users) don't see it. */}
          {canConfigure && (<>
          {/* Allowed asset hashtags */}
          <div style={S.card}>
            <BlockTitle left="Asset hashtags" right="the allowed list — keeps tags from drifting" />
            <div style={{ ...S.body, marginBottom: 12, fontSize: 12 }}>
              The hashtag dialog on press-kit and theme assets only offers these tags. Theme tags (#material, #craft, #climate, …) are added automatically when assets are assigned to a theme.
            </div>
            {hashtags === null ? <div style={S.meta}>Loading…</div> : (() => {
              const query = slugTag(newHashtag);
              const shown = [...hashtags].sort((a, b) => a.localeCompare(b)).filter(t => !query || t.includes(query));
              return (
                <>
                  {hashtags.length === 0 && <EmptyNote text="No hashtags yet — add the vocabulary you want to tag assets with." />}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: canEdit ? 6 : 0 }}>
                    {shown.map(t => (
                      <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        <Chip label={`#${t}`} cls="line" />
                        {canEdit && (
                          <button onClick={() => saveHashtags(hashtags.filter(x => x !== t))} title="Remove hashtag"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: '0 2px', fontSize: 11, lineHeight: 1 }}>×</button>
                        )}
                      </span>
                    ))}
                    {hashtags.length > 0 && shown.length === 0 && <span style={{ ...S.meta, fontSize: 11 }}>No match — add it below</span>}
                  </div>
                  {canEdit && (
                    <AddLine>
                      <InlineInput value={newHashtag} onChange={setNewHashtag} placeholder="Type to find or add…" style={{ maxWidth: 220 }} />
                      <BtnGhost onClick={addHashtag}>+ Add</BtnGhost>
                    </AddLine>
                  )}
                </>
              );
            })()}
          </div>

          {/* Practice (00_Practice) folder tree editor */}
          <div style={S.card}>
            <BlockTitle
              left="Practice folder tree"
              right={canEdit
                ? (
                  <button
                    onClick={createPracticeFolders}
                    disabled={folderOp.loading}
                    title={folderOp.loading && folderOp.kind === 'projects'
                      ? 'Waiting — a project folder sync is running'
                      : 'Create the studio-wide 00_Practice tree in Dropbox (missing folders only)'}
                    style={{ background: '#21302A', color: '#fff', border: 'none', borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: folderOp.loading ? 'default' : 'pointer', padding: '4px 10px', opacity: folderOp.loading ? 0.6 : 1 }}
                  >
                    {folderOp.loading && folderOp.kind === 'practice' ? 'Creating…' : 'Create practice folders'}
                  </button>
                )
                : 'studio-wide assets — 00_Practice'}
            />
            <div style={{ ...S.body, marginBottom: 14, fontSize: 12 }}>
              Folder path: <code style={{ background: 'rgba(27,27,25,.06)', padding: '2px 5px', borderRadius: 2, fontSize: 11 }}>/Architecture Brio/BRIO-press/00_Practice/<i>section</i>/</code> — edits here apply on the next “Create practice folders” run. Existing Dropbox folders are never renamed or deleted.
            </div>
            {practiceSchema === null ? <div style={S.meta}>Loading schema…</div> : (
              <>
                {practiceSchema.map((sec, si) => (
                  <div key={si} style={{ marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid rgba(27,27,25,.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <div style={S.eyebrow}>{sec.folder}</div>
                      {canEdit && (
                        <button onClick={() => { if (window.confirm(`Remove "${sec.folder}" from the tree? The Dropbox folder itself is not touched.`)) savePracticeSchema(practiceSchema.filter((_, i) => i !== si)); }} title="Remove section"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: '0 2px' }}><Trash2 size={12} /></button>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                      {(sec.subfolders || []).map((sub, bi) => (
                        <span key={bi} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EDEBE5', borderRadius: 3, padding: '2px 8px', fontSize: 12, color: '#33473F' }}>
                          {sub}
                          {canEdit && (
                            <button onClick={() => savePracticeSchema(practiceSchema.map((s, i) => i === si ? { ...s, subfolders: s.subfolders.filter((_, j) => j !== bi) } : s))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 0, lineHeight: 1, fontSize: 13 }}>×</button>
                          )}
                        </span>
                      ))}
                      {(sec.subfolders || []).length === 0 && <span style={{ ...S.meta, fontSize: 11 }}>No subfolders</span>}
                      {canEdit && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input
                            value={newPracticeSubDrafts[si] || ''}
                            onChange={e => setNewPracticeSubDrafts(d => ({ ...d, [si]: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') addPracticeSubfolder(si); }}
                            placeholder="Add subfolder…"
                            style={{ fontSize: 12, border: '1px dashed rgba(27,27,25,.25)', background: 'transparent', borderRadius: 3, padding: '2px 6px', outline: 'none', width: 120 }}
                          />
                          <button onClick={() => addPracticeSubfolder(si)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#33473F', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>+</button>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {canEdit && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, paddingTop: 14, borderTop: '1px solid rgba(27,27,25,.06)' }}>
                    <input
                      value={newPracticeSection}
                      onChange={e => setNewPracticeSection(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addPracticeSection(); }}
                      placeholder="New section name (e.g. Podcasts, Workshops)…"
                      style={{ flex: 1, fontSize: 12, border: '1px dashed rgba(27,27,25,.3)', background: 'transparent', borderRadius: 3, padding: '4px 8px', outline: 'none', fontFamily: 'inherit' }}
                    />
                    <button onClick={addPracticeSection} style={{ background: '#21302A', color: '#fff', border: 'none', borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', padding: '5px 12px', whiteSpace: 'nowrap' }}>+ Add</button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Team portraits — one folder per member, filed by active status */}
          <div style={S.card}>
            <BlockTitle
              left="Team portraits"
              right={canEdit ? (
                <span style={{ display: 'inline-flex', gap: 6 }}>
                  <button
                    onClick={() => runTeamPortraits({ mode: 'verify' })}
                    disabled={portraitOp.loading}
                    title="Read Dropbox and report what a sync would change. Creates and uploads nothing."
                    style={{ background: 'transparent', color: '#21302A', border: '1px solid #21302A', borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: portraitOp.loading ? 'default' : 'pointer', padding: '4px 10px', opacity: portraitOp.loading ? 0.6 : 1 }}
                  >
                    {portraitOp.loading && portraitOp.mode === 'verify'
                      ? `Checking… ${portraitOp.progress?.done || 0}/${portraitOp.progress?.total || '?'}`
                      : 'Check only'}
                  </button>
                  <button
                    onClick={() => runTeamPortraits({ mode: 'sync' })}
                    disabled={portraitOp.loading}
                    title="Create missing member folders, move anyone whose status changed, and upload new portraits"
                    style={{ background: '#21302A', color: '#fff', border: 'none', borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: portraitOp.loading ? 'default' : 'pointer', padding: '4px 10px', opacity: portraitOp.loading ? 0.6 : 1 }}
                  >
                    {portraitOp.loading && portraitOp.mode === 'sync'
                      ? `Syncing… ${portraitOp.progress?.done || 0}/${portraitOp.progress?.total || '?'}`
                      : 'Sync team portraits'}
                  </button>
                </span>
              ) : 'one folder per team member'}
            />
            <div style={{ ...S.body, marginBottom: 12, fontSize: 12 }}>
              Every team member on the database gets a folder under{' '}
              <code style={{ background: 'rgba(27,27,25,.06)', padding: '2px 5px', borderRadius: 2, fontSize: 11 }}>Portraits &amp; Team/Portraits/</code>,
              filed under <b>Active</b> or <b>Non-Active</b> to match their status in the app. Switching
              someone in Team → Members moves their folder, with everything in it, to the other half —
              you do not need to run this for that.
              <div style={{ marginTop: 8, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, color: '#6E6C66', lineHeight: 1.7 }}>
                Active / Robert Verrijt /<br />
                &nbsp;&nbsp;Robert Verrijt — Portrait.jpg&nbsp;&nbsp;<span style={{ color: '#9A988F' }}>← the current profile photo</span><br />
                &nbsp;&nbsp;Originals /&nbsp;&nbsp;<span style={{ color: '#9A988F' }}>← every earlier version and base file</span>
              </div>
              <div style={{ marginTop: 8 }}>
                Bios (Word / Pages) go in the member’s folder beside the portrait. Nothing here is ever
                deleted, and no file the sync did not write is moved — a document you drop in stays put.
              </div>
            </div>

            {portraitOp.error && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#FEF2F2', border: '1px solid rgba(192,57,43,.25)', borderRadius: 4, padding: '10px 14px', marginBottom: 10, fontSize: 12, color: '#922B21' }}>
                <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>Portrait sync failed</div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, wordBreak: 'break-all' }}>{portraitOp.error}</div>
                </div>
              </div>
            )}

            {portraitOp.results && (() => {
              // A run over a settled roster is 200 rows of "nothing happened".
              // Only the members something was done to, or failed for, are worth
              // listing; the rest collapse to a count.
              const notable = portraitOp.results.filter(r => r.action !== 'none' || r.uploaded?.length || r.archived?.length || r.errors?.length);
              const quiet = portraitOp.results.length - notable.length;
              const verb = portraitOp.mode === 'verify'
                ? { create: 'folder missing', move: 'in the wrong half', rename: 'named differently' }
                : { create: 'folder created', move: 'moved', rename: 'renamed' };
              return (
                <div style={{ fontSize: 12, color: '#6E6C66', borderTop: '1px solid rgba(27,27,25,.06)', paddingTop: 10 }}>
                  {quiet > 0 && (
                    <div style={{ marginBottom: 8, color: '#9A988F' }}>
                      {quiet} team member(s) already correct.
                    </div>
                  )}
                  {notable.length === 0 && <div style={{ color: '#9A988F' }}>Nothing to do.</div>}
                  {notable.map(r => (
                    <div key={r.userId} style={{ marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, color: '#1B1B19' }}>{r.folderName}</span>
                      <span style={{ color: '#9A988F' }}> · {r.bucket}</span>
                      {verb[r.action] && <span style={{ color: portraitOp.mode === 'verify' ? '#9F902A' : '#27AE60' }}>, {verb[r.action]}</span>}
                      {r.from && <span style={{ color: '#9A988F' }}> (from {r.from.split('/Portraits/')[1] || r.from})</span>}
                      {r.uploaded?.length > 0 && <span style={{ color: '#27AE60' }}>, {r.uploaded.length} file(s) uploaded</span>}
                      {r.archived?.length > 0 && <span style={{ color: '#9F902A' }}>, {r.archived.length} archived to Originals</span>}
                      {r.errors?.length > 0 && (
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#C0392B', paddingLeft: 10 }}>
                          {r.errors.map((e, i) => <div key={i}>{e}</div>)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Dropbox press folder schema editor */}
          <div style={S.card}>
            <BlockTitle
              left="Dropbox press folder schema"
              right="per-project trees — one folder per comms-enabled project"
            />
            <div style={{ ...S.body, marginBottom: 12, fontSize: 12 }}>
              Folder path: <code style={{ background: 'rgba(27,27,25,.06)', padding: '2px 5px', borderRadius: 2, fontSize: 11 }}>/Architecture Brio/BRIO-press/{'<CODE>_<NAME>'}/<i>section</i>/</code>
            </div>

            {/* Three actions, each stating what it reads and what it writes.
                They used to be two header buttons plus an underlined link in a
                grey sentence, which hid the one that re-reads Dropbox — the
                only one that notices a folder someone added there by hand. */}
            {canEdit && (() => {
              const busy = folderOp.loading;
              const waiting = busy && folderOp.kind === 'practice'
                ? 'Waiting — the practice folder run is finishing'
                : null;
              const progress = folderOp.progress ? `${folderOp.progress.done}/${folderOp.progress.total}` : '';
              const ACTIONS = [
                {
                  kind: 'verify',
                  label: 'Check only',
                  busyLabel: `Checking… ${progress}`,
                  run: verifyAllFolders,
                  outline: true,
                  blurb: <>Reads every project in Dropbox and lists what the schema says is missing. <b>Creates nothing.</b></>,
                },
                {
                  kind: 'projects',
                  label: 'Create missing',
                  busyLabel: `Creating… ${progress}`,
                  run: createAllFolders,
                  blurb: <>Creates the folders a project is missing, and renames a project's folder to match a project renamed in the app. Projects unchanged since their last check are <b>skipped without reading Dropbox</b> — which is what makes this quick.</>,
                },
                {
                  kind: 'force',
                  label: 'Full re-read',
                  busyLabel: `Re-reading… ${progress}`,
                  run: forceSyncAllFolders,
                  blurb: <>The same, but re-reads <b>every</b> project in Dropbox instead of skipping any. Use this after adding or deleting folders in Dropbox by hand — those are invisible to “Create missing”.</>,
                },
              ];
              return (
                <div style={{ border: '1px solid rgba(27,27,25,.10)', borderRadius: 4, marginBottom: 14 }}>
                  {ACTIONS.map((a, i) => (
                    <div key={a.kind} style={{
                      display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start',
                      padding: '9px 12px',
                      borderTop: i === 0 ? 'none' : '1px solid rgba(27,27,25,.07)',
                    }}>
                      <button
                        onClick={a.run}
                        disabled={busy}
                        title={waiting || undefined}
                        style={{
                          background: a.outline ? 'transparent' : '#21302A',
                          color: a.outline ? '#21302A' : '#fff',
                          border: a.outline ? '1px solid rgba(27,27,25,.25)' : 'none',
                          borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                          letterSpacing: '0.08em', textTransform: 'uppercase',
                          cursor: busy ? 'default' : 'pointer', padding: '5px 10px',
                          opacity: busy ? 0.6 : 1, whiteSpace: 'nowrap', minWidth: 124, flexShrink: 0,
                        }}
                      >
                        {busy && folderOp.kind === a.kind ? a.busyLabel : a.label}
                      </button>
                      <div style={{ flex: '1 1 240px', fontSize: 11.5, lineHeight: 1.55, color: '#6E6C66' }}>{a.blurb}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {schemaLoading
              ? <div style={S.meta}>Loading schema…</div>
              : <>
              {(schema || []).map(section => (
                <div key={section.key} style={{ marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid rgba(27,27,25,.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 10 }}>
                    <div style={S.eyebrow}>{section.folder}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {(canEdit || section.allowAnySubfolders) && (
                        <label
                          title="Nothing found directly inside this section is flagged as unexpected. Named subfolders are still created and still reported when missing."
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: section.allowAnySubfolders ? '#33473F' : '#9A988F', cursor: canEdit ? 'pointer' : 'default' }}
                        >
                          <input
                            type="checkbox"
                            checked={!!section.allowAnySubfolders}
                            disabled={!canEdit}
                            onChange={() => toggleAllowAnySubfolders(section.key)}
                            style={{ accentColor: GOLD, cursor: canEdit ? 'pointer' : 'default', margin: 0 }}
                          />
                          Allow any subfolder
                        </label>
                      )}
                      {canEdit && !DEFAULT_SECTION_KEYS.has(section.key) && (
                        <button onClick={() => removeSection(section.key)} title="Remove section" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: '0 2px' }}><Trash2 size={12} /></button>
                      )}
                    </div>
                  </div>
                  {/* Standard subfolders */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    {section.subfolders.map((sf, idx) => (
                      <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EDEBE5', borderRadius: 3, padding: '2px 8px', fontSize: 12, color: '#33473F' }}>
                        {sf}
                        {canEdit && (
                          <button onClick={() => removeSubfolder(section.key, idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 0, lineHeight: 1, fontSize: 13 }}>×</button>
                        )}
                      </span>
                    ))}
                    {section.subfolders.length === 0 && <span style={{ ...S.meta, fontSize: 11 }}>No subfolders</span>}
                    {canEdit && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input
                          value={newSubfolderDrafts[section.key] || ''}
                          onChange={e => setNewSubfolderDrafts(d => ({ ...d, [section.key]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') addSubfolder(section.key); }}
                          placeholder="Add subfolder…"
                          style={{ fontSize: 12, border: '1px dashed rgba(27,27,25,.25)', background: 'transparent', borderRadius: 3, padding: '2px 6px', outline: 'none', width: 120 }}
                        />
                        <button onClick={() => addSubfolder(section.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#33473F', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>+</button>
                      </span>
                    )}
                  </div>
                  {/* Sub-subfolder template (applied inside every subfolder of this section) */}
                  {(canEdit || (section.subfolderTemplate || []).length > 0) && (
                    <div style={{ paddingLeft: 10, borderLeft: '2px solid rgba(159,144,42,.25)', marginTop: 4 }}>
                      <div style={{ ...S.meta, fontSize: 10, color: '#9F902A', marginBottom: 4 }}>Template inside each subfolder</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                        {(section.subfolderTemplate || []).map((sf, idx) => (
                          <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#F5F0DC', borderRadius: 3, padding: '2px 8px', fontSize: 12, color: '#7A6020' }}>
                            {sf}
                            {canEdit && (
                              <button onClick={() => removeSubfolderTemplate(section.key, idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A060', padding: 0, lineHeight: 1, fontSize: 13 }}>×</button>
                            )}
                          </span>
                        ))}
                        {(section.subfolderTemplate || []).length === 0 && <span style={{ ...S.meta, fontSize: 11 }}>None — add folders to auto-create inside each subfolder</span>}
                        {canEdit && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <input
                              value={newSubfolderTemplateDrafts[section.key] || ''}
                              onChange={e => setNewSubfolderTemplateDrafts(d => ({ ...d, [section.key]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') addSubfolderTemplate(section.key); }}
                              placeholder="Add template folder…"
                              style={{ fontSize: 12, border: '1px dashed rgba(159,144,42,.4)', background: 'transparent', borderRadius: 3, padding: '2px 6px', outline: 'none', width: 140 }}
                            />
                            <button onClick={() => addSubfolderTemplate(section.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9F902A', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>+</button>
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {canEdit && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, paddingTop: 14, borderTop: '1px solid rgba(27,27,25,.06)' }}>
                  <input
                    value={newSectionDraft}
                    onChange={e => setNewSectionDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addSection(); }}
                    placeholder="New section name (e.g. Video, Site Survey)…"
                    style={{ flex: 1, fontSize: 12, border: '1px dashed rgba(27,27,25,.3)', background: 'transparent', borderRadius: 3, padding: '4px 8px', outline: 'none', fontFamily: 'inherit' }}
                  />
                  <button onClick={addSection} style={{ background: '#21302A', color: '#fff', border: 'none', borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', padding: '5px 12px', whiteSpace: 'nowrap' }}>+ Add</button>
                </div>
              )}
              </>
            }
          </div>

          {/* Approved custom subfolders */}
          {(() => {
            const approvedEntries = Object.entries(approvedSubfolders).flatMap(([sectionFolder, names]) =>
              names.map(name => ({ sectionFolder, name, display: sectionFolder === '__root__' ? name : `${sectionFolder} / ${name}` }))
            );
            if (approvedEntries.length === 0) return null;
            return (
              <div style={S.card}>
                <BlockTitle left="Approved custom subfolders" right="folders approved via audit — click × to revoke" />
                <div style={{ ...S.body, fontSize: 12, marginBottom: 10 }}>
                  These folders were found in Dropbox outside the schema and approved. They appear in grey (not gold) in project cards.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {approvedEntries.map(({ sectionFolder, name, display }) => (
                    <span key={`${sectionFolder}/${name}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EDEBE5', borderRadius: 3, padding: '2px 8px', fontSize: 12, color: '#33473F' }}>
                      {display}
                      {canEdit && (
                        <button onClick={() => removeApproved(sectionFolder, name)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 0, lineHeight: 1, fontSize: 13 }}>×</button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Folder creation error */}
          {folderOp.error && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#FEF2F2', border: '1px solid rgba(192,57,43,.25)', borderRadius: 4, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: '#922B21' }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Folder creation failed</div>
                <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, wordBreak: 'break-all' }}>{folderOp.error}</div>
              </div>
            </div>
          )}

          {/* Folder creation results */}
          {folderOp.results && folderOp.results.length > 0 && (
            <div style={{ ...S.card, fontSize: 12, color: '#6E6C66' }}>
              <BlockTitle left={folderOp.kind === 'practice'
                ? 'Folder creation results — 00_Practice'
                : folderOp.kind === 'verify'
                  ? `Schema check — ${folderOp.results.length} project(s)`
                  : folderOp.kind === 'force'
                    ? `Full re-read — ${folderOp.results.length} project(s)`
                    : `Folder sync results — ${folderOp.results.length} project(s)`} />
              {(() => {
                // A run over an unchanged estate is a wall of "unchanged" rows
                // that says nothing; collapse it to a count and list only the
                // projects something actually happened to.
                const skipped = folderOp.results.filter(r => r.skipped && !r.folderNameMismatch);
                const notable = folderOp.results.filter(r => !r.skipped || r.folderNameMismatch);
                return (
                  <>
                    {skipped.length > 0 && (
                      <div style={{ marginBottom: 8, color: '#9A988F' }}>
                        {skipped.length} project(s) unchanged since their last check — <b>Dropbox was not read</b> for these,
                        so a folder added or deleted there by hand would not have been noticed.{' '}
                        {canEdit && folderOp.kind !== 'force' && (
                          <button
                            onClick={forceSyncAllFolders}
                            disabled={folderOp.loading}
                            style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#9F902A', textDecoration: 'underline', cursor: folderOp.loading ? 'default' : 'pointer' }}
                          >
                            Run a full re-read
                          </button>
                        )}
                      </div>
                    )}
                    {notable.length === 0 && skipped.length > 0 && (
                      <div style={{ color: '#9A988F' }}>Nothing was created.</div>
                    )}
                    {notable.map(r => (
                      <div key={r.project_code} style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, color: '#1B1B19' }}>{r.project_code} {r.name}</span>
                        {' · '}
                        {r.created.length > 0
                          ? <span style={{ color: '#27AE60' }}>{r.created.length} created</span>
                          : r.skipped
                            ? <span style={{ color: '#9A988F' }}>unchanged</span>
                            : <span style={{ color: '#9A988F' }}>{r.existing.length} folder(s) present</span>}
                        {r.missing?.length > 0 && <span style={{ color: '#9F902A' }}>, {r.missing.length} missing</span>}
                        {r.projectRenamed && <span style={{ color: '#27AE60' }}>, folder renamed</span>}
                        {r.renamed?.length > 0 && <span style={{ color: '#27AE60' }}>, {r.renamed.length} subfolder(s) renamed</span>}
                        {r.errors.length > 0 && <span style={{ color: '#C0392B' }}>, {r.errors.length} errors</span>}
                        {r.missing?.length > 0 && (
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#9F902A', paddingLeft: 10 }}>
                            {r.missing.slice(0, 8).join(' · ')}{r.missing.length > 8 ? ` · +${r.missing.length - 8} more` : ''}
                          </div>
                        )}
                        {/* The project folder itself moved: the one line the
                            person who ran this will see saying so, and the name
                            everything in Dropbox now sits under. */}
                        {r.projectRenamed && (
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#27AE60', paddingLeft: 10 }}>
                            {r.projectRenamed.from} → {r.projectRenamed.to}
                          </div>
                        )}
                        {r.folderNameMismatch && (
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#9F902A', paddingLeft: 10 }}>
                            {folderOp.kind === 'verify' ? 'in' : 'still in'} <b>{r.folderNameMismatch.actual}</b> — expected <b>{r.folderNameMismatch.expected}</b>
                            {folderOp.kind !== 'verify' && ' — not renamed'}
                          </div>
                        )}
                        {/* A rename moved somebody's folder, so it says which one.
                            Listed in full rather than truncated: this is the only
                            record of it the person who ran the sync will see. */}
                        {r.renamed?.length > 0 && (
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#27AE60', paddingLeft: 10 }}>
                            {r.renamed.map((label, i) => <div key={i}>{label}</div>)}
                          </div>
                        )}
                        {/* Spelled out rather than left as a count: an error here
                            names the Dropbox folder somebody has to go and look at,
                            and a bare "1 errors" sends them to the function logs. */}
                        {r.errors.length > 0 && (
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#C0392B', paddingLeft: 10 }}>
                            {r.errors.slice(0, 4).map((msg, i) => <div key={i}>{msg}</div>)}
                            {r.errors.length > 4 && <div>+{r.errors.length - 4} more</div>}
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          )}
          </>)}

        </div>
      )}

      {sub === 'monograph' && (
        <div>
          <ThemeAssetsSection themeId="practice" section="monograph" projects={data.projects || []} canEdit={canEdit} showToast={showToast} />
          <div style={S.card}>
            <BlockTitle left="Monograph" right="20 years of BRIO — work in progress" />
            <p style={{ ...S.body, marginBottom: 12 }}>Gather everything that could feed a monograph — a publication, a film, or both. What would help:</p>
            <div style={{ marginBottom: 20 }}>
              {MONO_PROMPTS.map((q, i) => (
                <div key={i} style={{ ...S.card, marginBottom: 6, cursor: 'default', padding: '10px 14px' }}>
                  <div style={S.body}>{q}</div>
                </div>
              ))}
            </div>
            <BlockTitle left="Notes & ideas" />
            <NoteArea value={ms.notes || ''} onChange={v => saveSetting('monograph_settings', 'notes', v)} placeholder="The story of 20 years — publication, film, or both…" minHeight={140} />
          </div>
          <div style={S.card}>
            <BlockTitle left="Monograph details" />
            <InlineInput value={ms.title || ''} onChange={v => saveSetting('monograph_settings', 'title', v)} placeholder="Title…" style={{ ...S.h3, fontFamily: "'Spectral', Georgia, serif", border: 'none', background: 'transparent', padding: 0, marginBottom: 8, width: '100%' }} />
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <InlineInput value={ms.publisher || ''} onChange={v => saveSetting('monograph_settings', 'publisher', v)} placeholder="Publisher…" style={{ flex: 1 }} />
              <InlineInput value={ms.year || ''} onChange={v => saveSetting('monograph_settings', 'year', v)} placeholder="Year…" style={{ width: 80 }} />
            </div>
          </div>
          <div style={S.card}>
            <BlockTitle left="Documents" />
            {monoDocs.length === 0 && <EmptyNote text="No documents yet." />}
            {monoDocs.map((d, i) => deckRow(d.name, null, d.link, () => removeMonoDoc(i)))}
            {canEdit && (
              <AddLine>
                <InlineInput value={newMonoDoc} onChange={setNewMonoDoc} placeholder="Add a document — name or link…" style={{ flex: 1 }} />
                <BtnGhost onClick={addMonoDoc}>+ Add</BtnGhost>
              </AddLine>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CollaboratorsPage({ data, onBack, canEdit, refresh, showToast }) {
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');

  const collaborators = (data.collaborators || []).filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.role?.toLowerCase().includes(search.toLowerCase())
  );

  const addCollab = async () => {
    if (!newName.trim()) return;
    try { await commsInsert('collaborators', { comms_id: 'col-' + Date.now(), name: newName.trim() }); setNewName(''); refresh(); showToast('Added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteCollab = async (c) => {
    if (!window.confirm(`Delete ${c.name || 'this collaborator'}? Their press kit section (highlights, linked folders, filters) is removed too. Files in Dropbox are not touched.`)) return;
    try {
      // Clean up the collaborator's theme section rows (section = collaborator id)
      await comms.from('theme_assets').delete().eq('theme_id', 'collaborators').eq('section', c.comms_id);
      await comms.from('theme_folders').delete().eq('theme_id', 'collaborators').eq('section', c.comms_id);
      await commsDelete('collaborators', 'comms_id', c.comms_id);
      refresh();
      showToast('Collaborator deleted');
    } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All themes" />
      <h1 style={{ ...S.h1, marginBottom: 16 }}>Collaborators</h1>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search collaborators…" style={{ ...S.inputStyle, maxWidth: 320, marginBottom: 20 }} />
      {collaborators.map(c => (
        <div key={c.comms_id} style={{ ...S.card, position: 'relative' }}>
          {canEdit && (
            <button onClick={() => deleteCollab(c)} title="Delete collaborator"
              style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}>
              <Trash2 size={13} />
            </button>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12, paddingRight: canEdit ? 24 : 0 }}>
            <InlineInput value={c.name} onBlurSave={async v => { try { await commsUpdate('collaborators', 'comms_id', c.comms_id, { name: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Name…" style={{ fontFamily: "'Spectral', Georgia, serif", fontSize: 18, border: 'none', background: 'transparent', padding: 0 }} />
            <InlineInput value={c.role} onBlurSave={async v => { try { await commsUpdate('collaborators', 'comms_id', c.comms_id, { role: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Role / discipline…" />
          </div>
          <ThemeAssetsSection themeId="collaborators" section={c.comms_id} sectionLabel={c.name} projects={data.projects || []} canEdit={canEdit} showToast={showToast} defaultCollapsed />
          {[
            ['bio', 'Bio'],
            ['first_contact', 'First contact'],
            ['last_touch', 'Last touchpoint'],
            ['how_worked', 'How we worked together'],
            ['why', 'Why them'],
            ['quality', 'Quality of collaboration'],
            ['standout', 'What stood out'],
            ['story', 'Collaborator story'],
          ].map(([field, label]) => (
            <div key={field} style={{ marginBottom: 12 }}>
              <div style={{ ...S.eyebrow, marginBottom: 4 }}>{label}</div>
              <NoteArea value={c[field]} onChange={async v => { try { await commsUpdate('collaborators', 'comms_id', c.comms_id, { [field]: v }); } catch {} }} placeholder={`${label}…`} minHeight={48} />
            </div>
          ))}
        </div>
      ))}
      {canEdit && (
        <AddLine>
          <InlineInput value={newName} onChange={setNewName} placeholder="Collaborator name…" style={{ maxWidth: 280 }} />
          <BtnGhost onClick={addCollab}>+ Add collaborator</BtnGhost>
        </AddLine>
      )}
    </div>
  );
}

// ── PROJECTS TAB ──────────────────────────────────────────────────────────

// ── PROJECT COLOUR CODING ────────────────────────────────────────────────
// Classifies each project into one of 8 visual categories based on status,
// stage, publication record, and confidentiality. Confidential overrides all.

const PROJECT_COLOR_CATEGORIES = {
  // Completed family — sage greens (dark → mid → pale)
  'completed-published':   { label: 'Completed & published',        bg: '#E8F2EE', border: '#1A4D3C', text: null },
  'completed-unpublished': { label: 'Completed, not yet published', bg: '#F0F7F4', border: '#4A8C6A', text: null },
  'recently-completed':    { label: 'Recently completed (< 1 yr)',  bg: '#F7FBF9', border: '#8ABDA4', text: null },
  // Inactive / confidential family — warm grey / brown
  unbuilt:                 { label: 'Unbuilt (paused / archived)',  bg: '#F5F3F0', border: '#8C8070', text: null },
  confidential:            { label: 'Confidential (NDA)',           bg: '#EDE9E4', border: '#4A3C30', text: '#6A5E50' },
  // Active family — indigo blues (dark → mid → pale)
  'on-site':               { label: 'Active — on site',            bg: '#EAECF8', border: '#1E306E', text: null },
  'design-development':    { label: 'Active — design development', bg: '#F1F3FB', border: '#5068B8', text: null },
  'concept-schematic':     { label: 'Active — concept / schematic',bg: '#F7F8FD', border: '#90A0D8', text: null },
};

function classifyActiveStage(stageTimeline) {
  const isDone = tl => !!(tl && (tl.actualDate || tl.paidDate || tl.clientApprovalDate));
  const hasData = tl => !!(tl && (tl.contractDate || tl.targetDate || tl.actualDate || tl.paidDate || tl.startDate || tl.clientApprovalDate));
  const stages = [
    { key: 'advance',  cat: 'concept-schematic' },
    { key: 'cd_ad',    cat: 'concept-schematic' },
    { key: 'cd_id',    cat: 'concept-schematic' },
    { key: 'sd_ad',    cat: 'concept-schematic' },
    { key: 'sd_id',    cat: 'concept-schematic' },
    { key: 'dd_ad',    cat: 'design-development' },
    { key: 'dd_id',    cat: 'design-development' },
    { key: 'aoc_ad',   cat: 'design-development' },
    { key: 'aoc_id',   cat: 'design-development' },
    { key: 'td_ad',    cat: 'design-development' },
    { key: 'td_id',    cat: 'design-development' },
    { key: 'gfc1',     cat: 'design-development' },
    { key: 'gfc2',     cat: 'design-development' },
    { key: 'gfc3',     cat: 'design-development' },
    { key: 'cc',       cat: 'on-site' },
    { key: 'pcd',      cat: 'on-site' },
  ];
  for (const s of stages) {
    const tl = stageTimeline[s.key];
    if (hasData(tl) && !isDone(tl)) return s.cat;
  }
  const withData = stages.filter(s => hasData(stageTimeline[s.key]));
  return withData.length ? withData[withData.length - 1].cat : 'concept-schematic';
}

function classifyProject(project, publications) {
  if (project.confidential) return 'confidential';
  const status = project.status || 'active';
  if (status === 'paused' || status === 'archived') return 'unbuilt';
  const hasPub = (publications || []).some(
    pub => Array.isArray(pub.project_codes) && pub.project_codes.includes(project.project_code)
  );
  if (status === 'completed') {
    const completionActual = project.stage_timeline?.completion?.actualDate;
    const isRecent = completionActual &&
      new Date(completionActual) > new Date(Date.now() - 365 * 24 * 3600000);
    if (hasPub) return 'completed-published';
    if (isRecent) return 'recently-completed';
    return 'completed-unpublished';
  }
  return classifyActiveStage(project.stage_timeline || {});
}

function ProjectsTab({ data, canEdit, rev, refresh, showToast, onOpenProject }) {
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  const allProjects = data.projects || [];

  // Category filter options, derived from the typologies present in the data
  const categories = Array.from(new Set(allProjects.map(p => p.typology).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));
  const filterOptions = ['All', 'Priority', ...categories];

  // Status filter options in display order (matches PROJECT_COLOR_CATEGORIES order)
  const STATUS_FILTER_OPTIONS = [
    'All',
    ...Object.keys(PROJECT_COLOR_CATEGORIES),
  ];

  const projects = allProjects
    .filter(p =>
      !search || p.project_code?.toLowerCase().includes(search.toLowerCase()) || p.name?.toLowerCase().includes(search.toLowerCase()) || p.city?.toLowerCase().includes(search.toLowerCase()) || p.typology?.toLowerCase().includes(search.toLowerCase())
    )
    .filter(p => {
      if (catFilter === 'All') return true;
      if (catFilter === 'Priority') return !!p.priority;
      return p.typology === catFilter;
    })
    .filter(p => {
      if (statusFilter === 'All') return true;
      return classifyProject(p, data.publications) === statusFilter;
    })
    // Priority (starred) projects first, then alphabetical by project code
    .sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0) || (a.project_code || '').localeCompare(b.project_code || ''));

  const addProject = async () => {
    if (!newName.trim()) return;
    const slug = newName.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();
    try { await commsInsert('projects', { comms_id: slug, name: newName.trim(), sort_order: (data.projects || []).length + 1, nts: {}, kit: {}, coverage: [], awards_list: [] }); setNewName(''); refresh(); showToast('Project added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const togglePriority = async (p) => {
    try {
      if (p._has_enrich) {
        await commsUpdate('projects', 'project_code', p.project_code, { priority: !p.priority });
      } else {
        // No comms.projects enrichment row yet — create one keyed by project_code
        await commsInsert('projects', { comms_id: p.project_code, project_code: p.project_code, name: p.name, priority: true, nts: {}, kit: {}, coverage: [], awards_list: [] });
      }
      refresh();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const noteCount = p => {
    if (!p.nts || typeof p.nts !== 'object') return 0;
    return Object.values(p.nts).reduce((acc, cat) => acc + (Array.isArray(cat) ? cat.length : (Array.isArray(cat?.entries) ? cat.entries.length : 0)), 0);
  };

  const capitalize = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...S.eyebrow, marginBottom: 6 }}>Project stream</div>
        <h1 style={S.h1}>Projects</h1>
        <p style={{ ...S.body, marginTop: 10, maxWidth: 560 }}>Ordered by project code. Filter by category, star the ones that matter, and open a project for its press kit, notes, potential press, and the record of where it has been shared.</p>
      </div>
      {/* Dropbox told us something moved. Shown before the filters because it is
          about projects the current filter may well be hiding. */}
      <FolderDriftBanner
        rev={rev}
        onOpenProject={onOpenProject}
        projectNames={Object.fromEntries(allProjects.map(p => [p.project_code, p.name]))}
      />
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects…" style={{ ...S.inputStyle, maxWidth: 360, marginBottom: 16 }} />
      {/* Typology filter chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {filterOptions.map(f => (
          <FilterBtn key={f} label={(f === 'All' || f === 'Priority') ? f + (f === 'Priority' ? ' ★' : '') : capitalize(f)} active={catFilter === f} onClick={() => setCatFilter(f)} />
        ))}
      </div>
      {/* Status filter chips — colour-coded, doubles as legend */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {STATUS_FILTER_OPTIONS.map(key => {
          const c = PROJECT_COLOR_CATEGORIES[key];
          const isActive = statusFilter === key;
          if (key === 'All') {
            return <FilterBtn key="All" label="All statuses" active={isActive} onClick={() => setStatusFilter('All')} />;
          }
          return (
            <button
              key={key}
              onClick={() => setStatusFilter(isActive ? 'All' : key)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 20, cursor: 'pointer',
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                border: `1.5px solid ${c.border}`,
                background: isActive ? c.border : c.bg,
                color: isActive ? '#fff' : (c.text || c.border),
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 2, background: isActive ? 'rgba(255,255,255,0.5)' : c.border, flexShrink: 0, display: 'inline-block' }} />
              {c.label}
            </button>
          );
        })}
      </div>
      <div style={S.grid2}>
        {projects.map((p, i) => {
          const isPriority = !!p.priority;
          const category = classifyProject(p, data.publications);
          const colorStyle = PROJECT_COLOR_CATEGORIES[category] || {};
          const isConfidential = category === 'confidential';
          // A full-width empty row between the last starred project and the
          // first unstarred one, so the two groups read apart.
          const startsUnstarred = !isPriority && i > 0 && !!projects[i - 1].priority;
          return (
            <React.Fragment key={p.comms_id}>
            {startsUnstarred && <div aria-hidden="true" style={{ gridColumn: '1 / -1', height: 24 }} />}
            <div style={{ ...S.card, marginBottom: 0, backgroundColor: colorStyle.bg || S.card.backgroundColor, borderColor: colorStyle.border || S.card.borderColor, borderLeftWidth: 12, borderLeftStyle: 'solid', borderLeftColor: colorStyle.border || 'rgba(27,27,25,.12)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...S.eyebrow, marginBottom: 4, color: isConfidential ? '#8A8880' : undefined }}>
                    {[p.project_code, p.typology, p.city, p.year_start].filter(Boolean).join(' · ')}
                    {isConfidential && <span style={{ marginLeft: 8, color: '#9A988F', fontSize: 9 }}>CONFIDENTIAL</span>}
                  </div>
                  <button onClick={() => onOpenProject(p.comms_id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', display: 'block', width: '100%' }}>
                    <h3 style={{ ...S.h3, color: isConfidential ? '#A8A5A0' : undefined, textDecoration: 'underline', textDecorationColor: 'rgba(27,27,25,.2)', textUnderlineOffset: 3 }}>{p.name}</h3>
                  </button>
                </div>
                {canEdit && (
                  <button
                    onClick={() => togglePriority(p)}
                    title="Toggle priority"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: isPriority ? GOLD : '#CCC', flexShrink: 0 }}
                  >
                    <Star size={16} fill={isPriority ? GOLD : 'none'} />
                  </button>
                )}
              </div>
              {p.byline && <p style={{ ...S.body, marginTop: 4, fontStyle: 'italic', fontSize: 13, color: isConfidential ? '#7A7870' : undefined }}>{p.byline}</p>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                <span style={{ ...S.meta, color: isConfidential ? '#7A7870' : undefined }}>{noteCount(p)}/7 notes</span>
                <button onClick={() => onOpenProject(p.comms_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: isConfidential ? '#8A8880' : '#21302A', display: 'flex', alignItems: 'center', gap: 4 }}>
                  Open <ChevronRight size={12} />
                </button>
              </div>
            </div>
            </React.Fragment>
          );
        })}
      </div>
      {projects.length === 0 && <EmptyNote text="No projects in this category yet." />}
      {/* Projects are managed in the main app (comms_enabled flag on Projects page) */}
    </div>
  );
}

// ── PROJECT DETAIL ────────────────────────────────────────────────────────

const NTS_CATS = [
  ['client','Client story / origin of the brief'],
  ['inspiration','Initial inspirations / concept'],
  ['climate','Climate, geography, landscape'],
  ['materials','Materials & sourcing'],
  ['construction','Construction process / details'],
  ['learnings','Learnings — cross-cultural, unexpected'],
  ['change','What would we do differently'],
];

// Version-history key for a single project note (see comms.field_versions).
const noteFieldKey = (cat, noteId) => `nts.${cat}.${noteId}`;

function genDraft(project) {
  const nts = project.nts || {};
  const getLatest = cat => {
    const arr = Array.isArray(nts[cat]) ? nts[cat] : [];
    return arr.length ? arr[0].text : '';
  };
  return `ARCHITECTURE BRIO — PRESS RELEASE

${project.name || 'Project Name'}
${[project.typology, project.city, project.year_start].filter(Boolean).join(' · ')}

${project.byline || ''}

CLIENT & BRIEF
${getLatest('client') || '[client story]'}

CONCEPT & INSPIRATION
${getLatest('inspiration') || '[concept notes]'}

CLIMATE & CONTEXT
${getLatest('climate') || '[climate and geography notes]'}

MATERIALS
${getLatest('materials') || '[materials notes]'}

CONSTRUCTION
${getLatest('construction') || '[construction process notes]'}

${project.comms_ideas ? `COMMS IDEAS\n${project.comms_ideas}\n` : ''}
—
Architecture BRIO
Rotterdam · Mumbai
`;
}

// ── PROJECT FACTS DRAWER ────────────────────────────────────────────────────
// Compact, collapsible facts panel shown at the top of a comms project detail.
// Populated from the main app registry (public.projects + clients), with the
// current stage derived from the project's stage timeline — same stage keys
// and ordering as TimelinePage.

// Satellite thumbnail for the facts panel, on the same footing as the project
// map page: its GoogleSatelliteMap draws through the Maps JavaScript API with
// disableDefaultUI, so there is no pan D-pad, no inset overview map and no
// controls bar along the bottom — none of which the legacy
// `maps.google.com/maps?output=embed` iframe can be talked out of drawing once
// the frame is this big. (The Maps Embed API would also render a clean map, but
// REACT_APP_GOOGLE_MAPS_KEY is not authorised for it — the key is enabled for
// the JavaScript API only, the same restriction that keeps src/utils/elevation.js
// off Google's Elevation API.)
//
// The JS map needs a lat/lng, where the iframe took any address text. Project
// addresses are usually a coordinate pair already; anything else is geocoded
// through the project map page's shared Nominatim queue, and the old iframe
// stands in while that resolves or if it finds nothing.
const factsMapSrc = (address) =>
  `https://maps.google.com/maps?q=${encodeURIComponent(address)}&t=k&z=16&ie=UTF8&iwloc=&output=embed`;

const FACTS_MAP_ZOOM = 16;
// How long the JS map gets to paint before the panel gives up on it. Google
// renders its own "Sorry! Something went wrong" card inside the map div when the
// key is rejected — and not every such failure reaches gm_authFailure — so the
// only reliable signal that the map came up is tiles actually arriving.
const FACTS_MAP_TILE_TIMEOUT_MS = 6000;

// Google draws its logo and map-data links along the bottom edge of both the JS
// map and the legacy iframe. Rather than paint over that strip, the map hangs
// this far below the frame and the frame's overflow clips it — so the framed
// border sits on the crop itself, with no masked band inside it.
const FACTS_MAP_CROP = 26;
// pointerEvents off so clicks land on the enclosing "open in Maps" link rather
// than on a map that ignores gestures anyway.
const FACTS_MAP_LAYER = {
  position: 'absolute', top: 0, left: 0, right: 0, bottom: -FACTS_MAP_CROP,
  pointerEvents: 'none',
};
// Full-bleed crosshair marking the map centre — the project's own coordinates —
// in place of a dropped pin. The centre it marks is the map's, which the crop
// pushes below the frame's own middle.
const FACTS_MAP_CROSS_H = {
  position: 'absolute', top: `calc(50% + ${FACTS_MAP_CROP / 2}px)`, left: 0, right: 0, height: 1,
  background: 'rgba(255,255,255,.85)', pointerEvents: 'none',
};
const FACTS_MAP_CROSS_V = {
  position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1,
  background: 'rgba(255,255,255,.85)', pointerEvents: 'none',
};

function FactsMap({ address }) {
  const parsed = useMemo(() => parseCoordinates(address), [address]);
  // Geocoded coordinates are held with the address they were looked up for, so
  // a pending lookup for one project can never be read as another's.
  const [geocoded, setGeocoded] = useState(null);

  useEffect(() => {
    if (!address || parsed) { setGeocoded(null); return undefined; }
    let cancelled = false;
    setGeocoded(null);
    queueGeocode(address)
      .then(coords => { if (!cancelled && coords) setGeocoded({ address, coords }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [address, parsed]);

  const point = parsed || (geocoded?.address === address ? geocoded.coords : null);

  // Fall back to the keyless embed if the JS map never paints, so a rejected or
  // misconfigured API key costs the panel its clean map, not its map.
  const [mapFailed, setMapFailed] = useState(false);
  const failTimer = useRef(null);
  const pointKey = point ? `${point.lat},${point.lng}` : '';
  useEffect(() => {
    setMapFailed(false);
    if (!pointKey) return undefined;
    failTimer.current = setTimeout(() => setMapFailed(true), FACTS_MAP_TILE_TIMEOUT_MS);
    return () => clearTimeout(failTimer.current);
  }, [pointKey]);
  const handleTilesLoaded = useCallback(() => {
    clearTimeout(failTimer.current);
    setMapFailed(false);
  }, []);

  return (
    <>
      <div style={FACTS_MAP_LAYER}>
        {point && !mapFailed ? (
          <GoogleSatelliteMap
            lat={point.lat}
            lng={point.lng}
            zoom={FACTS_MAP_ZOOM}
            onTilesLoaded={handleTilesLoaded}
          />
        ) : (
          <iframe
            title="Project location"
            width="100%" height="100%"
            style={{ border: 0, display: 'block' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={factsMapSrc(address)}
          />
        )}
      </div>
      <div style={FACTS_MAP_CROSS_H} />
      <div style={FACTS_MAP_CROSS_V} />
    </>
  );
}

// Default stage id → stage_timeline key mapping (mirrors TimelinePage).
const FACTS_STAGE_ID_TO_KEY = {
  1: 'advance', 2: 'cd_ad', 3: 'cd_id', 4: 'sd_ad', 5: 'sd_id',
  6: 'dd_ad', 7: 'dd_id', 8: 'aoc_ad', 9: 'aoc_id', 10: 'td_ad', 11: 'td_id',
  12: 'gfc1', 13: 'gfc2', 14: 'gfc3', 15: 'completion', 16: 'cc',
  17: 'pcd',
};

// Walk a stage_timeline JSON tree and collect every ISO date stored under a
// key ending in "Date" (contractDate, targetDate, actualDate, paidDate, cc
// startDate/stopDate, part-stage dates, …). Non-date values under matching
// keys (e.g. emailStatus.invoiceDate = 'sent') are filtered by the ISO check.
function collectTimelineDates(node, out = []) {
  if (!node || typeof node !== 'object') return out;
  if (Array.isArray(node)) { node.forEach(n => collectTimelineDates(n, out)); return out; }
  Object.entries(node).forEach(([k, v]) => {
    if (typeof v === 'string' && /date$/i.test(k) && /^\d{4}-\d{2}-\d{2}/.test(v)) out.push(v.slice(0, 10));
    else if (v && typeof v === 'object') collectTimelineDates(v, out);
  });
  return out;
}

function factsDate(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Derive the stage the project is currently in: order the stages like
// TimelinePage, keep the ones that are part of this project (fee allocation
// or timeline data), and pick the first that isn't completed/invoiced yet.
function deriveCurrentStage(stageTimeline = {}, budgetAllocation = {}, tasks = []) {
  const source = (tasks && tasks.length)
    ? tasks
    : Object.entries(FACTS_STAGE_ID_TO_KEY).map(([tid, key]) => ({ id: Number(tid), name: key.replace(/_/g, ' ').toUpperCase() }));
  const ordered = [...source]
    .sort((a, b) => {
      const oa = a.order != null ? a.order : (a.id <= 16 ? a.id : 1000 + a.id);
      const ob = b.order != null ? b.order : (b.id <= 16 ? b.id : 1000 + b.id);
      return oa - ob;
    })
    .map(t => ({ key: FACTS_STAGE_ID_TO_KEY[t.id] || `custom_${t.id}`, name: t.name }));

  const hasAnyDate = tl => !!(tl && (tl.contractDate || tl.targetDate || tl.actualDate || tl.proformaInvoiceDate || tl.clientApprovalDate || tl.invoiceDate || tl.paidDate));
  const isDone = tl => !!(tl && (tl.actualDate || tl.clientApprovalDate || tl.invoiceDate || tl.paidDate));

  const relevant = ordered.filter(s => {
    if (s.key === 'cc') return false; // retainer stage, handled separately below
    const tl = stageTimeline[s.key];
    const alloc = Number(budgetAllocation?.[s.key]) || 0;
    const pct = Number(tl?.invoicePercent ?? tl?.billing?.invoicePercent) || 0;
    return alloc > 0 || pct > 0 || hasAnyDate(tl);
  });

  const current = relevant.find(s => !isDone(stageTimeline[s.key]));
  if (current) {
    const tl = stageTimeline[current.key] || {};
    return { label: current.name, detail: (tl.targetDate || tl.contractDate) ? 'in progress' : 'up next' };
  }

  // All design stages closed — surface the CC retainer lifecycle if running
  const cc = stageTimeline.cc;
  if (cc?.startDate) {
    const today = new Date().toISOString().slice(0, 10);
    if (!(cc.stopDate && cc.stopDate <= today)) {
      const openPause = (cc.pausedPeriods || []).find(p => p.from && !p.to);
      return { label: 'CC', detail: openPause ? 'paused' : 'active' };
    }
  }
  if (relevant.length > 0) return { label: 'All stages completed', detail: null };
  return null;
}

// A person is auto-listed on a project's design team when they have logged
// enough sustained work on it: BOTH thresholds must hold. Curated exceptions
// (a lead below the hours floor, a recent joiner short of 6 months, …) are
// handled by the manual add/remove overrides stored in comms.projects.design_team.
const DESIGN_TEAM_MIN_HOURS = 200;   // total hours logged on the project
const DESIGN_TEAM_MIN_MONTHS = 6;    // distinct calendar months with logged time

// Area concepts carried by the QS (BOQ) app, at both project and zone level.
// Column order mirrors the QS app's summary (built-up, carpet, construction).
const FACTS_AREA_TYPES = [
  { key: 'built_up_area', label: 'Built-up' },
  { key: 'carpet_area', label: 'Carpet' },
  { key: 'construction_area', label: 'Construction' },
];

// Seniority ranking for ordering the design team, following the practice's role
// framework (Partner/Director at the top down to Intern). job_level is an
// unreliable sort key on its own — level 0 is a catch-all holding both Directors
// and Interns — so title drives the order, with job_level only as a tiebreaker.
const DESIGN_TEAM_SENIORITY = {
  'partner': 100,
  'director': 95,
  'business associate': 85,
  'associate': 80,
  'principal': 90,
  'senior architect': 70,
  'project architect': 60,
  'architect': 55,
  'medior architect': 50,
  'junior architect': 40,
  'model maker': 25,
  'visualiser': 25,
  'visualizer': 25,
  'support': 20,
  'others': 15,
  'other': 15,
  'intern': 10,
};
const DESIGN_TEAM_SENIORITY_DEFAULT = 30; // unknown/unset role: neutral, above support, below junior

function seniorityRank(jobRole) {
  if (!jobRole) return DESIGN_TEAM_SENIORITY_DEFAULT;
  // Normalise dirty values like "Project Architect (6)" → "project architect".
  const key = String(jobRole).toLowerCase().replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  return DESIGN_TEAM_SENIORITY[key] ?? DESIGN_TEAM_SENIORITY_DEFAULT;
}

function normalizeDesignTeam(dt) {
  const src = dt && typeof dt === 'object' ? dt : {};
  const toIds = arr => (Array.isArray(arr) ? arr.map(Number).filter(n => Number.isFinite(n)) : []);
  return { added: toIds(src.added), removed: toIds(src.removed) };
}

function factsMonthYear(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function factsArea(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : '—';
}

// Every register firm, Media included (a photographer or videographer is
// credited here too). Paged, because the register runs past PostgREST's 1000
// row cap and a silent cut would drop the alphabetical tail from the picker.
async function fetchCollaboratorVendors() {
  const PAGE = 1000;
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from('vendors')
      .select('id, company_name, category, specialism')
      .order('company_name')
      .order('id')
      .range(from, from + PAGE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return rows;
}

// Type to search picker for one specialism's firms. The register holds over a
// thousand firms, too many for a plain dropdown, so the list narrows as you
// type and, before anything is typed, offers the firms that carry this
// specialism. Closed it looks like the design team's "+" picker.
function VendorSearchPicker({ vendors, discipline, empty, onPick, registerLink }) {
  const mono = "'IBM Plex Mono', monospace";
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const results = open ? searchVendors(vendors, query, discipline) : [];
  const close = () => { setOpen(false); setQuery(''); setActive(0); };
  const pick = (v) => { if (v) { onPick(Number(v.id)); close(); } };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title={`Add a ${discipline} collaborator`}
        style={{ fontFamily: mono, fontSize: 12, color: '#9A988F', background: 'transparent', border: '1px solid transparent', borderRadius: 2, padding: '1px 5px', cursor: 'pointer', flexShrink: 0 }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#D0CEC8'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
      >
        {empty ? 'Add firm…' : '+'}
      </button>
    );
  }
  return (
    // The list flows in the row rather than floating: the facts card clips its
    // overflow, which would cut a floating list off on the lower rows.
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
      <input
        autoFocus
        value={query}
        onChange={e => { setQuery(e.target.value); setActive(0); }}
        onKeyDown={e => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(results[active]); }
          else if (e.key === 'Escape') close();
        }}
        onBlur={close}
        placeholder="Type a firm name"
        style={{ fontFamily: mono, fontSize: 12, color: '#1B1B19', background: 'transparent', border: '1px solid #D0CEC8', borderRadius: 2, padding: '1px 5px', width: 180 }}
      />
      <div style={{ background: '#fff', border: '1px solid #D0CEC8', borderRadius: 3, width: 280, maxWidth: '100%', padding: '3px 0' }}>
        {results.map((v, i) => (
          <button
            key={v.id}
            // mousedown, not click: the input's blur would close the list first.
            onMouseDown={e => { e.preventDefault(); pick(v); }}
            onMouseEnter={() => setActive(i)}
            style={{ display: 'block', width: '100%', textAlign: 'left', background: i === active ? '#F1EFEA' : 'transparent', border: 'none', cursor: 'pointer', padding: '4px 10px' }}
          >
            <div style={{ fontSize: 13, color: '#1B1B19' }}>{v.company_name}</div>
            {v.specialism && <div style={{ fontFamily: mono, fontSize: 10, color: '#9A988F' }}>{v.specialism}</div>}
          </button>
        ))}
        {results.length === 0 && (
          <div style={{ padding: '4px 10px', fontFamily: mono, fontSize: 11, color: '#9A988F' }}>
            {query.trim() ? 'No firm by that name.' : `Type to search ${vendors.length.toLocaleString()} firms.`}
            {query.trim() && registerLink && (
              // mousedown for the same reason as the rows above.
              <span onMouseDown={e => e.preventDefault()} style={{ display: 'block', marginTop: 3 }}>{registerLink}</span>
            )}
          </div>
        )}
      </div>
    </span>
  );
}

function ProjectFactsDrawer({ projectCode, canEdit = false, showToast, onOpenVendorRegister }) {
  const [open, setOpen] = useState(() => { try { return localStorage.getItem('comms_facts_open') !== '0'; } catch { return true; } });
  const [facts, setFacts] = useState(null); // { project, client, tasks, contributors, users, commsExists, area }
  const [designTeam, setDesignTeam] = useState({ added: [], removed: [] }); // manual overrides
  // Collaborators are rows of public.vendor_project_links, the same table the
  // Contacts register's BRIO Project column writes, so a firm credited in
  // either place shows in both. The rows carry the project id they were read
  // for, and a write's response is applied only while that is still the
  // project on screen: link ids and vendor ids are global, so a slow reply for
  // the previous project would otherwise land on this one.
  const [links, setLinks] = useState({ projectId: null, rows: [] });
  const [pendingSpecialisms, setPendingSpecialisms] = useState([]); // opened, no firm picked yet
  const [specialismOptions, setSpecialismOptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const linksProjectRef = useRef(null);
  // Consultant and contractor drawers start closed; photographers stay open.
  const [collabOpen, setCollabOpen] = useState({ consultants: false, trade: false });
  const [areaOpen, setAreaOpen] = useState(false);

  useEffect(() => {
    if (!projectCode) { setFacts(null); return; }
    let cancelled = false;
    setAreaOpen(false);
    setDesignTeam({ added: [], removed: [] });
    setLinks({ projectId: null, rows: [] });
    linksProjectRef.current = null;
    setPendingSpecialisms([]);
    (async () => {
      try {
        const { data: proj } = await supabase
          .from('projects')
          .select('id, name, project_code, client_id, category, status, project_size, plot_area, project_address, city, country, stage_timeline, budget_allocation, elevation_m, climate_zone, site_types, climate_normals, subsurface, subsurface_override')
          .eq('project_code', projectCode)
          .is('deleted_at', null)
          .maybeSingle();
        if (cancelled) return;
        if (!proj) { setFacts(null); return; }
        const boqSchema = supabase.schema('boq');
        const [clientRes, tasksRes, teamRes, usersRes, commsRes, boqProjRes, linksRes, vendorRows, specialismRes] = await Promise.all([
          proj.client_id
            ? supabase.from('clients').select('id, name, organisation').eq('id', proj.client_id).maybeSingle()
            : Promise.resolve({ data: null }),
          supabase.from('tasks').select('*').order('id'),
          // Per-contributor hours + active months (admin/supervisor only server-side).
          supabase.rpc('get_project_design_team', { p_project_id: proj.id }),
          supabase.from('users').select('id, name, job_role, job_level, is_active, status').is('deleted_at', null).order('name'),
          comms.from('projects').select('comms_id, design_team').eq('project_code', projectCode).maybeSingle(),
          // QS (BOQ) area totals, linked by project_code (no FK between registries).
          boqSchema.from('projects').select('id, carpet_area, built_up_area, construction_area').eq('project_code', projectCode).is('deleted_at', null).maybeSingle(),
          supabase.from('vendor_project_links').select('id, vendor_id, specialism').eq('project_id', proj.id).order('id'),
          fetchCollaboratorVendors().catch(() => []),
          supabase.from('specialism_options').select('label, category'),
        ]);
        if (cancelled) return;
        const boqProj = boqProjRes?.data || null;
        let zones = [];
        if (boqProj?.id) {
          const { data: zoneRows } = await boqSchema
            .from('project_zones')
            .select('id, parent_id, code, label, zone_type, sort_order, carpet_area, built_up_area, construction_area')
            .eq('project_id', boqProj.id)
            .order('sort_order', { ascending: true })
            .order('code', { ascending: true });
          if (cancelled) return;
          zones = zoneRows || [];
        }
        setFacts({
          project: proj,
          client: clientRes.data || null,
          tasks: tasksRes.data || [],
          contributors: Array.isArray(teamRes?.data) ? teamRes.data : [],
          users: Array.isArray(usersRes?.data) ? usersRes.data : [],
          commsExists: !!commsRes?.data,
          area: boqProj ? { project: boqProj, zones } : null,
        });
        setDesignTeam(normalizeDesignTeam(commsRes?.data?.design_team));
        linksProjectRef.current = Number(proj.id);
        setLinks({ projectId: Number(proj.id), rows: linksRes?.data || [] });
        setVendors(vendorRows || []);
        setSpecialismOptions(sortSpecialismOptions(specialismRes?.data || []));
      } catch {
        if (!cancelled) setFacts(null);
      }
    })();
    return () => { cancelled = true; };
  }, [projectCode]);

  const toggle = () => {
    setOpen(o => {
      try { localStorage.setItem('comms_facts_open', o ? '0' : '1'); } catch {}
      return !o;
    });
  };

  // Persist the manual design-team overrides to comms.projects, mirroring the
  // upsert-vs-update guard used by ProjectDetail (avoid clobbering enrichment
  // when a comms row already exists). Optimistic, with revert on failure.
  const writeCommsProject = async (patch) => {
    if (facts?.commsExists) {
      await commsUpdate('projects', 'project_code', projectCode, patch);
    } else {
      await comms.from('projects').upsert(
        { comms_id: projectCode, project_code: projectCode, name: facts?.project?.name || projectCode, nts: {}, kit: {}, coverage: [], awards_list: [], ...patch },
        { onConflict: 'project_code' }
      );
      setFacts(f => (f ? { ...f, commsExists: true } : f));
    }
  };

  const saveDesignTeam = async (next) => {
    const prev = designTeam;
    setDesignTeam(next);
    try {
      await writeCommsProject({ design_team: next });
    } catch (e) {
      setDesignTeam(prev);
      showToast?.(e?.message || 'Could not save the design team', 'error');
    }
  };

  // Credit a firm under a specialism on this project. Stores no specialism
  // when it is the firm's own first one, so the link keeps following the
  // register if that changes.
  const addFirm = async (vendor, specialism) => {
    const projectId = Number(facts?.project?.id);
    if (!projectId) return;
    try {
      await requireSession();
      const { data, error } = await supabase
        .from('vendor_project_links')
        .insert({ vendor_id: Number(vendor.id), project_id: projectId, specialism: specialismToStore(vendor, specialism) })
        .select('id, vendor_id, specialism')
        .single();
      if (error) throw error;
      if (linksProjectRef.current !== projectId) return;
      setLinks(l => (l.projectId === projectId ? { ...l, rows: [...l.rows, data] } : l));
    } catch (e) {
      showToast?.(e?.code === '23505' ? `${vendor.company_name} is already credited for ${specialism}` : (e?.message || 'Could not add the collaborator'), 'error');
    }
  };

  // Removes this one credit. The firm stays on the project under any other
  // specialism it is credited for, and on the register if it still has one.
  const removeLink = async (link, specialism) => {
    const projectId = links.projectId;
    const prev = links;
    const rest = links.rows.filter(r => r.id !== link.id);
    // Keep the row open once its last firm goes, so a swap does not make it jump.
    setPendingSpecialisms(p => (p.some(x => x.toLowerCase() === specialism.toLowerCase()) ? p : [...p, specialism]));
    setLinks({ projectId, rows: rest });
    try {
      await requireSession();
      const { data, error } = await supabase.from('vendor_project_links').delete().eq('id', link.id).select('id');
      if (error) throw error;
      // A delete that RLS filters out matches nothing rather than raising.
      if (!data?.length) throw new Error('You do not have permission to remove collaborators.');
    } catch (e) {
      if (linksProjectRef.current === projectId) setLinks(prev);
      showToast?.(e?.message || 'Could not remove the collaborator', 'error');
    }
  };

  if (!facts?.project) return null;
  const { project, client, tasks } = facts;

  const clientName = client?.organisation || client?.name || '—';
  const address = (project.project_address || '').trim();
  const sizeLabel = project.project_size ? `${Number(project.project_size).toLocaleString()} m²` : '—';
  const plotAreaLabel = project.plot_area ? `${Number(project.plot_area).toLocaleString()} m²` : '—';
  const elevationLabel = project.elevation_m != null ? `${Math.round(Number(project.elevation_m))} m` : '—';
  const climateLabel = project.climate_zone ? koppenLabel(project.climate_zone) : '—';
  const siteTypesLabel = Array.isArray(project.site_types) && project.site_types.length
    ? project.site_types.map(siteTypeLabel).join(', ')
    : '—';
  // Regional subsurface context (see utils/subsurface.js). Both rows are hidden
  // until a site has been enriched, and both carry the "regional, not site data"
  // caveat on hover — these facts feed press copy, where stating the geology
  // under one plot as fact would be wrong.
  const sub = (project.subsurface && typeof project.subsurface === 'object') ? project.subsurface : null;
  // A hand-set reading (projects.subsurface_override) beats the mapped one here
  // too — it is the firm's own site knowledge, and press copy should use it.
  const subOverride = (project.subsurface_override && typeof project.subsurface_override === 'object')
    ? project.subsurface_override : null;
  const soilRes = resolveSoil(sub, subOverride);
  const bedrockRes = resolveBedrock(sub, subOverride);
  const soilFactLabel = soilRes.manual ? soilRes.text : (() => {
    const soil = sub?.soil;
    if (!soil) return null;
    const parts = [];
    if (soil.surface?.texture) parts.push(`${soil.surface.texture} (0–5 cm)`);
    if (soil.deep?.texture) parts.push(`${soil.deep.texture} (1–2 m)`);
    return parts.length ? parts.join(' · ') : null;
  })();
  const bedrockFactLabel = bedrockRes.text;
  // The bedrock reading in full, as visible lines rather than a hover title:
  // these facts are read on tablets, where a `title` never fires, and the
  // headline alone ("Basalt · Phanerozoic") hides what the map actually said.
  const bedrockDetail = (() => {
    const b = sub?.bedrock;
    const mapped = resolveBedrock(sub, null).text;
    const age = Array.isArray(b?.ageRangeMa) && b.ageRangeMa.length === 2
      ? `${b.ageRangeMa[0]}–${b.ageRangeMa[1]} Ma`
      : null;
    return [
      bedrockRes.manual
        ? `Set by hand${mapped ? ` — mapped reading: ${mapped}` : ' — no mapped reading for this site'}`
        : null,
      b?.unit,
      age,
      bedrockSourceTerms(sub),
      b?.description,
      b ? `${b.source || 'macrostrat'} · mapped at regional scale — not a borehole log.` : null,
    ].filter(Boolean);
  })();
  const subsurfaceHint = 'Regional context from a 250 m soil grid and a mapped geological unit — describes the area, not this plot. Not a site investigation.';
  const manualHint = 'Set by hand on Projects Overview — the firm\'s own site knowledge, not the mapped dataset.';
  const soilHint = [soilRes.manual ? manualHint : null, subsurfaceHint].filter(Boolean).join('\n');
  const bedrockHint = [
    bedrockRes.manual ? manualHint : null,
    bedrockSourceTerms(sub),
    subsurfaceHint,
  ].filter(Boolean).join('\n');
  const cn = (project.climate_normals && typeof project.climate_normals === 'object') ? project.climate_normals : null;
  const seasonTempLabel = (dayC, nightC) => `${dayC != null ? Math.round(dayC) : '–'}° / ${nightC != null ? Math.round(nightC) : '–'}° day/night`;
  const winterTempLabel = cn ? seasonTempLabel(cn.winterDayC, cn.winterNightC) : '—';
  const summerTempLabel = cn ? seasonTempLabel(cn.summerDayC, cn.summerNightC) : '—';
  const precipLabel = cn && (cn.winterPrecipMm != null || cn.summerPrecipMm != null)
    ? `${cn.winterPrecipMm ?? '–'} mm winter · ${cn.summerPrecipMm ?? '–'} mm summer`
    : '—';

  const allDates = collectTimelineDates(project.stage_timeline).sort();
  const startLabel = factsDate(allDates[0]);
  const endLabel = factsDate(allDates[allDates.length - 1]);
  const datesLabel = startLabel ? `${startLabel} – ${endLabel || '…'}` : '—';

  const status = (project.status || 'active').toLowerCase();
  let statusLabel, statusCls;
  if (status === 'completed') { statusLabel = 'Completed'; statusCls = 'pub'; }
  else if (status === 'paused') { statusLabel = 'Paused'; statusCls = 'pot'; }
  else if (status === 'archived') { statusLabel = 'Archived'; statusCls = 'new'; }
  else {
    const stage = deriveCurrentStage(project.stage_timeline || {}, project.budget_allocation || {}, tasks);
    statusLabel = stage ? (stage.detail ? `${stage.label} · ${stage.detail}` : stage.label) : 'Active';
    statusCls = 'pipe';
  }

  const mono = "'IBM Plex Mono', monospace";
  // Wide enough that most labels ("Precipitation", "Landscape architecture")
  // stay on one line. The area statement below indents by the same amount.
  const FACT_LABEL_WIDTH = 150;
  const FACT_ROW_GAP = 12;
  const labelStyle = { fontFamily: mono, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6E6C66', width: FACT_LABEL_WIDTH, flexShrink: 0 };
  const valueStyle = { fontSize: 13, color: '#1B1B19', lineHeight: 1.45, minWidth: 0, overflowWrap: 'anywhere' };
  const factRow = (label, value) => (
    <div style={{ display: 'flex', gap: FACT_ROW_GAP, alignItems: 'baseline' }}>
      <span style={labelStyle}>{label}</span>
      <span style={{ ...valueStyle, flex: '1 1 auto' }}>{value}</span>
    </div>
  );

  // ── Design team: auto members (from logged time) + manual overrides ───────
  const contributors = (facts.contributors || []).map(c => ({
    userId: Number(c.user_id),
    hours: Number(c.total_hours) || 0,
    months: Number(c.active_months) || 0,
    first: c.first_date,
    last: c.last_date,
  }));
  const contribById = new Map(contributors.map(c => [c.userId, c]));
  const usersById = new Map((facts.users || []).map(u => [Number(u.id), u]));
  const autoIds = contributors
    .filter(c => c.hours >= DESIGN_TEAM_MIN_HOURS && c.months >= DESIGN_TEAM_MIN_MONTHS)
    .map(c => c.userId);
  const autoSet = new Set(autoIds);
  const removedSet = new Set(designTeam.removed || []);
  const memberIds = [...new Set([...autoIds, ...(designTeam.added || [])])].filter(id => !removedSet.has(id));
  const memberSet = new Set(memberIds);
  const teamMembers = memberIds
    .map(id => {
      const u = usersById.get(id);
      if (!u) return null; // skip ids that no longer resolve to a (non-deleted) user
      const c = contribById.get(id);
      return {
        userId: id,
        name: u.name || `#${id}`,
        role: u.job_role || null,
        seniority: seniorityRank(u.job_role),
        jobLevel: Number(u.job_level) || 0,
        auto: autoSet.has(id),
        hours: c ? c.hours : null,
        months: c ? c.months : null,
        first: c ? c.first : null,
        last: c ? c.last : null,
      };
    })
    .filter(Boolean)
    // Order by seniority (most senior first), then finer job level, then
    // contribution on this project, then name.
    .sort((a, b) =>
      b.seniority - a.seniority ||
      b.jobLevel - a.jobLevel ||
      (b.hours ?? -1) - (a.hours ?? -1) ||
      a.name.localeCompare(b.name)
    );

  // Manual picker pool: active staff plus any resolved contributor (so a former
  // colleague or a near-miss on the threshold can still be credited), minus
  // whoever is already shown.
  const candMap = new Map();
  (facts.users || []).forEach(u => { if (u.is_active !== false && u.status !== 'inactive') candMap.set(Number(u.id), u.name); });
  contributors.forEach(c => { const u = usersById.get(c.userId); if (u) candMap.set(c.userId, u.name); });
  const teamOptions = [...candMap.entries()]
    .map(([id, name]) => ({ id, name, hours: contribById.get(id)?.hours ?? null }))
    .filter(o => !memberSet.has(o.id))
    .sort((a, b) => (b.hours ?? -1) - (a.hours ?? -1) || a.name.localeCompare(b.name));

  const addMember = (id) => {
    const nextRemoved = (designTeam.removed || []).filter(x => x !== id);
    const nextAdded = (autoSet.has(id) || (designTeam.added || []).includes(id))
      ? (designTeam.added || [])
      : [...(designTeam.added || []), id];
    saveDesignTeam({ added: nextAdded, removed: nextRemoved });
  };
  const removeMember = (id) => {
    const nextAdded = (designTeam.added || []).filter(x => x !== id);
    const nextRemoved = (autoSet.has(id) && !(designTeam.removed || []).includes(id))
      ? [...(designTeam.removed || []), id]
      : (designTeam.removed || []);
    saveDesignTeam({ added: nextAdded, removed: nextRemoved });
  };
  const memberTitle = (m) => {
    const rolePrefix = m.role ? `${m.role} · ` : '';
    if (m.hours == null) return `${rolePrefix}added manually`;
    const span = m.first ? ` · ${factsMonthYear(m.first)}–${factsMonthYear(m.last)}` : '';
    return `${rolePrefix}${Math.round(m.hours).toLocaleString()} h · ${m.months} mo${span}${m.auto ? '' : ' · added'}`;
  };

  const designTeamValue = (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 5, rowGap: 2, alignItems: 'baseline' }}>
      {teamMembers.map((m, i) => (
        <span key={m.userId} title={memberTitle(m)} style={{ whiteSpace: 'nowrap' }}>
          <span>{m.name}</span>
          {canEdit && (
            <button onClick={() => removeMember(m.userId)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 0, margin: '0 0 0 4px', lineHeight: 1, fontSize: 12 }} title="Remove from design team">×</button>
          )}
          {i < teamMembers.length - 1 && <span style={{ color: '#6E6C66' }}>,</span>}
        </span>
      ))}
      {teamMembers.length === 0 && !canEdit && <span style={{ color: '#9A988F' }}>—</span>}
      {canEdit && teamOptions.length > 0 && (
        <select
          value=""
          onChange={e => { if (e.target.value) addMember(Number(e.target.value)); }}
          style={{ fontFamily: mono, fontSize: 12, color: '#9A988F', background: 'transparent', border: '1px solid transparent', borderRadius: 2, padding: '1px 5px', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', width: teamMembers.length === 0 ? 130 : 24, flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#D0CEC8'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; }}
          title="Add a design team member"
        >
          <option value="">{teamMembers.length === 0 ? 'Add member…' : '+'}</option>
          {teamOptions.map(o => (
            <option key={o.id} value={o.id}>{o.name}{o.hours != null ? ` · ${Math.round(o.hours)} h` : ''}</option>
          ))}
        </select>
      )}
    </span>
  );

  // ── Collaborators: register firms per specialism (vendor_project_links) ──
  // Three places on the sheet: photographers as plain rows under the design
  // team, consultants in one drawer, contractors / vendors / suppliers in
  // another. The press (magazines, books, writers, ...) is left out; its
  // register links are untouched, it is just not credited here.
  const vendorsById = new Map(vendors.map(v => [Number(v.id), v]));
  const linkGroup = l => {
    const v = vendorsById.get(Number(l.vendor_id));
    return collaboratorGroup(effectiveSpecialism(l, v), v, specialismOptions);
  };
  const linkRows = links.rows.filter(l => vendorsById.has(Number(l.vendor_id))); // a firm deleted from the register stops rendering
  const linksByGroup = { photo: [], consultants: [], trade: [] };
  linkRows.forEach(l => { const g = linkGroup(l); if (g) linksByGroup[g].push(l); });
  const optionsByGroup = { photo: [], consultants: [], trade: [] };
  specialismOptions.forEach(o => { const g = collaboratorGroup(o.label, null, specialismOptions); if (g) optionsByGroup[g].push(o); });
  const pendingIn = g => pendingSpecialisms.filter(sp => collaboratorGroup(sp, null, specialismOptions) === g);
  // Photographers keep their rows on screen for an editor even when empty,
  // since there is no drawer to add them from.
  const photoPending = canEdit ? [...new Set([...PHOTO_SPECIALISMS, ...pendingIn('photo')])] : pendingIn('photo');
  const rowsFor = g => specialismRows(linksByGroup[g], vendorsById, g === 'photo' ? photoPending : pendingIn(g), specialismOptions);
  const vendorsFor = g => vendors.filter(v => vendorInGroup(v, g));

  const pickerStyle = { fontFamily: mono, fontSize: 12, color: '#9A988F', background: 'transparent', border: '1px solid transparent', borderRadius: 2, padding: '1px 5px', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none', flexShrink: 0 };
  const pickerHover = {
    onMouseEnter: e => { e.currentTarget.style.borderColor = '#D0CEC8'; },
    onMouseLeave: e => { e.currentTarget.style.borderColor = 'transparent'; },
  };
  // Firms and their specialisms are maintained on the vendor register, never
  // here; this is the way there. Absent when the user has no access to it.
  const registerLink = onOpenVendorRegister ? (
    <button
      onClick={onOpenVendorRegister}
      title="Add firms or specialisms on the vendor register"
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: mono, fontSize: 11, color: GOLD, display: 'inline-flex', alignItems: 'center', gap: 3 }}
    >
      Vendor register <ExternalLink size={10} />
    </button>
  ) : null;

  const collaboratorValue = (group, specialism) => {
    const unset = specialism === NO_SPECIALISM;
    const members = linksByGroup[group]
      .filter(l => effectiveSpecialism(l, vendorsById.get(Number(l.vendor_id))).toLowerCase() === specialism.toLowerCase())
      .map(l => ({ link: l, vendor: vendorsById.get(Number(l.vendor_id)) }));
    const memberIds = new Set(members.map(m => Number(m.vendor.id)));
    const options = vendorsFor(group).filter(v => !memberIds.has(Number(v.id)));
    // The two built in photo rows stay put for an editor; any other empty
    // row was opened by hand and can be closed again.
    const closable = members.length === 0 && !(group === 'photo' && PHOTO_SPECIALISMS.some(p => p.toLowerCase() === specialism.toLowerCase()));
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 5, rowGap: 2, alignItems: 'baseline' }}>
        {members.map((m, i) => (
          <span key={m.link.id} title={[m.vendor.specialism, m.vendor.category].filter(Boolean).join(' · ')} style={{ whiteSpace: 'nowrap' }}>
            <span>{m.vendor.company_name}</span>
            {canEdit && (
              <button onClick={() => removeLink(m.link, specialism)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 0, margin: '0 0 0 4px', lineHeight: 1, fontSize: 12 }} title={`Remove from ${specialism}`}>×</button>
            )}
            {i < members.length - 1 && <span style={{ color: '#6E6C66' }}>,</span>}
          </span>
        ))}
        {members.length === 0 && !canEdit && <span style={{ color: '#9A988F' }}>—</span>}
        {/* A firm with no specialism is filed by setting one on the register,
            so this row takes no new firms. */}
        {unset && members.length > 0 && (
          <span style={{ fontFamily: mono, fontSize: 10, color: '#9A988F' }}>set their specialism on the register</span>
        )}
        {canEdit && !unset && options.length > 0 && (
          <VendorSearchPicker
            vendors={options}
            discipline={specialism}
            empty={members.length === 0}
            onPick={id => { const v = vendorsById.get(Number(id)); if (v) addFirm(v, specialism); }}
            registerLink={registerLink}
          />
        )}
        {canEdit && closable && (
          <button onClick={() => setPendingSpecialisms(p => p.filter(d => d.toLowerCase() !== specialism.toLowerCase()))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 0, lineHeight: 1, fontSize: 12 }} title="Close this specialism">×</button>
        )}
      </span>
    );
  };

  const addSpecialismValue = (group) => {
    const used = new Set(rowsFor(group).map(r => r.toLowerCase()));
    const unused = optionsByGroup[group].filter(o => !used.has(o.label.toLowerCase()));
    const byCategory = unused.reduce((acc, o) => {
      (acc[o.category || 'Other'] = acc[o.category || 'Other'] || []).push(o.label);
      return acc;
    }, {});
    const grouped = Object.keys(byCategory).length > 1;
    return (
      <span style={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 10, rowGap: 2, alignItems: 'baseline' }}>
        {unused.length > 0 && (
          <select
            value=""
            onChange={e => { const v = e.target.value; if (v) setPendingSpecialisms(p => [...p, v]); }}
            style={{ ...pickerStyle, width: 140 }}
            {...pickerHover}
            title="Add a specialism"
          >
            <option value="">Add specialism…</option>
            {grouped
              ? Object.entries(byCategory).map(([cat, labels]) => (
                <optgroup key={cat} label={cat}>
                  {labels.map(l => <option key={l} value={l}>{l}</option>)}
                </optgroup>
              ))
              : unused.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
        )}
        {registerLink}
      </span>
    );
  };

  // A specialism row inside a drawer: the same row, label indented.
  const subRow = (label, value) => (
    <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
      <span style={{ ...labelStyle, paddingLeft: 12, boxSizing: 'border-box' }}>{label}</span>
      <span style={{ ...valueStyle, flex: '1 1 auto' }}>{value}</span>
    </div>
  );

  const DRAWER_CHEVRON = { position: 'absolute', left: -21, top: 0, color: '#6E6C66' };

  // Consultants and contractors: a heading row that opens and closes, showing
  // who is in it at a glance while closed.
  const collabDrawer = (group, title) => {
    const rows = rowsFor(group);
    const firmNames = [...new Set(linksByGroup[group].map(l => vendorsById.get(Number(l.vendor_id))?.company_name).filter(Boolean))];
    if (!canEdit && firmNames.length === 0) return null;
    const isOpen = collabOpen[group];
    const toggleDrawer = () => setCollabOpen(o => ({ ...o, [group]: !o[group] }));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
          <button
            onClick={toggleDrawer}
            aria-expanded={isOpen}
            style={{ ...labelStyle, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', position: 'relative' }}
          >
            {/* The chevron hangs in the margin, in the same column as the
                Project facts chevron above (16px card padding + 13px icon +
                8px gap = the 37px the fact rows start at), so the label
                itself lines up with every other label. */}
            {isOpen
              ? <ChevronDown size={13} style={DRAWER_CHEVRON} />
              : <ChevronRight size={13} style={DRAWER_CHEVRON} />}
            {title}
          </button>
          <span
            onClick={isOpen ? undefined : toggleDrawer}
            style={{ ...valueStyle, flex: '1 1 auto', color: firmNames.length ? '#6E6C66' : '#9A988F', cursor: isOpen ? 'default' : 'pointer', whiteSpace: isOpen ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {firmNames.length ? (isOpen ? '' : firmNames.join(', ')) : '—'}
          </span>
        </div>
        {isOpen && rows.map(sp => (
          <React.Fragment key={`${group}-${sp}`}>{subRow(sp, collaboratorValue(group, sp))}</React.Fragment>
        ))}
        {isOpen && canEdit && subRow('', addSpecialismValue(group))}
      </div>
    );
  };

  // ── QS area statement (from the BOQ app, keyed by project_code) ───────────
  const area = facts.area;
  const areaCols = area
    ? FACTS_AREA_TYPES.filter(t => (area.project && area.project[t.key] != null) || (area.zones || []).some(z => z[t.key] != null))
    : [];
  // Only list zones that actually carry an area value in a shown column, so a
  // project with unpriced/placeholder zones (only a project-level total) doesn't
  // render a column of blank rows.
  const areaZones = area ? (area.zones || []).filter(z => areaCols.some(c => z[c.key] != null)) : [];
  // Totals reconcile with the visible rows: when zones are listed, sum them (the
  // QS app's zone-view Σ); otherwise fall back to the project-level total.
  const areaTotal = {};
  areaCols.forEach(c => {
    if (areaZones.length) {
      const vals = areaZones.map(z => z[c.key]).filter(v => v != null);
      areaTotal[c.key] = vals.length ? vals.reduce((s, v) => s + Number(v), 0) : (area.project?.[c.key] ?? null);
    } else {
      areaTotal[c.key] = area.project?.[c.key] ?? null;
    }
  });
  const hasAreaStatement = areaCols.length > 0;

  return (
    <div style={{ ...S.card, padding: 0, marginBottom: 24, overflow: 'hidden' }}>
      <button
        onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 16px', textAlign: 'left' }}
      >
        {open ? <ChevronDown size={13} style={{ color: '#6E6C66', flexShrink: 0 }} /> : <ChevronRight size={13} style={{ color: '#6E6C66', flexShrink: 0 }} />}
        <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#21302A' }}>Project facts</span>
        {!open && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1, justifyContent: 'flex-end' }}>
            <span style={{ fontFamily: mono, fontSize: 11, color: '#6E6C66', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {[clientName !== '—' ? clientName : null, project.project_size ? sizeLabel : null].filter(Boolean).join(' · ')}
            </span>
            <Chip label={statusLabel} cls={statusCls} />
          </span>
        )}
      </button>
      {open && (
        <div style={{ display: 'flex', gap: 20, padding: '2px 16px 16px 37px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flex: '1 1 240px', minWidth: 0 }}>
            {factRow('Client', clientName)}
            {factRow('Address', address ? (
              <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 6 }}>
                <span>{address}</span>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                  target="_blank" rel="noreferrer" title="Open in Google Maps"
                  style={{ color: GOLD, display: 'inline-flex', alignSelf: 'center' }}
                >
                  <ExternalLink size={11} />
                </a>
              </span>
            ) : '—')}
            {/* The readable location. It becomes the PROJECT-LOCATION field of
                press-kit filenames, so a blank here is why a rename comes out
                without one — but it is registry data, edited on the projects
                overview, and read-only here like every other fact in this
                panel. The address above is a street or coordinates and cannot
                stand in for it. */}
            {factRow('Location', [project.city, project.country].filter(Boolean).join(', ') || '—')}
            {/* Project size + optional detailed area statement from the QS app */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                <span style={labelStyle}>Project size</span>
                <span style={{ ...valueStyle, display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>{sizeLabel}</span>
                  {hasAreaStatement && (
                    <button
                      onClick={() => setAreaOpen(o => !o)}
                      title="Detailed area statement from the QS app"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: GOLD, fontFamily: mono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                    >
                      {areaOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Area statement
                    </button>
                  )}
                </span>
              </div>
              {hasAreaStatement && areaOpen && (
                <div style={{ marginLeft: FACT_LABEL_WIDTH + FACT_ROW_GAP, overflowX: 'auto', maxWidth: '100%' }}>
                  <table style={{ borderCollapse: 'collapse', fontFamily: mono, fontSize: 11, color: '#1B1B19' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '2px 10px 4px 4px', color: '#6E6C66', fontWeight: 600, borderBottom: '1px solid rgba(27,27,25,.15)' }}>Zone</th>
                        {areaCols.map(c => (
                          <th key={c.key} style={{ textAlign: 'right', padding: '2px 4px 4px 10px', color: '#6E6C66', fontWeight: 600, borderBottom: '1px solid rgba(27,27,25,.15)', whiteSpace: 'nowrap' }}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {areaZones.map(z => (
                        <tr key={z.id}>
                          <td style={{ textAlign: 'left', padding: '2px 10px 2px 4px', paddingLeft: z.parent_id ? 16 : 4, whiteSpace: 'nowrap' }}>
                            <span style={{ color: '#33473F', fontWeight: 600 }}>{z.code}</span>{z.label ? <span style={{ color: '#6E6C66' }}> {z.label}</span> : null}
                          </td>
                          {areaCols.map(c => (
                            <td key={c.key} style={{ textAlign: 'right', padding: '2px 4px', whiteSpace: 'nowrap' }}>{factsArea(z[c.key])}</td>
                          ))}
                        </tr>
                      ))}
                      <tr>
                        <td style={{ textAlign: 'left', padding: '3px 10px 2px 4px', fontWeight: 700, borderTop: '1px solid rgba(27,27,25,.25)' }}>{areaZones.length ? 'Total' : 'Project'}</td>
                        {areaCols.map(c => (
                          <td key={c.key} style={{ textAlign: 'right', padding: '3px 4px 2px', fontWeight: 700, borderTop: '1px solid rgba(27,27,25,.25)', whiteSpace: 'nowrap' }}>{factsArea(areaTotal[c.key])}</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ fontFamily: mono, fontSize: 9, color: '#9A988F', marginTop: 4, letterSpacing: '0.06em', textTransform: 'uppercase' }}>m² · from QS app</div>
                </div>
              )}
            </div>
            {factRow('Plot area', plotAreaLabel)}
            {factRow('Elevation', elevationLabel)}
            {factRow('Climate', climateLabel)}
            {factRow('Site type', siteTypesLabel)}
            {cn && factRow('Winter temp', winterTempLabel)}
            {cn && factRow('Summer temp', summerTempLabel)}
            {cn && factRow('Precipitation', precipLabel)}
            {soilFactLabel && factRow('Soil', <span title={soilHint}>{soilFactLabel}</span>)}
            {bedrockFactLabel && factRow('Bedrock', (
              <span title={bedrockHint}>
                {bedrockFactLabel}
                {bedrockDetail.map((line, i) => (
                  <div key={i} style={{ fontSize: 10.5, color: '#9A988F', lineHeight: 1.45 }}>{line}</div>
                ))}
              </span>
            ))}
            {factRow('Dates', datesLabel)}
            {factRow('Status', <Chip label={statusLabel} cls={statusCls} />)}
            {(teamMembers.length > 0 || canEdit) && factRow('Design team', designTeamValue)}
            {rowsFor('photo').map(sp => (
              <React.Fragment key={`photo-${sp}`}>{factRow(sp, collaboratorValue('photo', sp))}</React.Fragment>
            ))}
            {collabDrawer('consultants', 'Consultants')}
            {collabDrawer('trade', 'Contractors & suppliers')}
          </div>
          {address && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
              target="_blank" rel="noreferrer" title="Open in Google Maps"
              style={{ display: 'flex', flexDirection: 'column', alignSelf: 'stretch', textDecoration: 'none', flex: '0 0 560px', maxWidth: '100%' }}
            >
              {/* Fills the height of the facts column beside it (the panel's flex
                  row stretches this anchor), with a floor for short fact lists. */}
              <div style={{ width: '100%', flex: '1 1 auto', minHeight: 150, borderRadius: 3, border: '1px solid rgba(27,27,25,.12)', overflow: 'hidden', position: 'relative', background: '#1a2e1a' }}>
                <FactsMap address={address} />
                <div style={{ position: 'absolute', bottom: 6, right: 8, background: 'rgba(0,0,0,0.65)', borderRadius: 3, padding: '2px 8px', fontFamily: mono, fontSize: 9, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#fff', pointerEvents: 'none' }}>
                  Open in Maps ↗
                </div>
              </div>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectDetail({ id, data, onBack, canEdit, canConfigure, refresh, showToast, currentUser, onOpenVendorRegister }) {
  const project = (data.projects || []).find(p => p.comms_id === id);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [showAll, setShowAll] = useState({});
  const [showPubText, setShowPubText] = useState(() => new Set());
  const [draft, setDraft] = useState('');
  const [newCoverage, setNewCoverage] = useState({ name: '', type: 'Publication', when: '', link: '' });
  const [newAward, setNewAward] = useState({ award_body: '', year: '' });
  const [newSocial, setNewSocial] = useState({ text: '', channel: '' });
  const [auditState, setAuditState] = useState({ loading: false, results: null });
  // Bumped when this page fixes something the webhook's alerts describe, so the
  // alerts panel re-reads rather than showing a finding that is already gone.
  const [alertsRev, setAlertsRev] = useState(0);
  // Key of the subfolder whose version folders are being created, so only that
  // row shows a spinner.
  const [fillingTemplate, setFillingTemplate] = useState(null);
  const [auditChecked, setAuditChecked] = useState({});
  const [kitSchema, setKitSchema] = useState(PRESS_KIT_DEFAULT_SCHEMA);
  const [approvedSubfolders, setApprovedSubfolders] = useState({});
  // Which version to show when a folder holds the same images at several sizes.
  // A viewing preference, so it is remembered per browser across projects.
  const [versionPref, setVersionPref] = useState(readVersionPref);
  const { sectionExpanded, sectionFiles, expandAll, loadSectionFiles, toggleSectionFiles, expandSections, toggleExpandAll, refreshFolders } = usePressFolderBrowser(versionPref);
  const { selectMode, selection, selectedList, toggleSelectMode, exitSelectMode } = useAssetSelection({ allowFiles: true });
  const [lightboxEntry, setLightboxEntry] = useState(null);
  const didAutoSync = useRef(new Set());
  const didAutoExpand = useRef(new Set());
  const [imgCols, setImgCols] = useState(() => { try { return Number(localStorage.getItem('comms_imgCols')) || 3; } catch { return 3; } });
  const [manageMode, setManageMode] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [themeAssignOpen, setThemeAssignOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  // Sharing. `shareAssets` is null while closed; a resend opens the same modal
  // with the files of an existing share rather than the current selection, so
  // "send the high-res of what I sent last week" needs no re-picking.
  const [shareAssets, setShareAssets] = useState(null);
  const [sharesRev, setSharesRev] = useState(0);
  const [tagsRev, setTagsRev] = useState(0);
  const tagsByAsset = useAssetTags(tagsRev);
  const [rankModalOpen, setRankModalOpen] = useState(false);
  const [ranksRev, setRanksRev] = useState(0);
  const { ranksById, applyRank } = useAssetRanks(ranksRev);
  // Shows only assets ranked SHORTLIST_MIN_RANK or above. A view over the
  // listing already loaded, so toggling it costs no Dropbox call.
  const [shortlistOnly, setShortlistOnly] = useState(false);
  // Renaming and sharing are open to every active employee, like the rest of
  // comms editing: press curation is done on the `user` role too. The edge
  // functions (rename-press-files, share-press-selection, purge-expired-shares)
  // refuse only a caller with no employee record or one marked inactive, and
  // an inactive account never reaches this page.
  const canRename = !!currentUser;
  const canShare = canRename;
  // Rank one asset — from the tile's right-click menu or the lightbox. The
  // star moves immediately and rolls back if the write fails, so rating a run
  // of images stays responsive. project_code is added here because it is the
  // only layer that knows which project the asset was browsed under.
  const projectCode = project?.project_code;
  const rateAsset = useCallback(async (asset, rank) => {
    if (!asset?.fileId) return;
    const previous = ranksById[asset.fileId] || 0;
    if (rank === previous) return;
    applyRank(asset.fileId, rank);
    try {
      await updateAssetRanks([{ ...asset, projectCode }], rank);
    } catch (e) {
      applyRank(asset.fileId, previous);
      showToast(e.message, 'error');
    }
  }, [ranksById, applyRank, projectCode, showToast]);
  const { openRankMenu, rankMenu } = useRankMenu({ ranksById, onRate: rateAsset });
  // Memoized: the lightbox binds a keyboard listener keyed on this object.
  const rankView = useMemo(
    () => ({ byId: ranksById, min: shortlistOnly ? SHORTLIST_MIN_RANK : 0, onRate: rateAsset, openRankMenu }),
    [ranksById, shortlistOnly, rateAsset, openRankMenu],
  );
  const kitLayoutKey = `comms_kit_layout_${id}`;
  const [sectionOrder, setSectionOrder] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(`comms_kit_layout_${id}`) || '{}'); return s.order?.length ? s.order : PRESS_KIT_DEFAULT_SCHEMA.map(s => s.key); } catch { return PRESS_KIT_DEFAULT_SCHEMA.map(s => s.key); }
  });
  const [hiddenSections, setHiddenSections] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(`comms_kit_layout_${id}`) || '{}'); return new Set(s.hidden || []); } catch { return new Set(); }
  });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  // Version history for this project's long-form text fields (byline, website
  // text, outreach). Keyed by project_code, which is also the comms_id here.
  const fieldVersions = useCommsFieldVersions('project', project?.project_code || id);

  useEffect(() => {
    supabase.from('app_settings').select('value').eq('key', 'pressKitFolderSchema').maybeSingle().then(({ data: row }) => {
      if (row?.value) {
        try {
          const v = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
          if (Array.isArray(v) && v.length) {
            setKitSchema(v);
            setSectionOrder(curr => {
              const missing = v.filter(s => !curr.includes(s.key)).map(s => s.key);
              return missing.length ? [...curr, ...missing] : curr;
            });
          }
        } catch {}
      }
    });
    supabase.from('app_settings').select('value').eq('key', 'pressKitApprovedSubfolders').maybeSingle().then(({ data: row }) => {
      if (row?.value) {
        try {
          const v = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
          if (v && typeof v === 'object') setApprovedSubfolders(v);
        } catch {}
      }
    });
  }, []);

  // Auto-fill missing Dropbox links on first open of each project
  useEffect(() => {
    if (!project || didAutoSync.current.has(id)) return;
    const currentKit = typeof project.kit === 'object' && project.kit !== null ? project.kit : {};
    if (kitSchema.every(s => currentKit[s.key]?.link)) return;
    didAutoSync.current.add(id);
    callEdgeFunction('sync-press-folder-links', {
      projects: [{ project_code: project.project_code, name: project.name }],
    }).then(() => refresh()).catch(() => {});
  }, [id, project]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expand the press-kit folders by default on first open of each project, so the
  // photos/thumbnails show without needing to hit "expand all". Cascading into
  // subfolders is handled inside usePressFolderBrowser while expand-all is on.
  useEffect(() => {
    if (!project) return;
    const kitLinks = typeof project.kit === 'object' && project.kit !== null ? project.kit : {};
    // Require at least one section to have a synced link as a proxy that Dropbox
    // is configured for this project, then expand ALL non-hidden schema sections.
    if (!kitSchema.some(s => kitLinks[s.key]?.link)) return;
    const linkedSections = kitSchema.filter(s => !hiddenSections.has(s.key));
    // Guard key includes schema fingerprint so the effect re-runs when kitSchema
    // loads custom sections from the DB (which happens after the initial render).
    const expandKey = `${id}:${kitSchema.map(s => s.key).join(',')}`;
    if (didAutoExpand.current.has(expandKey)) return;
    didAutoExpand.current.add(expandKey);
    const folderName = `${project.project_code}_${(project.name || '').replace(/\//g, '_')}`;
    expandSections(linkedSections.map(s => ({ key: s.key, path: `${BRIO_PRESS_BASE}/${folderName}/${s.folder}` })));
  }, [id, project, kitSchema]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!project) return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All projects" />
      <EmptyNote text="Project not found." />
    </div>
  );

  const nts = typeof project.nts === 'object' && project.nts !== null ? project.nts : {};
  const kit = typeof project.kit === 'object' && project.kit !== null ? project.kit : {};
  const coverage = Array.isArray(project.coverage) ? project.coverage : [];
  // Awards come from comms.awards, linked by project_codes (migration
  // 20260903000003). project.awards_list is the pre-link copy and is no longer read.
  const allAwards = data.awards || [];
  const projectAwards = allAwards.filter(a => Array.isArray(a.project_codes) && a.project_codes.includes(id));
  const linkableAwards = allAwards.filter(a => !(Array.isArray(a.project_codes) && a.project_codes.includes(id)));
  const socialAngles = Array.isArray(project.social) ? project.social : [];

  // Upsert into comms.projects by project_code (id = project_code for pub-sourced projects)
  const upsertCommsProject = async (fields) => {
    const projectCode = project.project_code || id;
    if (project._has_enrich) {
      await commsUpdate('projects', 'project_code', projectCode, fields);
    } else {
      await comms.from('projects').upsert({ comms_id: projectCode, project_code: projectCode, name: project.name, nts: {}, kit: {}, coverage: [], awards_list: [], ...fields }, { onConflict: 'project_code' });
    }
  };

  // Autosave of a long-form text field. Deliberately does NOT touch version
  // history — versions are only created by the + button beside the field.
  const saveField = async (field, value) => {
    if (value === (project[field] || '')) return;
    try { await upsertCommsProject({ [field]: value }); refresh(); showToast('Saved'); } catch (e) { showToast(e.message, 'error'); }
  };

  // Capture a field's current wording as a new version (the + button).
  const createFieldVersion = async (field, content) => {
    try {
      await fieldVersions.saveVersion({
        field,
        content,
        editedById: currentUser?.id ?? null,
        editedByName: currentUser?.name || '',
      });
      showToast('Version saved');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  // Returns false when the user backs out, so the preview panel stays open.
  const deleteFieldVersion = async (versionId, label) => {
    if (!window.confirm(`Delete version ${label}? The wording it holds will be lost.`)) return false;
    try {
      await fieldVersions.deleteVersion(versionId);
      showToast('Version deleted');
      return true;
    } catch (e) {
      showToast(e.message, 'error');
      return false;
    }
  };

  const renameFieldVersion = async (versionId, label) => {
    try {
      await fieldVersions.renameVersion(versionId, label);
      showToast((label || '').trim() ? 'Version renamed' : 'Version name cleared');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  // Start a new dated note in a category. The author is whoever is signed in —
  // no need to type a name.
  const addNoteWithText = async (cat, text, noteId = Date.now()) => {
    if (!text?.trim()) return false;
    const existing = Array.isArray(nts[cat]) ? nts[cat] : [];
    const entry = { id: noteId, text, by: currentUser?.name || 'BRIO', at: new Date().toISOString().split('T')[0] };
    const newNts = { ...nts, [cat]: [entry, ...existing] };
    try {
      await upsertCommsProject({ nts: newNts });
      refresh();
      showToast('Note added');
      return true;
    } catch (e) {
      showToast(e.message, 'error');
      return false;
    }
  };

  const addNote = async (cat) => {
    if (await addNoteWithText(cat, noteDrafts[cat])) setNoteDrafts(d => ({ ...d, [cat]: '' }));
  };

  // + on a category that has no note yet: create the note, then version it.
  const addNoteAndVersion = async (cat, text) => {
    const noteId = Date.now();
    if (await addNoteWithText(cat, text, noteId)) await createFieldVersion(noteFieldKey(cat, noteId), text);
  };

  // Notes live inside the `nts` JSON blob, so each entry keeps its own history
  // under nts.<category>.<note id>.
  const saveNote = async (cat, noteId, text) => {
    const existing = Array.isArray(nts[cat]) ? nts[cat] : [];
    if (text === (existing.find(n => n.id === noteId)?.text || '')) return;
    const newNts = { ...nts, [cat]: existing.map(n => n.id === noteId ? { ...n, text } : n) };
    try { await upsertCommsProject({ nts: newNts }); refresh(); showToast('Saved'); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteNote = async (cat, noteId) => {
    if (!window.confirm('Delete this note?')) return;
    const existing = Array.isArray(nts[cat]) ? nts[cat] : [];
    const newNts = { ...nts, [cat]: existing.filter(n => n.id !== noteId) };
    try { await upsertCommsProject({ nts: newNts }); refresh(); showToast('Note deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const saveKit = async (section, field, value) => {
    const newKit = { ...kit, [section]: { ...(kit[section] || {}), [field]: value } };
    try { await upsertCommsProject({ kit: newKit }); } catch (e) { showToast(e.message, 'error'); }
  };

  const projectFolderName = `${project.project_code}_${(project.name || '').replace(/\//g, '_')}`;
  const syncedAs = kit._syncedAs;
  const folderMismatch = syncedAs && syncedAs !== projectFolderName ? syncedAs : null;

  /**
   * "Rename in Dropbox" on a folder-name mismatch: one move that puts the
   * project folder back to {code}_{name}. Done server-side so the check that
   * nothing already sits under the new name is made against a fresh listing,
   * not the one on screen.
   */
  const renameProjectFolder = async () => {
    setAuditState(s => ({ ...s, loading: true }));
    try {
      const json = await callEdgeFunction('create-press-folders', {
        mode: 'rename-project-folder',
        target: { project_code: project.project_code, name: project.name },
      });
      const r = json.results?.[0];
      showToast(r?.renamed ? `Renamed to ${r.projectFolder}` : (json.message || 'Nothing to rename'));
      if (r?.renamed) {
        // The section links are id-based and survive the move, but the sync
        // re-reads them at the new path and re-stamps this card's record of
        // the folder name, which is what the banner above keys on.
        try {
          await callEdgeFunction('sync-press-folder-links', { projects: [{ project_code: project.project_code, name: project.name }] });
        } catch (e) { showToast(`Renamed, but the links did not re-sync: ${e.message}`, 'error'); }
        // Every cached listing under this project names the old path.
        clearListingCache();
        refresh();
      }
      // The webhook's "renamed" alert for this project is resolved by the same
      // call, so the panel above re-reads rather than keep showing it.
      setAlertsRev(v => v + 1);
      await runAudit();
    } catch (e) {
      showToast(e.message, 'error');
      setAuditState(s => ({ ...s, loading: false }));
    }
  };

  const runAudit = async () => {
    setAuditState({ loading: true, results: null });
    try {
      const json = await callEdgeFunction('audit-press-folders', {
        projects: [{ project_code: project.project_code, name: project.name }],
      });
      setAuditState({ loading: false, results: json.results?.[0] || null });
    } catch (e) {
      showToast(e.message, 'error');
      setAuditState({ loading: false, results: null });
    }
  };

  /**
   * Fills in the version folders a subfolder is missing, from the press-kit
   * card the user is already looking at.
   *
   * Scope is one subfolder, so the edge function does one listing and at most a
   * handful of creates — this is the cheap counterpart to a whole-project sync,
   * and the reason adding a photographer folder no longer means running one.
   */
  const fillSubfolderTemplate = async ({ sfKey, section, subName, sfPath, count }) => {
    setFillingTemplate(sfKey);
    try {
      const json = await callEdgeFunction('create-press-folders', {
        mode: 'template',
        target: {
          project_code: project.project_code,
          name: project.name,
          folder_name: projectFolderName,
          section,
          subfolder: subName,
        },
      });
      const created = json.results?.[0]?.created?.length ?? 0;
      showToast(created > 0
        ? `Added ${created} folder(s) inside ${subName}`
        : `${subName} already has its ${count} version folder(s)`);
      // The listing we judged from is now stale. Drop it and re-fetch directly:
      // toggleSectionFiles reads its state from the render closure, so calling
      // it twice in one tick would not reload anything.
      invalidateListing(sfPath);
      await loadSectionFiles(sfKey, sfPath);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setFillingTemplate(null);
    }
  };

  const createMissingFolders = async () => {
    try {
      // Asking for one named project is an explicit "fix this one", so it
      // re-reads Dropbox rather than trusting the recorded state.
      const json = await callEdgeFunction('create-press-folders', {
        projects: [{ project_code: project.project_code, name: project.name }],
        force: true,
      });
      const r = json.results?.[0];
      showToast(r ? `Created ${r.created.length} folder(s)` : 'Done');
      await runAudit();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const approveSelected = async () => {
    const newApproved = { ...approvedSubfolders };
    Object.entries(auditChecked).forEach(([key, checked]) => {
      if (!checked) return;
      const slashIdx = key.indexOf('/');
      const sectionFolder = key.slice(0, slashIdx);
      const name = key.slice(slashIdx + 1);
      if (!newApproved[sectionFolder]) newApproved[sectionFolder] = [];
      if (!newApproved[sectionFolder].includes(name)) newApproved[sectionFolder].push(name);
    });
    setApprovedSubfolders(newApproved);
    setAuditChecked({});
    try {
      await supabase.from('app_settings').upsert({ key: 'pressKitApprovedSubfolders', value: newApproved });
      showToast('Approved folders saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleExpandAll = () => {
    toggleExpandAll(kitSchema
      .filter(s => !hiddenSections.has(s.key))
      .map(s => ({ key: s.key, path: `${BRIO_PRESS_BASE}/${projectFolderName}/${s.folder}` })));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIdx = sectionOrder.indexOf(active.id);
      const newIdx = sectionOrder.indexOf(over.id);
      const newOrder = arrayMove(sectionOrder, oldIdx, newIdx);
      setSectionOrder(newOrder);
      try { localStorage.setItem(kitLayoutKey, JSON.stringify({ order: newOrder, hidden: [...hiddenSections] })); } catch {}
    }
  };

  const toggleHide = (key) => {
    const next = new Set(hiddenSections);
    if (next.has(key)) next.delete(key); else next.add(key);
    setHiddenSections(next);
    try { localStorage.setItem(kitLayoutKey, JSON.stringify({ order: sectionOrder, hidden: [...next] })); } catch {}
  };

  const addCoverage = async () => {
    if (!newCoverage.name) return;
    const newCov = [...coverage, { ...newCoverage, id: Date.now() }];
    try { await upsertCommsProject({ coverage: newCov }); refresh(); setNewCoverage({ name: '', type: 'Publication', when: '', link: '' }); showToast('Added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const updateCoverage = async (covId, fields) => {
    const newCov = coverage.map(c => c.id === covId ? { ...c, ...fields } : c);
    try { await upsertCommsProject({ coverage: newCov }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteCoverage = async (covId) => {
    if (!window.confirm('Delete this entry?')) return;
    const newCov = coverage.filter(c => c.id !== covId);
    try { await upsertCommsProject({ coverage: newCov }); refresh(); showToast('Deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const addAward = async () => {
    const name = newAward.award_body.trim();
    if (!name) return;
    const year = parseInt(newAward.year, 10);
    try {
      await commsInsert('awards', { comms_id: 'aw-' + Date.now(), award_body: name, year: Number.isFinite(year) ? year : null, project_codes: [id] });
      refresh(); setNewAward({ award_body: '', year: '' }); showToast('Award added');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const updateAward = async (commsId, fields) => {
    try { await commsUpdate('awards', 'comms_id', commsId, fields); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const linkAward = async (commsId) => {
    const award = allAwards.find(a => a.comms_id === commsId);
    if (!award) return;
    const codes = Array.isArray(award.project_codes) ? award.project_codes : [];
    await updateAward(commsId, { project_codes: [...codes, id] });
  };

  // Unlinking keeps the award on the practice list; only the link to this project goes.
  const unlinkAward = async (award) => {
    const codes = (Array.isArray(award.project_codes) ? award.project_codes : []).filter(c => c !== id);
    await updateAward(award.comms_id, { project_codes: codes });
  };

  const addSocialAngle = async (text, channel) => {
    if (!text.trim()) return;
    const newList = [...socialAngles, { id: Date.now(), text: text.trim(), channel: channel || '' }];
    try { await upsertCommsProject({ social: newList }); refresh(); showToast('Added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const updateSocialAngle = async (sid, fields) => {
    const newList = socialAngles.map(s => s.id === sid ? { ...s, ...fields } : s);
    try { await upsertCommsProject({ social: newList }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteSocialAngle = async (sid) => {
    const newList = socialAngles.filter(s => s.id !== sid);
    try { await upsertCommsProject({ social: newList }); refresh(); showToast('Deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const publications = coverage.filter(c => c.type === 'Publication');
  const engagements = coverage.filter(c => c.type !== 'Publication');

  return (
    <div style={S.page}>
      {/* Photographs open into the full-screen review mode; PDFs keep the
          plain lightbox, which is the only thing that can render them. */}
      {lightboxEntry?.entries?.[lightboxEntry.index]?.isPdf
        ? <Lightbox entries={lightboxEntry.entries} index={lightboxEntry.index} onClose={() => setLightboxEntry(null)} rankView={rankView} />
        : lightboxEntry && (
            <ReviewMode
              entries={lightboxEntry.entries}
              index={lightboxEntry.index}
              folderPath={lightboxEntry.folderPath}
              deep={!!lightboxEntry.deep}
              label={(lightboxEntry.folderPath || '').split('/').slice(-2).join(' · ')}
              onClose={() => setLightboxEntry(null)}
              rankView={rankView}
              tagsByAsset={tagsByAsset}
              onTagsSaved={() => setTagsRev(r => r + 1)}
              showToast={showToast}
              versionPref={versionPref}
              canRename={canRename}
              canShare={canShare}
              kitSchema={kitSchema}
              projectCode={projectCode}
              project={project}
              onRenamed={() => {
                // Tag rows moved with the files, and the folder listing behind
                // the grid still describes the old names.
                setTagsRev(r => r + 1);
                setRanksRev(r => r + 1);
                refreshFolders();
              }}
              onShared={() => setSharesRev(r => r + 1)}
            />
          )}
      {rankMenu}
      {assignOpen && (
        <AssignToPostModal
          assets={selectedList}
          posts={data.social_cal || []}
          project={project}
          kitSchema={kitSchema}
          onClose={() => setAssignOpen(false)}
          onDone={() => {
            setAssignOpen(false);
            exitSelectMode();
            refresh();
            showToast(`${selectedList.length} file${selectedList.length === 1 ? '' : 's'} added — open the Social tab to build the post`);
          }}
          showToast={showToast}
        />
      )}
      {themeAssignOpen && (
        <AssignToThemeModal
          assets={selectedList.map(a => ({ ...a, projectCode: project.project_code }))}
          kitSchema={kitSchema}
          onClose={() => setThemeAssignOpen(false)}
          onDone={async (targets) => {
            setThemeAssignOpen(false);
            exitSelectMode();
            // Offer to add this project to the plain themes it was just assigned to,
            // so the theme's "Related projects" stays consistent.
            const linkable = [...new Set(targets
              .filter(t => !['practice', 'collaborators'].includes(t.themeId))
              .map(t => t.themeId))]
              .filter(tid => !(project.themes || []).includes(tid));
            if (linkable.length && window.confirm(`Also link ${project.name} to ${linkable.length === 1 ? 'this theme' : 'these themes'} under Practice?`)) {
              try { await upsertCommsProject({ themes: [...(project.themes || []), ...linkable] }); refresh(); } catch (e) { showToast(e.message, 'error'); }
            }
            setTagsRev(r => r + 1);
            showToast(`Assigned to ${targets.length} theme${targets.length === 1 ? '' : 's'} — see the Practice tab`);
          }}
          showToast={showToast}
        />
      )}
      {tagModalOpen && (
        <TagAssetsModal
          assets={selectedList.map(a => ({ ...a, projectCode: project.project_code }))}
          onClose={() => setTagModalOpen(false)}
          onDone={() => {
            setTagModalOpen(false);
            exitSelectMode();
            setTagsRev(r => r + 1);
            showToast('Hashtags saved');
          }}
          showToast={showToast}
        />
      )}
      {shareAssets && (
        <ShareAssetsModal
          assets={shareAssets}
          projectCode={project.project_code}
          onClose={() => setShareAssets(null)}
          onDone={() => {
            exitSelectMode();
            setSharesRev(r => r + 1);
          }}
          showToast={showToast}
        />
      )}
      {rankModalOpen && (
        <RankAssetsModal
          assets={selectedList.map(a => ({ ...a, projectCode: project.project_code }))}
          ranksById={ranksById}
          onClose={() => setRankModalOpen(false)}
          onDone={(rank, n) => {
            setRankModalOpen(false);
            exitSelectMode();
            setRanksRev(r => r + 1);
            showToast(rank
              ? `Ranked ${n} file${n === 1 ? '' : 's'} at ${rank} star${rank === 1 ? '' : 's'}`
              : `Cleared the rank on ${n} file${n === 1 ? '' : 's'}`);
          }}
          showToast={showToast}
        />
      )}
      {selectMode && (
        <SelectBar
          count={selectedList.length}
          emptyText="Click files to select them"
          actions={[
            { label: 'Rank', onClick: () => setRankModalOpen(true) },
            { label: 'Share', onClick: () => setShareAssets(selectedList) },
            { label: 'Assign to post', onClick: () => setAssignOpen(true) },
            { label: 'Assign to theme', onClick: () => setThemeAssignOpen(true) },
            { label: 'Hashtags', onClick: () => setTagModalOpen(true) },
          ]}
          onCancel={exitSelectMode}
        />
      )}
      <BackBtn onClick={onBack} label="All projects" />
      <div style={{ ...S.eyebrow, marginBottom: 6 }}>{[project.typology, project.city, project.year_start].filter(Boolean).join(' · ')}</div>
      <h1 style={{ ...S.h1, marginBottom: 8 }}>{project.name}</h1>
      <div style={{ marginBottom: 24 }}>
        <LockableField
          key={`${id}-byline`}
          compact
          label="Byline"
          multiline={false}
          value={project.byline || ''}
          canEdit={canEdit}
          versions={fieldVersions.versionsByField.byline || []}
          onSave={v => saveField('byline', v)}
          onSaveVersion={v => createFieldVersion('byline', v)}
          onRestore={v => saveField('byline', v)}
          onRename={renameFieldVersion}
          onDeleteVersion={deleteFieldVersion}
          placeholder="Add a byline…"
          inputStyle={{ fontSize: 15, color: '#6E6C66', fontStyle: 'italic', border: 'none', borderBottom: '1px dashed rgba(27,27,25,.25)', borderRadius: 0, padding: '2px 0', outline: 'none' }}
        />
      </div>

      {/* PROJECT FACTS (from main app registry) */}
      <ProjectFactsDrawer projectCode={project.project_code} canEdit={canEdit} showToast={showToast} onOpenVendorRegister={onOpenVendorRegister} />

      {/* PRESS KIT */}
      <div style={{ marginBottom: 32 }}>
        <div style={S.blockTitle}>
          <span>Press Kit & Assets</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ display: 'flex', gap: 2 }}>
              {[
                { n: 1, title: '1 column', icon: (
                  <svg width="14" height="11" viewBox="0 0 18 14" fill="currentColor">
                    <rect x="1" y="2" width="16" height="1" rx="0.5" />
                    <rect x="1" y="6" width="16" height="1" rx="0.5" />
                    <rect x="1" y="10" width="16" height="1" rx="0.5" />
                  </svg>
                )},
                { n: 2, title: '2 columns', icon: (
                  <svg width="14" height="11" viewBox="0 0 18 14" fill="currentColor">
                    <rect x="1" y="2" width="6" height="1" rx="0.5" /><rect x="1" y="6" width="6" height="1" rx="0.5" /><rect x="1" y="10" width="6" height="1" rx="0.5" />
                    <rect x="11" y="2" width="6" height="1" rx="0.5" /><rect x="11" y="6" width="6" height="1" rx="0.5" /><rect x="11" y="10" width="6" height="1" rx="0.5" />
                  </svg>
                )},
                { n: 3, title: '3 columns', icon: (
                  <svg width="14" height="11" viewBox="0 0 18 14" fill="currentColor">
                    <rect x="1" y="2" width="4" height="1" rx="0.5" /><rect x="1" y="6" width="4" height="1" rx="0.5" /><rect x="1" y="10" width="4" height="1" rx="0.5" />
                    <rect x="7" y="2" width="4" height="1" rx="0.5" /><rect x="7" y="6" width="4" height="1" rx="0.5" /><rect x="7" y="10" width="4" height="1" rx="0.5" />
                    <rect x="13" y="2" width="4" height="1" rx="0.5" /><rect x="13" y="6" width="4" height="1" rx="0.5" /><rect x="13" y="10" width="4" height="1" rx="0.5" />
                  </svg>
                )},
              ].map(({ n, title, icon }) => (
                <button key={n} onClick={() => { setImgCols(n); try { localStorage.setItem('comms_imgCols', String(n)); } catch {} }}
                  title={title}
                  style={{ background: imgCols === n ? '#21302A' : 'none', color: imgCols === n ? '#F7F6F2' : '#6E6C66', border: '1px solid rgba(27,27,25,.2)', borderRadius: 2, padding: '2px 4px', cursor: 'pointer', lineHeight: 0, display: 'flex', alignItems: 'center' }}>
                  {icon}
                </button>
              ))}
            </div>
            <button onClick={handleExpandAll} title={expandAll ? 'Collapse all cards' : 'Expand all cards'}
              style={{ background: expandAll ? '#21302A' : 'none', color: expandAll ? '#F7F6F2' : '#6E6C66', border: '1px solid rgba(27,27,25,.2)', borderRadius: 2, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
              <ChevronsUpDown size={11} />
            </button>
            {/* Listings are cached so revisiting a project doesn't re-read
                Dropbox; this re-reads them for files added since. */}
            <button onClick={refreshFolders} title="Reload folders from Dropbox"
              style={{ background: 'none', color: '#6E6C66', border: '1px solid rgba(27,27,25,.2)', borderRadius: 2, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
              <RefreshCw size={11} />
            </button>
            <button onClick={() => setManageMode(m => !m)} title={manageMode ? 'Done — close settings' : 'Settings: image versions, card order, hide cards'}
              style={{ background: manageMode ? '#21302A' : 'none', color: manageMode ? '#F7F6F2' : '#6E6C66', border: '1px solid rgba(27,27,25,.2)', borderRadius: 2, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
              <SlidersHorizontal size={11} />
            </button>
            <button onClick={() => setShortlistOnly(v => !v)}
              title={shortlistOnly ? 'Showing only the publication shortlist — click to show everything' : `Show only assets ranked ${SHORTLIST_MIN_RANK} stars or above`}
              style={{ background: shortlistOnly ? GOLD : 'none', color: shortlistOnly ? '#fff' : '#6E6C66', border: shortlistOnly ? `1px solid ${GOLD}` : '1px solid rgba(27,27,25,.2)', borderRadius: 2, cursor: 'pointer', padding: '2px 5px', lineHeight: 1, display: 'flex', alignItems: 'center', fontSize: 10 }}>
              {'\u2605'}
            </button>
            {/* Image selection is available to every authenticated user: the
                underlying comms writes (assign to post/theme, hashtags) are all
                permitted by RLS `USING (true)`, so this tool is not gated on canEdit. */}
            <button onClick={toggleSelectMode} title={selectMode ? 'Exit selection' : 'Select images or videos for a social post'}
              style={{ background: selectMode ? GOLD : 'none', color: selectMode ? '#fff' : '#6E6C66', border: selectMode ? `1px solid ${GOLD}` : '1px solid rgba(27,27,25,.2)', borderRadius: 2, cursor: 'pointer', padding: '2px 4px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
              <ImagePlus size={11} />
            </button>
          </div>
        </div>
        {folderMismatch && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, background: '#FEF9EC', border: '1px solid rgba(159,144,42,.3)', borderRadius: 4, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#7A6020' }}>
            <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ flex: 1 }}>Dropbox folder name is out of sync — currently linked to <strong>{folderMismatch}</strong>. Rename the Dropbox folder to <strong>{projectFolderName}</strong> to match the updated project name. Every file keeps its id, so tags, ranks and share links follow it.</span>
            {/* The same move the audit panel offers, one click nearer: this
                banner is what a person sees first after renaming a project,
                and sending them to find a button further down lost most of
                them. The next folder sync would do it too. */}
            {canEdit && (
              <button
                onClick={renameProjectFolder}
                disabled={auditState.loading}
                style={{ flexShrink: 0, background: 'none', border: `1px solid ${GOLD}`, borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: GOLD, cursor: auditState.loading ? 'default' : 'pointer', padding: '3px 8px', opacity: auditState.loading ? 0.5 : 1 }}
              >
                {auditState.loading ? 'Working…' : 'Rename in Dropbox'}
              </button>
            )}
          </div>
        )}
        {manageMode && (
          <div style={{ ...S.card, marginBottom: 14 }}>
            <div style={{ ...S.eyebrow, marginBottom: 8 }}>Asset settings</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{ ...S.body, fontSize: 13 }}>Image versions</span>
              <select
                value={versionPref}
                onChange={e => { setVersionPref(e.target.value); writeVersionPref(e.target.value); }}
                style={{ ...S.inputStyle, width: 'auto', minWidth: 170 }}
              >
                {VERSION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ ...S.meta, fontSize: 11.5, lineHeight: 1.6 }}>
              Some folders hold the same pictures at several sizes (jpeg-highres, jpeg-midres, tiff…).
              Only the version chosen here is shown and downloaded — “Best available” takes the
              largest JPEG each folder has, and falls back to the next size when that one is empty.
              The others stay in Dropbox until you pick them.
              <br />
              Drag a card by its handle to reorder it, or use the eye to hide a whole folder.
            </div>
          </div>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sectionOrder} strategy={rectSortingStrategy}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
              {sectionOrder
                .map(key => kitSchema.find(s => s.key === key))
                .filter(Boolean)
                .filter(s => manageMode || !hiddenSections.has(s.key))
                .map(s => (
                  <KitCard
                    key={s.key}
                    section={s.key}
                    label={s.label}
                    dropboxFolder={s.folder}
                    kit={kit}
                    sectionSchema={s}
                    approvedSubfolders={approvedSubfolders}
                    manageMode={manageMode}
                    isHidden={hiddenSections.has(s.key)}
                    onToggleHide={() => toggleHide(s.key)}
                    sectionExpanded={sectionExpanded}
                    sectionFiles={sectionFiles}
                    onToggleFiles={toggleSectionFiles}
                    versionPref={versionPref}
                    saveKit={saveKit}
                    projectFolderName={projectFolderName}
                    imgCols={imgCols}
                    onOpenLightbox={setLightboxEntry}
                    // A folder name opens the review mode on everything under
                    // it; the mode walks the folder itself, so no entries here.
                    onOpenFolder={path => setLightboxEntry({ entries: [], index: 0, folderPath: path, deep: true })}
                    selection={selection}
                    tagsByAsset={tagsByAsset}
                    rankView={rankView}
                    onFillTemplate={canEdit ? fillSubfolderTemplate : null}
                    fillingTemplate={fillingTemplate}
                  />
                ))
              }
            </div>
          </SortableContext>
        </DndContext>

        {/* What the Dropbox webhook noticed on its own, above the button that
            asks the same question by hand. */}
        <FolderAlertsPanel
          projectCode={project.project_code}
          rev={alertsRev}
          canEdit={canEdit}
          onCreateMissing={createMissingFolders}
          showToast={showToast}
        />

        {/* Folder structure audit */}
        {canEdit && (
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={runAudit}
              disabled={auditState.loading}
              style={{ background: 'none', border: '1px solid rgba(27,27,25,.2)', borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6E6C66', cursor: auditState.loading ? 'default' : 'pointer', padding: '4px 10px', opacity: auditState.loading ? 0.5 : 1 }}
            >
              {auditState.loading ? 'Checking…' : 'Check Dropbox structure'}
            </button>
            {auditState.results && (() => {
              const r = auditState.results;
              const missing = r.sections.filter(s => !s.exists || s.subfolders.some(sf => !sf.exists));
              // Build a flat list of unexpected items with their approval state
              const unknownItems = [
                ...(r.unknownSections || []).map(name => ({
                  key: `__root__/${name}`,
                  label: name,
                  approved: (approvedSubfolders['__root__'] || []).some(a => a.toLowerCase() === name.toLowerCase()),
                })),
                // A section that allows any subfolder has nothing to approve:
                // the audit still lists what it found there, and the webhook
                // applies the same rule when it decides what to alert on.
                ...(r.sections || []).filter(s => !s.allowAnySubfolders).flatMap(s =>
                  (s.unknownSubfolders || []).map(name => ({
                    key: `${s.folder}/${name}`,
                    label: `${s.folder} / ${name}`,
                    approved: (approvedSubfolders[s.folder] || []).some(a => a.toLowerCase() === name.toLowerCase()),
                  }))
                ),
              ];
              const unapproved = unknownItems.filter(i => !i.approved);
              const alreadyApproved = unknownItems.filter(i => i.approved);
              const anyChecked = Object.values(auditChecked).some(Boolean);
              return (
                <div style={{ fontSize: 12, color: '#6E6C66', lineHeight: 1.6 }}>
                  {r.folderNameMismatch && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, background: '#FEF9EC', border: '1px solid rgba(159,144,42,.3)', borderRadius: 4, padding: '6px 10px', marginBottom: 8, color: '#7A6020' }}>
                      <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                      <span style={{ flex: 1 }}>Dropbox folder is <strong>{r.folderNameMismatch.actual}</strong> but current name would be <strong>{r.folderNameMismatch.expected}</strong>. Renaming keeps every file id, so tags, ranks and share links follow it.</span>
                      <button
                        onClick={renameProjectFolder}
                        disabled={auditState.loading}
                        style={{ flexShrink: 0, background: 'none', border: `1px solid ${GOLD}`, borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: GOLD, cursor: auditState.loading ? 'default' : 'pointer', padding: '3px 8px', opacity: auditState.loading ? 0.5 : 1 }}
                      >
                        Rename in Dropbox
                      </button>
                    </div>
                  )}
                  {/* A read that failed says nothing about the tree, so it must
                      not be shown as a finding about it. */}
                  {r.unreadable
                    ? <span style={{ color: '#7A6020' }}>Couldn’t read this folder in Dropbox just now — nothing was checked. Try again in a moment.</span>
                    : !r.exists
                    ? <span style={{ color: '#C0392B' }}>Project folder not found in Dropbox ({r.projectFolder})</span>
                    : (
                      <>
                        {missing.length === 0
                          ? <span style={{ color: '#27AE60' }}>All schema folders present ✓</span>
                          : (
                            <span>
                              Missing: {missing.map(s => {
                                const missingSubs = s.subfolders.filter(sf => !sf.exists);
                                if (!s.exists) return s.folder;
                                return missingSubs.map(sf => `${s.folder}/${sf.name}`).join(', ');
                              }).join(', ')}
                              {' '}·{' '}
                              <button onClick={createMissingFolders} style={{ background: 'none', border: 'none', color: '#0061FF', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}>Create missing</button>
                            </span>
                          )
                        }
                        {/* Approving unexpected folders writes pressKitApprovedSubfolders
                            to public.app_settings (admin-only RLS), so the approve UI is
                            gated on canConfigure. Everyone can still run the audit and
                            create missing folders (edge functions, any authenticated user). */}
                        {canConfigure && unapproved.length > 0 && (
                          <div style={{ marginTop: 6, padding: '8px 10px', backgroundColor: '#FEF9E7', border: '1px solid #E8C84A', borderRadius: 2 }}>
                            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7A5C00', marginBottom: 6 }}>⚠ Unexpected folders</div>
                            {unapproved.map(item => (
                              <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={!!auditChecked[item.key]}
                                  onChange={e => setAuditChecked(c => ({ ...c, [item.key]: e.target.checked }))}
                                  style={{ accentColor: GOLD, cursor: 'pointer' }}
                                />
                                <span style={{ color: '#7A5C00' }}>{item.label}</span>
                              </label>
                            ))}
                            {anyChecked && (
                              <button
                                onClick={approveSelected}
                                style={{ marginTop: 6, background: GOLD, color: '#fff', border: 'none', borderRadius: 2, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', padding: '4px 10px' }}
                              >
                                Approve selected
                              </button>
                            )}
                          </div>
                        )}
                        {alreadyApproved.length > 0 && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#9A988F' }}>
                            ✓ Approved: {alreadyApproved.map(i => i.label).join(', ')}
                          </div>
                        )}
                        {(() => {
                          const missingTemplate = (r.sections || []).flatMap(s =>
                            (s.subfolderTemplateAudit || []).map(a => `${s.folder}/${a.subfolder}: ${a.missing.join(', ')}`)
                          );
                          if (missingTemplate.length === 0) return null;
                          return (
                            <div style={{ marginTop: 6, padding: '8px 10px', backgroundColor: '#F5F0DC', border: '1px solid #C8B86A', borderRadius: 2 }}>
                              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#7A6020', marginBottom: 5 }}>Missing template folders</div>
                              {missingTemplate.map((line, i) => (
                                <div key={i} style={{ fontSize: 12, color: '#7A6020', marginBottom: 2 }}>· {line}</div>
                              ))}
                              <button
                                onClick={createMissingFolders}
                                style={{ marginTop: 6, background: '#9F902A', color: '#fff', border: 'none', borderRadius: 2, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', padding: '4px 10px' }}
                              >
                                Create missing
                              </button>
                            </div>
                          );
                        })()}
                      </>
                    )
                  }
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* NOTEBOOK */}
      <div style={{ marginBottom: 32 }}>
        <BlockTitle left="Project Notes" right={`your KU notebook — signed as ${currentUser?.name || 'BRIO'}`} />
        {NTS_CATS.map(([cat, label]) => {
          const entries = Array.isArray(nts[cat]) ? nts[cat] : [];
          const show = showAll[cat];
          const visible = show ? entries : entries.slice(0, 1);
          return (
            <div key={cat} style={{ marginBottom: 20 }}>
              <div style={{ ...S.eyebrow, marginBottom: 8 }}>{label}</div>
              {/* Like Website text: author/date on the left, controls on the
                  right, both above the box the note is written in. */}
              {visible.map(e => (
                canEdit
                  ? <div key={e.id} style={{ marginBottom: 10 }}>
                      <LockableField
                        key={`${id}-nts-${cat}-${e.id}`}
                        compact
                        label={[e.by, e.at].filter(Boolean).join(' · ')}
                        value={e.text || ''}
                        canEdit={canEdit}
                        versions={fieldVersions.versionsByField[noteFieldKey(cat, e.id)] || []}
                        onSave={v => saveNote(cat, e.id, v)}
                        onSaveVersion={v => createFieldVersion(noteFieldKey(cat, e.id), v)}
                        onRestore={v => saveNote(cat, e.id, v)}
                        onRename={renameFieldVersion}
                        onDeleteVersion={deleteFieldVersion}
                        actions={
                          <button onClick={() => deleteNote(cat, e.id)} title="Delete this note" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, display: 'flex', flexShrink: 0 }}><Trash2 size={12} /></button>
                        }
                        placeholder="Note…"
                        minHeight={40}
                      />
                    </div>
                  : <div key={e.id} style={{ padding: '8px 12px', background: '#FBFAF7', borderRadius: 3, marginBottom: 6, borderLeft: '2px solid #33473F' }}>
                      <p style={{ fontSize: 13.5, color: '#1B1B19', lineHeight: 1.6, margin: '0 0 4px' }}>{e.text}</p>
                      <p style={{ ...S.meta, marginTop: 4 }}>{e.by} · {e.at}</p>
                    </div>
              ))}
              {/* Empty category: the same locked field, with the same controls,
                  so every heading looks alike whether or not it has been
                  written in. Writing in it creates the category's first dated
                  note, which then carries its own version history. */}
              {entries.length === 0 && canEdit && (
                <div style={{ marginBottom: 10 }}>
                  <LockableField
                    key={`${id}-nts-${cat}-new`}
                    compact
                    value=""
                    canEdit={canEdit}
                    onSave={v => addNoteWithText(cat, v)}
                    onSaveVersion={v => addNoteAndVersion(cat, v)}
                    placeholder="Unlock the padlock to write this note…"
                    minHeight={40}
                  />
                </div>
              )}
              {entries.length > 1 && (
                <button onClick={() => setShowAll(s => ({ ...s, [cat]: !s[cat] }))} style={{ background: 'none', border: 'none', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6E6C66', cursor: 'pointer', padding: '4px 0', marginBottom: 6 }}>
                  {show ? '↑ Show less' : `↓ Show all ${entries.length} notes`}
                </button>
              )}
              {/* Second and later notes in a category are added here — the locked
                  fields above edit the notes that already exist. */}
              {entries.length > 0 && canEdit && (
                <NoteArea
                  value={noteDrafts[cat] || ''}
                  onChange={v => setNoteDrafts(d => ({ ...d, [cat]: v }))}
                  onBlurSave={() => { if ((noteDrafts[cat] || '').trim()) addNote(cat); }}
                  placeholder="Add another note… (saves on blur)"
                  minHeight={40}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* WEBSITE TEXT */}
      <div style={{ marginBottom: 32 }}>
        <LockableField
          key={`${id}-website_text`}
          label="Website text"
          hint="description + photo captions from architecturebrio.com"
          value={project.website_text || ''}
          canEdit={canEdit}
          versions={fieldVersions.versionsByField.website_text || []}
          onSave={v => saveField('website_text', v)}
          onSaveVersion={v => createFieldVersion('website_text', v)}
          onRestore={v => saveField('website_text', v)}
          onRename={renameFieldVersion}
          onDeleteVersion={deleteFieldVersion}
          placeholder="Paste the project description and photo captions from the website…"
          minHeight={120}
        />
      </div>

      {/* OUTREACH */}
      <div style={{ marginBottom: 32 }}>
        <LockableField
          key={`${id}-comms_ideas`}
          label="Outreach"
          value={project.comms_ideas || ''}
          canEdit={canEdit}
          versions={fieldVersions.versionsByField.comms_ideas || []}
          onSave={v => saveField('comms_ideas', v)}
          onSaveVersion={v => createFieldVersion('comms_ideas', v)}
          onRestore={v => saveField('comms_ideas', v)}
          onRename={renameFieldVersion}
          onDeleteVersion={deleteFieldVersion}
          placeholder="Comms ideas for this project…"
          minHeight={64}
        />
      </div>

      {/* PUBLISHING HISTORY */}
      <div style={{ marginBottom: 32 }}>
        <BlockTitle left="Publishing history" right="attach links / scans" />
        {/* Publications linked via comms.publications.project_codes */}
        {(data.publications || []).filter(p => Array.isArray(p.project_codes) && p.project_codes.includes(id)).map(p => {
          const pubAuthor = p.author_contact_id != null ? (data.pub_people || []).find(c => c.id === Number(p.author_contact_id)) : null;
          const pubTextOpen = showPubText.has(p.comms_id);
          return (
          <div key={p.comms_id} style={{ padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={S.meta}>{p.date || p.year || ''}</span>
              <span style={{ flex: 1, fontSize: 14, color: '#1B1B19' }}>{p.title || p.magazine}{pubAuthor && <span style={{ ...S.meta, marginLeft: 8 }}>by {pubAuthor.name}</span>}</span>
              {p.is_interview && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#33473F', color: '#fff', borderRadius: 2, padding: '1px 5px' }}>Interview</span>}
              {p.notes && (
                <button
                  onClick={() => setShowPubText(prev => { const n = new Set(prev); if (n.has(p.comms_id)) n.delete(p.comms_id); else n.add(p.comms_id); return n; })}
                  title={pubTextOpen ? 'Hide publication text' : 'Show publication text'}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 2, background: 'none', border: 'none', cursor: 'pointer', color: '#6E6C66', padding: 0, flexShrink: 0, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase' }}
                >
                  {pubTextOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}Text
                </button>
              )}
              {p.url && <a href={p.url} target="_blank" rel="noreferrer" style={{ color: '#21302A', flexShrink: 0 }} title="Open publication"><ExternalLink size={12} /></a>}
            </div>
            {p.notes && pubTextOpen && (
              <div style={{ marginTop: 6 }}>
                {p.notes.split(/\n{2,}/).map((para, i) => (
                  <p key={i} style={{ ...S.body, color: '#4A4842', margin: i === 0 ? 0 : '10px 0 0' }}>
                    {para.split('\n').map((line, j) => (
                      <React.Fragment key={j}>{j > 0 && <br />}{line}</React.Fragment>
                    ))}
                  </p>
                ))}
              </div>
            )}
          </div>
          );
        })}
        {publications.length === 0 && (data.publications || []).filter(p => Array.isArray(p.project_codes) && p.project_codes.includes(id)).length === 0 ? <EmptyNote text="Not shared anywhere yet." /> : publications.map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
            {canEdit
              ? <InlineInput value={c.when} onBlurSave={v => updateCoverage(c.id, { when: v })} placeholder="Date…" style={{ ...S.meta, width: 72, flexShrink: 0 }} />
              : <span style={{ ...S.meta, flexShrink: 0 }}>{c.when}</span>}
            {canEdit
              ? <InlineInput value={c.name} onBlurSave={v => updateCoverage(c.id, { name: v })} placeholder="Publication name…" style={{ flex: 1, fontSize: 14, color: '#1B1B19' }} />
              : <span style={{ flex: 1, fontSize: 14, color: '#1B1B19' }}>{c.name}</span>}
            {c.link && <a href={c.link} target="_blank" rel="noreferrer" style={{ color: '#21302A', flexShrink: 0 }} title="Open link"><ExternalLink size={12} /></a>}
            {canEdit && (
              <>
                <InlineInput value={c.link} onBlurSave={v => updateCoverage(c.id, { link: v })} placeholder="Link…" style={{ width: 160, fontSize: 11, color: '#9A988F' }} />
                <button onClick={() => deleteCoverage(c.id)} title="Delete entry" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0 }}><Trash2 size={13} /></button>
              </>
            )}
          </div>
        ))}
        {canEdit && (
          <AddLine>
            <InlineInput value={newCoverage.name} onChange={v => setNewCoverage(x => ({ ...x, name: v }))} placeholder="Publication name…" />
            <InlineInput value={newCoverage.when} onChange={v => setNewCoverage(x => ({ ...x, when: v }))} placeholder="Year…" style={{ width: 80 }} />
            <InlineInput value={newCoverage.link} onChange={v => setNewCoverage(x => ({ ...x, link: v }))} placeholder="Link…" />
            <BtnGhost onClick={addCoverage}>+ Add</BtnGhost>
          </AddLine>
        )}
      </div>

      {/* PUBLIC ENGAGEMENT */}
      <div style={{ marginBottom: 32 }}>
        <BlockTitle left="Public engagement" right="events, exhibitions, talks, lectures" />
        {engagements.length === 0 ? <EmptyNote text="No public engagements recorded." /> : engagements.map(c => (
          <div key={c.id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
            {canEdit ? (
              <>
                <InlineInput value={c.when} onBlurSave={v => updateCoverage(c.id, { when: v })} placeholder="Year…" style={{ width: 64 }} />
                <InlineInput value={c.name} onBlurSave={v => updateCoverage(c.id, { name: v })} placeholder="Event / lecture name…" style={{ flex: 2 }} />
                <InlineInput value={c.link} onBlurSave={v => updateCoverage(c.id, { link: v })} placeholder="Link…" style={{ flex: 1 }} />
                {c.link && <a href={c.link} target="_blank" rel="noreferrer" style={{ color: '#21302A', flexShrink: 0 }}><ExternalLink size={12} /></a>}
                <button onClick={() => deleteCoverage(c.id)} title="Delete entry" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0 }}><Trash2 size={13} /></button>
              </>
            ) : (
              <>
                <span style={S.meta}>{c.when}</span>
                <span style={{ flex: 1, fontSize: 14, color: '#1B1B19' }}>{c.name}</span>
                <Chip label={c.type} cls="pipe" />
                {c.link && <a href={c.link} target="_blank" rel="noreferrer" style={{ color: '#21302A' }}><ExternalLink size={12} /></a>}
              </>
            )}
          </div>
        ))}
        {canEdit && (
          <AddLine>
            <InlineInput value={newCoverage.name} onChange={v => setNewCoverage(x => ({ ...x, name: v, type: 'Event' }))} placeholder="Event / lecture name…" />
            <InlineInput value={newCoverage.when} onChange={v => setNewCoverage(x => ({ ...x, when: v }))} placeholder="Year…" style={{ width: 80 }} />
            <BtnGhost onClick={addCoverage}>+ Add</BtnGhost>
          </AddLine>
        )}
      </div>

      {/* AWARDS — comms.awards rows linked to this project */}
      <div style={{ marginBottom: 32 }}>
        <BlockTitle left="Awards" />
        {projectAwards.map(a => (
          <div key={a.comms_id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
            {canEdit ? (
              <>
                <InlineInput value={a.year == null ? '' : String(a.year)} onBlurSave={v => { const y = parseInt(v, 10); updateAward(a.comms_id, { year: Number.isFinite(y) ? y : null }); }} placeholder="Year…" style={{ width: 64 }} />
                <InlineInput value={a.award_body} onBlurSave={v => updateAward(a.comms_id, { award_body: v })} placeholder="Award name…" style={{ flex: 2 }} />
                <InlineInput value={a.category || ''} onBlurSave={v => updateAward(a.comms_id, { category: v })} placeholder="Category…" style={{ flex: 2 }} />
                <InlineInput value={a.result || ''} onBlurSave={v => updateAward(a.comms_id, { result: v })} placeholder="Result…" style={{ width: 110 }} />
                <InlineInput value={a.url || ''} onBlurSave={v => updateAward(a.comms_id, { url: v })} placeholder="Link…" style={{ flex: 1 }} />
                {a.url && <a href={a.url} target="_blank" rel="noreferrer" style={{ color: '#21302A', flexShrink: 0 }}><ExternalLink size={12} /></a>}
                <button onClick={() => unlinkAward(a)} title="Unlink from this project" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0, fontSize: 14, lineHeight: 1 }}>×</button>
              </>
            ) : (
              <>
                <span style={S.meta}>{a.year}</span>
                <span style={{ flex: 1, fontSize: 14, color: '#1B1B19' }}>{a.award_body}{a.category ? <span style={{ color: '#6E6C66' }}> · {a.category}</span> : null}</span>
                {a.result && <Chip label={a.result} cls={statusClass(a.result)} />}
                {a.url && <a href={a.url} target="_blank" rel="noreferrer" style={{ color: '#21302A' }}><ExternalLink size={12} /></a>}
              </>
            )}
          </div>
        ))}
        {projectAwards.length === 0 && <EmptyNote text="No awards linked to this project yet." />}
        {canEdit && (
          <AddLine>
            <InlineInput value={newAward.award_body} onChange={v => setNewAward(x => ({ ...x, award_body: v }))} placeholder="New award name…" />
            <InlineInput value={newAward.year} onChange={v => setNewAward(x => ({ ...x, year: v }))} placeholder="Year…" style={{ width: 80 }} />
            <BtnGhost onClick={addAward}>+ Add</BtnGhost>
            {linkableAwards.length > 0 && (
              <select value="" onChange={e => { if (e.target.value) linkAward(e.target.value); }} title="Link an award already on the practice list" style={{ ...S.inputStyle, maxWidth: 260 }}>
                <option value="">Link existing award…</option>
                {linkableAwards.map(a => <option key={a.comms_id} value={a.comms_id}>{[a.year, a.award_body, a.category].filter(Boolean).join(' · ')}</option>)}
              </select>
            )}
          </AddLine>
        )}
      </div>

      {/* CONFIDENTIALITY */}
      {canEdit && (
        <div style={{ marginBottom: 32 }}>
          <BlockTitle left="Comms & Outreach" />
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#1B1B19' }}>
            <input
              type="checkbox"
              checked={!!project.confidential}
              onChange={async e => {
                try { await upsertCommsProject({ confidential: e.target.checked }); refresh(); showToast(e.target.checked ? 'Marked confidential' : 'Confidential flag removed'); } catch (err) { showToast(err.message, 'error'); }
              }}
              style={{ accentColor: GOLD, width: 16, height: 16 }}
            />
            <span>
              <strong>Confidential project (NDA)</strong>
              <span style={{ ...S.meta, display: 'block', marginTop: 1 }}>Mutes the card on the Projects board and hides it from any public-facing lists. Does not restrict access within this app.</span>
            </span>
          </label>
        </div>
      )}

      {/* SOCIAL ANGLES */}
      <div style={{ marginBottom: 32 }}>
        <BlockTitle left="Social angles" right="story ideas for this project" />
        {socialAngles.length === 0 && <EmptyNote text="No social angles noted yet." />}
        {socialAngles.map(s => (
          <div key={s.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
            {s.channel && <Chip label={s.channel} cls="line" />}
            <span style={{ ...S.body, flex: 1 }}>{s.text}</span>
            {canEdit && (
              <button onClick={() => deleteSocialAngle(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2, flexShrink: 0 }}><Trash2 size={13} /></button>
            )}
          </div>
        ))}
        {canEdit && (
          <AddLine>
            <select value={newSocial.channel} onChange={e => setNewSocial(x => ({ ...x, channel: e.target.value }))} style={{ ...S.inputStyle, width: 140, flexShrink: 0 }}>
              <option value="">Channel…</option>
              {['Instagram','LinkedIn','Press','Event','Publication','Talk'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <InlineInput value={newSocial.text} onChange={v => setNewSocial(x => ({ ...x, text: v }))} placeholder="Describe the social angle…" />
            <BtnGhost onClick={async () => { await addSocialAngle(newSocial.text, newSocial.channel); setNewSocial({ text: '', channel: '' }); }}>+ Add</BtnGhost>
          </AddLine>
        )}
      </div>

      {/* DRAFT RELEASE */}
      <div style={{ marginBottom: 32 }}>
        <BlockTitle left="Draft release" />
        {draft
          ? <NoteArea value={draft} onChange={setDraft} minHeight={300} />
          : <BtnGhost onClick={() => setDraft(genDraft(project))}>Compose draft release</BtnGhost>
        }
      </div>

      {/* SHARED LINKS */}
      <div style={{ marginBottom: 32 }}>
        <BlockTitle left="Shared links" right="press files sent out — auto-deleted on expiry" />
        <SharesPanel
          projectCode={project.project_code}
          rev={sharesRev}
          onResend={files => setShareAssets(files)}
          showToast={showToast}
        />
      </div>
    </div>
  );
}

// ── SOCIAL TAB ────────────────────────────────────────────────────────────

function MiroFrameInline({ frame, boardId, onClose }) {
  const embedUrl = `https://miro.com/app/live-embed/${boardId}/?moveToWidget=${frame.id}&cot=14`;
  return (
    <div style={{ marginTop: 16, border: '1px solid rgba(27,27,25,.12)', borderRadius: 4, overflow: 'hidden', background: '#F7F6F2' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid rgba(27,27,25,.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={S.eyebrow}>Frame</span>
          <span style={{ fontSize: 13, color: '#1B1B19' }}>{frame.title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <a href={frame.deepLink} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
            <Chip label="Open in Miro ↗" cls="act" />
          </a>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6E6C66', lineHeight: 1 }}>×</button>
        </div>
      </div>
      <iframe
        src={embedUrl}
        title={frame.title}
        style={{ width: '100%', height: '60vh', border: 'none', display: 'block' }}
        allow="fullscreen; clipboard-read; clipboard-write"
      />
      <div style={{ padding: '8px 14px', borderTop: '1px solid rgba(27,27,25,.1)' }}>
        <span style={{ ...S.meta, fontSize: 11 }}>If the board appears blank, set it to "Anyone with the link can view" in Miro's share settings.</span>
      </div>
    </div>
  );
}

const POST_TYPES = ['Materials', 'Process', 'Viewpoint', 'Collaborator', "This didn't go to plan"];
const STATUS_CYCLE = { draft: 'review', review: 'approved', approved: 'posted', posted: 'draft', scheduled: 'posted' };
// max = hard caption limit; fold = roughly where "…more" truncates the caption in the feed
const PLATFORM_LIMITS = {
  'Instagram Posts':   { max: 2200, fold: 125 },
  'Instagram Stories': { max: 2200, fold: 2200 },
  'LinkedIn':          { max: 3000, fold: 210 },
  'Instagram':         { max: 2200, fold: 125 }, // backward-compat for old posts
};

// Normalise legacy 'Instagram' platform value (stored before the Posts/Stories split).
function normalizePlatform(pl) {
  return pl === 'Instagram' ? 'Instagram Posts' : pl;
}

function postPlatforms(p) {
  const raw = Array.isArray(p.platforms) && p.platforms.length ? p.platforms : (p.platform ? [p.platform] : []);
  return raw.map(normalizePlatform);
}

// ── CROP & EXPORT ─────────────────────────────────────────────────────────

const CROP_PRESETS = [
  { key: 'square',    label: 'Square 1:1',      aspect: 1,      w: 1080, h: 1080 },
  { key: 'portrait',  label: 'Portrait 4:5',    aspect: 4 / 5,  w: 1080, h: 1350 },
  { key: 'story',     label: 'Story 9:16',      aspect: 9 / 16, w: 1080, h: 1920 },
  { key: 'landscape', label: 'LinkedIn 1.91:1', aspect: 1.91,   w: 1200, h: 628 },
];

// Largest centered rect of the given aspect that fits the image, normalized 0..1
function coverRect(imgW, imgH, aspect) {
  let w, h;
  if (imgW / imgH > aspect) { h = imgH; w = imgH * aspect; } else { w = imgW; h = imgW / aspect; }
  return { x: (imgW - w) / 2 / imgW, y: (imgH - h) / 2 / imgH, w: w / imgW, h: h / imgH };
}

function slugify(s) {
  return (s || 'post').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'post';
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

function triggerDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
}

// Download the full-res original. Dropbox temporary links normally allow CORS
// reads; if not, fetchPressFileBlob streams the bytes through the edge function.
async function fetchOriginalBlob(img) {
  const { link } = await callEdgeFunction('get-press-file-link', { path: img.dropbox_path });
  try {
    return await fetchPressFileBlob({ url: link, path: img.dropbox_path });
  } catch {
    throw new Error(`Could not download ${img.file_name || img.dropbox_path}`);
  }
}

// Full hashtag list for a variant: tags from its selected sets (in set order)
// followed by its manual hashtags, de-duplicated.
function resolveHashtags(variant, hashtagSets) {
  const fromSets = (variant.hashtag_set_ids || [])
    .map(id => (hashtagSets || []).find(s => s.id === id))
    .filter(Boolean)
    .flatMap(s => s.tags || []);
  return [...new Set([...fromSets, ...(variant.hashtags || [])])];
}

function buildCaptionTxt(post, images, hashtagSets) {
  const lines = [];
  lines.push(post.title || '(untitled post)');
  if (post.post_date) lines.push(`Scheduled: ${post.post_date}`);
  if (post.type) lines.push(`Type: ${post.type}`);
  lines.push('');
  const variants = (post.variants && typeof post.variants === 'object') ? post.variants : {};
  postPlatforms(post).forEach(pl => {
    const v = variants[pl] || {};
    const tags = resolveHashtags(v, hashtagSets).slice(0, 5);
    const keywords = (v.keywords || []).join(', ');
    lines.push(`━━━ ${pl} ━━━`);
    lines.push(v.caption || '(no caption yet)');
    if (keywords) { lines.push(''); lines.push(`Keywords: ${keywords}`); }
    if (tags.length) { lines.push(''); lines.push(tags.join(' ')); }
    if ((v.collaborators || []).length) lines.push(`Collaborators: ${v.collaborators.join(' ')}`);
    lines.push('');
  });
  if (images.some(i => i.credit || i.image_caption)) {
    lines.push('Image captions & credits:');
    images.forEach((i, idx) => {
      const nn = String(idx + 1).padStart(2, '0');
      if (i.image_caption) lines.push(`${nn} [caption] ${i.image_caption}`);
      if (i.credit) lines.push(`${nn} [credit] ${i.file_name || ''} — ${i.credit}`);
    });
  }
  return lines.join('\n');
}

function CropModal({ img, onClose, onSaved, showToast }) {
  const [link, setLink] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [dims, setDims] = useState(null);
  const [preset, setPreset] = useState(CROP_PRESETS[0].key);
  const [crops, setCrops] = useState(img.crops && typeof img.crops === 'object' ? img.crops : {});
  const [saving, setSaving] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    callEdgeFunction('get-press-file-link', { path: img.dropbox_path })
      .then(j => { if (!cancelled) setLink(j.link); })
      .catch(e => { if (!cancelled) setLoadError(e.message); });
    return () => { cancelled = true; };
  }, [img.dropbox_path]);

  useEffect(() => {
    if (!link) return;
    const im = new Image();
    im.onload = () => setDims({ w: im.naturalWidth, h: im.naturalHeight });
    im.onerror = () => setLoadError('Could not load the image.');
    im.src = link;
  }, [link]);

  const p = CROP_PRESETS.find(c => c.key === preset);
  const cover = dims ? coverRect(dims.w, dims.h, p.aspect) : null;
  const rect = dims ? (crops[preset] || cover) : null;
  const zoom = rect && cover ? cover.w / rect.w : 1;

  const clampRect = r => ({
    ...r,
    x: Math.min(Math.max(r.x, 0), Math.max(0, 1 - r.w)),
    y: Math.min(Math.max(r.y, 0), Math.max(0, 1 - r.h)),
  });
  const setRect = r => setCrops(c => ({ ...c, [preset]: clampRect(r) }));

  const onZoom = z => {
    if (!rect || !cover) return;
    const cx = rect.x + rect.w / 2, cy = rect.y + rect.h / 2;
    const w = cover.w / z, h = cover.h / z;
    setRect({ x: cx - w / 2, y: cy - h / 2, w, h });
  };

  const vpW = Math.min(460, Math.round(420 * p.aspect));
  const vpH = Math.round(vpW / p.aspect);
  const scale = rect && dims ? vpW / (rect.w * dims.w) : 1;

  const onPointerDown = e => {
    if (!rect) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { sx: e.clientX, sy: e.clientY, rx: rect.x, ry: rect.y };
  };
  const onPointerMove = e => {
    const d = dragRef.current;
    if (!d || !rect || !dims) return;
    setRect({
      ...rect,
      x: d.rx - (e.clientX - d.sx) / scale / dims.w,
      y: d.ry - (e.clientY - d.sy) / scale / dims.h,
    });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const save = async () => {
    setSaving(true);
    try {
      await commsUpdate('social_post_images', 'comms_id', img.comms_id, { crops });
      onSaved();
    } catch (e) { showToast(e.message, 'error'); setSaving(false); }
  };

  const lowRes = rect && dims && rect.w * dims.w < p.w;

  return (
    <CommsModal title={`Crop — ${img.file_name || ''}`} onClose={onClose} width={560}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 14 }}>
        {CROP_PRESETS.map(c => (
          <button key={c.key} onClick={() => setPreset(c.key)}
            style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', background: preset === c.key ? '#21302A' : 'none', color: preset === c.key ? '#F7F6F2' : '#6E6C66', border: '1px solid rgba(27,27,25,.15)', borderRadius: 2, padding: '5px 9px', cursor: 'pointer' }}>
            {c.label}{crops[c.key] ? ' •' : ''}
          </button>
        ))}
      </div>

      {loadError && <EmptyNote text={loadError} />}
      {!loadError && (!link || !dims) && <EmptyNote text="Loading full-resolution image from Dropbox…" />}

      {link && dims && rect && (
        <>
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            style={{ width: vpW, height: vpH, overflow: 'hidden', position: 'relative', cursor: 'grab', touchAction: 'none', borderRadius: 3, background: '#1B1B19', margin: '0 auto' }}
          >
            <img
              src={link}
              alt={img.file_name || ''}
              draggable={false}
              style={{ position: 'absolute', width: dims.w * scale, height: dims.h * scale, left: -rect.x * dims.w * scale, top: -rect.y * dims.h * scale, maxWidth: 'none', userSelect: 'none', pointerEvents: 'none' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <span style={{ ...S.meta, fontSize: 11 }}>Zoom</span>
            <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={e => onZoom(Number(e.target.value))} style={{ flex: 1, accentColor: GOLD }} />
            <button
              onClick={() => setCrops(c => { const n = { ...c }; delete n[preset]; return n; })}
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase', background: 'none', border: '1px solid rgba(27,27,25,.15)', color: '#6E6C66', borderRadius: 2, padding: '4px 8px', cursor: 'pointer' }}>
              Reset
            </button>
          </div>
          <div style={{ ...S.meta, fontSize: 10, marginTop: 6 }}>
            Drag to reposition · exports at {p.w}×{p.h}px
            {lowRes && <span style={{ color: '#C0392B' }}> · ⚠ source is smaller than the export size — will be upscaled</span>}
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={save}>{saving ? 'Saving…' : 'Save crops'}</BtnPrimary>
      </div>
    </CommsModal>
  );
}

function ExportModal({ post, images, onClose, showToast, hashtagSets }) {
  const platforms = postPlatforms(post);
  const defaultPresets = () => {
    const s = new Set();
    if (platforms.some(p => p === 'Instagram Posts' || p === 'Instagram Stories')) { s.add('square'); s.add('portrait'); }
    if (platforms.includes('LinkedIn')) s.add('landscape');
    if (s.size === 0) { s.add('square'); s.add('portrait'); }
    return s;
  };
  const [selected, setSelected] = useState(defaultPresets);
  const [doZip, setDoZip] = useState(true);
  const [doDropbox, setDoDropbox] = useState(true);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const [done, setDone] = useState(false);
  const cancelRef = useRef(false);

  const addLog = line => setLog(l => [...l, line]);

  const togglePreset = key => setSelected(cur => {
    const next = new Set(cur);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const datePart = (post.post_date || '').replace(/-/g, '') || 'undated';
  const slug = slugify(post.title);
  const folder = `${post.post_date || 'undated'} — ${(post.title || 'untitled').replace(/[\\/:?*"<>|]/g, '_')}`;

  const run = async () => {
    if (running) return;
    const presets = CROP_PRESETS.filter(pr => selected.has(pr.key));
    if (presets.length === 0 && !images.some(i => i.is_video)) { showToast('Pick at least one format'); return; }
    if (!doZip && !doDropbox) { showToast('Pick zip download and/or Dropbox'); return; }
    setRunning(true);
    setDone(false);
    setLog([]);
    cancelRef.current = false;
    const zip = doZip ? new JSZip() : null;
    const errors = [];
    try {
      for (let i = 0; i < images.length; i++) {
        if (cancelRef.current) break;
        const img = images[i];
        const nn = String(i + 1).padStart(2, '0');
        if (img.is_video) {
          if (doDropbox) {
            addLog(`Copying video ${img.file_name} to Dropbox…`);
            const ext = (img.file_name || '').includes('.') ? img.file_name.slice(img.file_name.lastIndexOf('.')) : '.mp4';
            try {
              await callEdgeFunction('export-social-post', { folder, fileName: `${datePart}_${slug}_${nn}_video${ext}`, copyFromPath: img.dropbox_path });
            } catch (e) { errors.push(`${img.file_name}: ${e.message}`); }
          } else {
            addLog(`Skipping video ${img.file_name} (videos are copied on Dropbox only)`);
          }
          continue;
        }
        addLog(`Fetching ${img.file_name}…`);
        let bitmap;
        try {
          const blob = await fetchOriginalBlob(img);
          bitmap = await createImageBitmap(blob);
        } catch (e) {
          errors.push(`${img.file_name}: ${e.message}`);
          continue;
        }
        for (const pr of presets) {
          if (cancelRef.current) break;
          const rect = (img.crops && img.crops[pr.key]) || coverRect(bitmap.width, bitmap.height, pr.aspect);
          const canvas = document.createElement('canvas');
          canvas.width = pr.w;
          canvas.height = pr.h;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(bitmap, rect.x * bitmap.width, rect.y * bitmap.height, rect.w * bitmap.width, rect.h * bitmap.height, 0, 0, pr.w, pr.h);
          const jblob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
          if (!jblob) { errors.push(`${img.file_name} (${pr.key}): crop failed`); continue; }
          const fname = `${datePart}_${slug}_${nn}_${pr.key}.jpg`;
          if (zip) zip.file(fname, jblob);
          if (doDropbox) {
            addLog(`Uploading ${fname}…`);
            try {
              await callEdgeFunction('export-social-post', { folder, fileName: fname, contentBase64: await blobToBase64(jblob) });
            } catch (e) { errors.push(`${fname}: ${e.message}`); }
          }
        }
        bitmap.close();
      }

      if (!cancelRef.current) {
        const txt = buildCaptionTxt(post, images, hashtagSets);
        if (zip) zip.file('caption.txt', txt);
        if (doDropbox) {
          addLog('Writing caption.txt…');
          try {
            await callEdgeFunction('export-social-post', { folder, fileName: 'caption.txt', contentBase64: btoa(unescape(encodeURIComponent(txt))) });
          } catch (e) { errors.push(`caption.txt: ${e.message}`); }
        }
        if (zip) {
          addLog('Building zip…');
          const zblob = await zip.generateAsync({ type: 'blob' });
          triggerDownload(zblob, `${datePart}_${slug}.zip`);
        }
      }

      errors.forEach(e => addLog(`⚠ ${e}`));
      addLog(cancelRef.current
        ? 'Cancelled.'
        : errors.length
          ? `Done with ${errors.length} error(s).`
          : `Done — saved to Dropbox: BRIO-press / 00_Practice / Social Media / ${folder}`);
      setDone(true);
    } catch (e) {
      addLog(`⚠ ${e.message}`);
      showToast(e.message, 'error');
    }
    setRunning(false);
  };

  return (
    <CommsModal title="Export post" onClose={() => { cancelRef.current = true; onClose(); }} width={520}>
      <Field label="Formats">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CROP_PRESETS.map(pr => (
            <FilterBtn key={pr.key} label={pr.label} active={selected.has(pr.key)} onClick={() => togglePreset(pr.key)} />
          ))}
        </div>
      </Field>
      <Field label="Destination">
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5, cursor: 'pointer', fontSize: 13, color: '#1B1B19' }}>
          <input type="checkbox" checked={doZip} onChange={e => setDoZip(e.target.checked)} style={{ accentColor: GOLD }} />
          Download zip to this computer
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, color: '#1B1B19' }}>
          <input type="checkbox" checked={doDropbox} onChange={e => setDoDropbox(e.target.checked)} style={{ accentColor: GOLD }} />
          Save to Dropbox — BRIO-press / 00_Practice / Social Media / {folder}
        </label>
      </Field>
      <div style={{ ...S.meta, fontSize: 10, marginBottom: 12 }}>
        {images.length} file(s) · images without a saved crop use a centered crop · caption.txt with captions, hashtags &amp; credits is included
      </div>

      {log.length > 0 && (
        <div style={{ background: '#FBFAF7', border: '1px solid rgba(27,27,25,.1)', borderRadius: 3, padding: '8px 10px', maxHeight: 140, overflowY: 'auto', marginBottom: 12 }}>
          {log.map((l, i) => (
            <div key={i} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, color: l.startsWith('⚠') ? '#C0392B' : '#6E6C66', lineHeight: 1.7 }}>{l}</div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        {running
          ? <BtnGhost onClick={() => { cancelRef.current = true; }}>Cancel</BtnGhost>
          : <BtnGhost onClick={onClose}>{done ? 'Close' : 'Cancel'}</BtnGhost>}
        <BtnPrimary onClick={run}>{running ? 'Exporting…' : done ? 'Export again' : 'Export'}</BtnPrimary>
      </div>
    </CommsModal>
  );
}

function statusChipClass(s) {
  if (s === 'posted') return 'pub';
  if (s === 'approved' || s === 'scheduled') return 'pipe';
  if (s === 'review') return 'pot';
  return 'new';
}

function socialTypeClass(t) {
  const map = { 'Materials': 'pub', 'Process': 'pipe', 'Viewpoint': 'pot', 'Collaborator': 'act', "This didn't go to plan": 'new' };
  return map[t] || 'line';
}

function useMiroFrames(boardId) {
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!boardId) return;
    setLoading(true);
    setError(null);
    fetch(`/api/miro-frames?boardId=${encodeURIComponent(boardId)}`)
      .then(r => r.json())
      .then(json => {
        if (json.error) throw new Error(json.error);
        setFrames(json.frames || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [boardId]);

  return { frames, loading, error };
}

function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STATUS_COLORS = { draft: '#9E9C96', review: '#3A566B', approved: '#8A6A2A', scheduled: '#8A6A2A', posted: '#2F5E4E' };

// Cover image rendered square, honouring the saved square crop when present
function SquareCover({ img, size }) {
  const rect = img?.crops && typeof img.crops === 'object' ? img.crops.square : null;
  const base = { width: size || '100%', aspectRatio: '1', display: 'block' };
  if (img?.thumb_url && rect && rect.w > 0 && rect.h > 0) {
    return (
      <div style={{ ...base, position: 'relative', overflow: 'hidden' }}>
        <img src={img.thumb_url} alt={img.alt_text || ''} style={{ position: 'absolute', width: `${100 / rect.w}%`, height: `${100 / rect.h}%`, left: `-${(rect.x / rect.w) * 100}%`, top: `-${(rect.y / rect.h) * 100}%`, maxWidth: 'none', display: 'block' }} />
      </div>
    );
  }
  if (img?.thumb_url) return <img src={img.thumb_url} alt={img.alt_text || ''} style={{ ...base, objectFit: 'cover' }} />;
  return (
    <div style={{ ...base, background: '#E8E5DD', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9A988F' }}>
      {img?.is_video ? <Film size={16} /> : <ImagePlus size={16} />}
    </div>
  );
}

const PLATFORM_ABBR = { 'Instagram Posts': 'IG', 'Instagram Stories': 'Story', 'LinkedIn': 'LI', 'Instagram': 'IG' };

function FilmstripThumb({ img }) {
  if (img.thumb_url && !img.is_video) {
    return (
      <img
        src={img.thumb_url}
        alt={img.alt_text || ''}
        title={img.file_name || ''}
        loading="lazy"
        style={{ height: 150, width: 'auto', maxWidth: 260, objectFit: 'cover', borderRadius: 2, display: 'block', flex: 'none' }}
      />
    );
  }
  return (
    <div title={img.file_name || ''} style={{ height: 150, width: 120, flex: 'none', borderRadius: 2, background: '#E8E5DD', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#9A988F' }}>
      {img.is_video ? <Film size={20} /> : <ImagePlus size={20} />}
      <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, padding: '0 8px', textAlign: 'center', wordBreak: 'break-all', maxHeight: 38, overflow: 'hidden' }}>{img.file_name}</span>
    </div>
  );
}

// Filmstrip list row: slim rail with day/title/status, the whole image board as
// a large strip beside it. Clicking the row opens the post board.
function PostListRow({ p, images, canEdit, expanded, onToggle, onSaveTitle, onCycleStatus, onDelete }) {
  const now = new Date();
  const d = p.post_date ? new Date(p.post_date + 'T00:00:00') : null;
  const day = d ? String(d.getDate()).padStart(2, '0') : '—';
  const mon = d ? d.toLocaleDateString('en-GB', { month: 'short' }) : 'no date';
  const yr = d && d.getFullYear() !== now.getFullYear() ? ` ’${String(d.getFullYear()).slice(2)}` : '';
  const platformsTxt = postPlatforms(p).map(pl => PLATFORM_ABBR[pl] || pl).join('·');
  return (
    <div
      onClick={onToggle}
      title={expanded ? 'Collapse' : 'Open post board'}
      style={{ display: 'flex', gap: 16, padding: '14px 0', borderBottom: expanded ? 'none' : '1px solid rgba(27,27,25,.07)', cursor: 'pointer' }}
    >
      <div style={{ width: 128, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 5, paddingTop: 2 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontFamily: "'Spectral', Georgia, serif", fontWeight: 500, fontSize: 26, lineHeight: 1, color: '#1B1B19' }}>{day}</span>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#9A988F' }}>{mon}{yr}</span>
          <span style={{ marginLeft: 'auto', color: '#9E9C96', display: 'flex' }}>{expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>
        </div>
        {canEdit
          ? (
            <div onClick={e => e.stopPropagation()} style={{ cursor: 'text' }} title="Click to edit title">
              <InlineInput
                value={p.title || ''}
                onBlurSave={onSaveTitle}
                placeholder="Post title…"
                style={{ fontSize: 12, lineHeight: 1.35, color: '#1B1B19', background: 'transparent', border: 'none', borderRadius: 0, padding: 0 }}
              />
            </div>
          )
          : <span style={{ fontSize: 12, lineHeight: 1.35, color: '#1B1B19' }}>{p.title || <span style={{ color: '#9A988F', fontStyle: 'italic' }}>(untitled)</span>}</span>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[p.status] || STATUS_COLORS.draft, flexShrink: 0 }} />
          <span
            onClick={canEdit ? (e => { e.stopPropagation(); onCycleStatus(); }) : undefined}
            title={canEdit ? 'Click to change status' : undefined}
            style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#6E6C66', cursor: canEdit ? 'pointer' : 'inherit' }}
          >
            {p.status || 'draft'}{platformsTxt ? ` · ${platformsTxt}` : ''}
          </span>
          {canEdit && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              title="Delete post"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: '0 2px', lineHeight: 1, display: 'flex', alignItems: 'center' }}
            ><Trash2 size={12} /></button>
          )}
        </div>
        {p.type && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9A988F' }}>{p.type}</span>}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, alignItems: 'flex-start' }}>
        {images.length === 0
          ? (
            <div style={{ height: 150, flex: 'none', display: 'flex', alignItems: 'center', border: '1px dashed rgba(27,27,25,.18)', borderRadius: 3, padding: '0 18px', color: '#9A988F', fontSize: 12, fontStyle: 'italic', boxSizing: 'border-box' }}>
              No images yet — select them in a project's press kit and assign them to this post.
            </div>
          )
          : images.map(img => <FilmstripThumb key={img.comms_id} img={img} />)}
      </div>
    </div>
  );
}

function MonthView({ posts, imagesByPost, onOpenPost }) {
  const now = new Date();
  const todayStr = localDateStr(now);
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });

  const first = new Date(ym.y, ym.m, 1);
  const label = first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const startOffset = (first.getDay() + 6) % 7; // Monday-start grid
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const dateStr = d => `${ym.y}-${String(ym.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const byDate = {};
  posts.forEach(p => { if (p.post_date) (byDate[p.post_date] = byDate[p.post_date] || []).push(p); });

  const nav = delta => setYm(({ y, m }) => { const d = new Date(y, m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={() => nav(-1)} style={{ background: 'none', border: '1px solid rgba(27,27,25,.15)', borderRadius: 2, cursor: 'pointer', color: '#6E6C66', padding: '3px 10px', fontSize: 13 }}>‹</button>
        <span style={{ fontFamily: "'Spectral', Georgia, serif", fontSize: 17, color: '#1B1B19' }}>{label}</span>
        <button onClick={() => nav(1)} style={{ background: 'none', border: '1px solid rgba(27,27,25,.15)', borderRadius: 2, cursor: 'pointer', color: '#6E6C66', padding: '3px 10px', fontSize: 13 }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
          <div key={d} style={{ ...S.eyebrow, fontSize: 9, textAlign: 'center', paddingBottom: 4 }}>{d}</div>
        ))}
        {cells.map((d, i) => {
          const ds = d ? dateStr(d) : null;
          const dayPosts = ds ? (byDate[ds] || []) : [];
          const isToday = ds === todayStr;
          return (
            <div key={i} style={{ minHeight: 86, background: d ? '#F7F6F2' : 'transparent', border: d ? `1px solid ${isToday ? GOLD : 'rgba(27,27,25,.08)'}` : 'none', borderRadius: 3, padding: d ? '4px 5px' : 0 }}>
              {d && (
                <>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: isToday ? GOLD : '#9A988F', textAlign: 'right', marginBottom: 3 }}>{d}</div>
                  {dayPosts.map(p => {
                    const cover = (imagesByPost[p.comms_id] || [])[0];
                    return (
                      <button
                        key={p.comms_id}
                        onClick={() => onOpenPost(p.comms_id)}
                        title={`${p.title || '(untitled)'} · ${postPlatforms(p).join(' + ') || 'no platform'} · ${p.status || 'draft'}`}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%', background: '#FBFAF7', border: '1px solid rgba(27,27,25,.08)', borderLeft: `3px solid ${STATUS_COLORS[p.status] || STATUS_COLORS.draft}`, borderRadius: 2, padding: '3px 4px', marginBottom: 3, cursor: 'pointer', textAlign: 'left' }}
                      >
                        {cover && <span style={{ width: 18, height: 18, flexShrink: 0, borderRadius: 2, overflow: 'hidden' }}><SquareCover img={cover} size={18} /></span>}
                        <span style={{ fontSize: 10.5, color: '#1B1B19', lineHeight: 1.25, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                          {p.title || '(untitled)'}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
        {Object.entries({ draft: 'Draft', review: 'Review', approved: 'Approved', posted: 'Posted' }).map(([k, lbl]) => (
          <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...S.meta, fontSize: 10 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: STATUS_COLORS[k] }} /> {lbl}
          </span>
        ))}
      </div>
    </div>
  );
}

function IgGridView({ posts, imagesByPost, onOpenPost }) {
  const igPosts = posts
    .filter(p => postPlatforms(p).some(pl => pl === 'Instagram Posts' || pl === 'Instagram Stories') && (imagesByPost[p.comms_id] || []).length > 0)
    .sort((a, b) => (b.post_date || '').localeCompare(a.post_date || ''));
  if (igPosts.length === 0) return <EmptyNote text="No Instagram posts with images yet — the grid preview shows how your profile will read, newest first." />;
  return (
    <div style={{ maxWidth: 520 }}>
      <p style={{ ...S.meta, fontSize: 11, marginBottom: 10 }}>How the Instagram grid will read — covers with their square crop, newest first. Upcoming posts are marked.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {igPosts.map(p => {
          const cover = (imagesByPost[p.comms_id] || [])[0];
          const posted = p.status === 'posted';
          return (
            <button key={p.comms_id} onClick={() => onOpenPost(p.comms_id)} title={`${p.post_date || ''} · ${p.title || '(untitled)'} · ${p.status || 'draft'}`}
              style={{ position: 'relative', padding: 0, border: 'none', background: 'none', cursor: 'pointer', lineHeight: 0 }}>
              <SquareCover img={cover} />
              {!posted && (
                <span style={{ position: 'absolute', top: 4, left: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, letterSpacing: '0.05em', textTransform: 'uppercase', background: 'rgba(27,27,25,.65)', color: '#fff', borderRadius: 2, padding: '2px 5px' }}>
                  {p.post_date || p.status || 'draft'}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HashtagSetsModal({ sets, onSave, onClose }) {
  const [local, setLocal] = useState(() => (sets || []).map(s => ({ ...s, tags: [...(s.tags || [])] })));
  const [busy, setBusy] = useState(false);
  const update = (i, field, v) => setLocal(l => l.map((s, idx) => idx === i ? { ...s, [field]: v } : s));
  return (
    <CommsModal title="Hashtag sets" onClose={onClose} width={520}>
      <p style={{ ...S.body, fontSize: 12.5, marginBottom: 14 }}>Reusable groups of hashtags — one click on a post adds the whole set to its caption variant.</p>
      {local.map((set, i) => (
        <div key={set.id || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
          <InlineInput value={set.name} onChange={v => update(i, 'name', v)} placeholder="Set name…" style={{ width: 140, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <TokenInput value={set.tags} onSave={v => update(i, 'tags', v)} placeholder="#BRIOarchitecture #…" />
          </div>
          <button onClick={() => setLocal(l => l.filter((_, idx) => idx !== i))} title="Delete set" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 4, flexShrink: 0 }}><Trash2 size={13} /></button>
        </div>
      ))}
      <BtnGhost onClick={() => setLocal(l => [...l, { id: 'hs-' + Date.now(), name: '', tags: [] }])}>+ Add set</BtnGhost>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={async () => { setBusy(true); await onSave(local.filter(s => (s.name || '').trim() || (s.tags || []).length)); }}>
          {busy ? 'Saving…' : 'Save sets'}
        </BtnPrimary>
      </div>
    </CommsModal>
  );
}

function KeywordSetsModal({ sets, onSave, onClose }) {
  const [local, setLocal] = useState(() => (sets || []).map(s => ({ ...s, tags: [...(s.tags || [])] })));
  const [busy, setBusy] = useState(false);
  const update = (i, field, v) => setLocal(l => l.map((s, idx) => idx === i ? { ...s, [field]: v } : s));
  return (
    <CommsModal title="Keyword sets" onClose={onClose} width={520}>
      <p style={{ ...S.body, fontSize: 12.5, marginBottom: 14 }}>Reusable groups of plain keywords — one click on a post adds the whole set to its caption variant (no # prefix).</p>
      {local.map((set, i) => (
        <div key={set.id || i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
          <InlineInput value={set.name} onChange={v => update(i, 'name', v)} placeholder="Set name…" style={{ width: 140, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <TokenInput value={set.tags} onSave={v => update(i, 'tags', v)} placeholder="architecture, sustainability, interiors…" />
          </div>
          <button onClick={() => setLocal(l => l.filter((_, idx) => idx !== i))} title="Delete set" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 4, flexShrink: 0 }}><Trash2 size={13} /></button>
        </div>
      ))}
      <BtnGhost onClick={() => setLocal(l => [...l, { id: 'ks-' + Date.now(), name: '', tags: [] }])}>+ Add set</BtnGhost>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <BtnGhost onClick={onClose}>Cancel</BtnGhost>
        <BtnPrimary onClick={async () => { setBusy(true); await onSave(local.filter(s => (s.name || '').trim() || (s.tags || []).length)); }}>
          {busy ? 'Saving…' : 'Save sets'}
        </BtnPrimary>
      </div>
    </CommsModal>
  );
}

// Caption editor with live character counter and above-the-fold preview
function CaptionField({ value, onSave, platform }) {
  const [local, setLocal] = useState(value || '');
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setLocal(value || ''); }, [value]);
  const limits = PLATFORM_LIMITS[platform] || { max: 3000, fold: 200 };
  const len = local.length;
  return (
    <div>
      <textarea
        value={local}
        onChange={e => setLocal(e.target.value)}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; if (local !== (value || '')) onSave(local); }}
        placeholder={`${platform} caption…`}
        style={{ ...S.noteArea, minHeight: 110 }}
      />
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: len > limits.max ? '#C0392B' : '#9A988F', marginTop: 3 }}>
        {len} / {limits.max}
        {len > limits.fold && ` · truncated to "…more" after ~${limits.fold} characters`}
      </div>
      {len > limits.fold && (
        <div style={{ fontSize: 11.5, color: '#6E6C66', marginTop: 4, fontStyle: 'italic' }}>
          Above the fold: “{local.slice(0, limits.fold)}…”
        </div>
      )}
    </div>
  );
}

// Space/comma-separated tokens (hashtags, @accounts) stored as a text array.
// Renders as a single-row textarea that auto-expands so all tokens stay visible.
function TokenInput({ value, onSave, placeholder }) {
  const [local, setLocal] = useState((value || []).join(' '));
  const focused = useRef(false);
  const ref = useRef(null);
  useEffect(() => { if (!focused.current) setLocal((value || []).join(' ')); }, [value]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [local]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={local}
      onChange={e => setLocal(e.target.value)}
      onFocus={() => { focused.current = true; }}
      onBlur={() => {
        focused.current = false;
        const arr = local.split(/[\s,]+/).map(s => s.trim()).filter(Boolean);
        if (JSON.stringify(arr) !== JSON.stringify(value || [])) onSave(arr);
      }}
      placeholder={placeholder}
      style={{ ...S.inputStyle, resize: 'none', overflow: 'hidden', lineHeight: 1.5, display: 'block' }}
    />
  );
}

// Multi-select for @accounts: selected handles as removable chips, plus an
// input that adds on Enter/space/comma or when a datalist suggestion is picked.
function MultiAccountPicker({ value, onSave, suggestions, listId, placeholder }) {
  const list = value || [];
  const [draft, setDraft] = useState('');

  const add = raw => {
    const t = (raw || '').trim().replace(/,+$/, '');
    if (!t) return;
    const handle = t.startsWith('@') ? t : '@' + t;
    if (!list.includes(handle)) onSave([...list, handle]);
    setDraft('');
  };

  return (
    <div>
      {list.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 5 }}>
          {list.map(h => (
            <span key={h} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#DDE5DF', color: '#21302A', borderRadius: 2, padding: '3px 7px', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5 }}>
              {h}
              <button onClick={() => onSave(list.filter(x => x !== h))} title="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6E6C66', padding: 0, lineHeight: 1, fontSize: 12 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <input
        value={draft}
        list={listId}
        onChange={e => {
          const v = e.target.value;
          // Picking a datalist suggestion fires change with the full handle — add it straight away
          if ((suggestions || []).some(s => s.handle === v)) add(v);
          else setDraft(v);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === ',') { e.preventDefault(); add(draft); }
          if (e.key === 'Backspace' && !draft && list.length) onSave(list.slice(0, -1));
        }}
        onBlur={() => add(draft)}
        placeholder={list.length ? 'Add another…' : placeholder}
        style={S.inputStyle}
      />
    </div>
  );
}

function ImageCaptionField({ value, onSave }) {
  const [local, setLocal] = useState(value || '');
  const [expanded, setExpanded] = useState(false);
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setLocal(value || ''); }, [value]);
  if (!expanded && !local) {
    return (
      <button
        onClick={() => setExpanded(true)}
        style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: '0.04em', color: '#9A988F', background: 'none', border: '1px dashed rgba(27,27,25,.18)', borderRadius: 2, padding: '2px 6px', cursor: 'pointer', width: '100%', textAlign: 'left' }}
      >+ image caption</button>
    );
  }
  return (
    <div>
      <textarea
        autoFocus={expanded && !local}
        value={local}
        onChange={e => setLocal(e.target.value)}
        onFocus={() => { focused.current = true; setExpanded(true); }}
        onBlur={() => { focused.current = false; if (local !== (value || '')) onSave(local); if (!local) setExpanded(false); }}
        placeholder="Caption for this image…"
        rows={2}
        style={{ ...S.noteArea, fontSize: 11, minHeight: 0, padding: '4px 6px', resize: 'vertical', width: '100%', boxSizing: 'border-box' }}
      />
    </div>
  );
}

function SortableBoardTile({ img, canEdit, onRemove, onSaveField, onCrop }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.comms_id, disabled: !canEdit });
  const cropKeys = img.crops && typeof img.crops === 'object' ? Object.keys(img.crops).filter(k => CROP_PRESETS.some(p => p.key === k)) : [];
  // Preview the saved crop (first preset in CROP_PRESETS order that has one),
  // shown at the crop's own aspect ratio so the tile reflects the export.
  const previewPreset = CROP_PRESETS.find(p => cropKeys.includes(p.key));
  const previewRect = previewPreset ? img.crops[previewPreset.key] : null;
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, width: 148, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div {...attributes} {...listeners} title={img.file_name + (previewPreset ? ` — showing ${previewPreset.label} crop` : '')} style={{ position: 'relative', cursor: canEdit ? 'grab' : 'default', touchAction: 'none', borderRadius: 2, overflow: 'hidden', background: '#E8E5DD' }}>
        {img.thumb_url && previewRect && previewRect.w > 0 && previewRect.h > 0
          ? (
            <div style={{ position: 'relative', width: '100%', aspectRatio: String(previewPreset.aspect), overflow: 'hidden' }}>
              <img
                src={img.thumb_url}
                alt={img.alt_text || img.file_name || ''}
                style={{ position: 'absolute', width: `${100 / previewRect.w}%`, height: `${100 / previewRect.h}%`, left: `-${(previewRect.x / previewRect.w) * 100}%`, top: `-${(previewRect.y / previewRect.h) * 100}%`, maxWidth: 'none', display: 'block' }}
              />
              <span style={{ position: 'absolute', bottom: 4, left: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, letterSpacing: '0.05em', textTransform: 'uppercase', background: 'rgba(27,27,25,.55)', color: '#fff', borderRadius: 2, padding: '1px 4px' }}>
                {previewPreset.key}
              </span>
            </div>
          )
          : img.thumb_url
          ? <img src={img.thumb_url} alt={img.alt_text || img.file_name || ''} style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', display: 'block' }} />
          : (
            <div style={{ width: '100%', aspectRatio: '4/5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#9A988F' }}>
              {img.is_video ? <Film size={22} /> : <ImagePlus size={22} />}
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, padding: '0 8px', textAlign: 'center', wordBreak: 'break-all' }}>{img.file_name}</span>
            </div>
          )}
        {canEdit && (
          <button
            onClick={e => { e.stopPropagation(); onRemove(); }}
            onPointerDown={e => e.stopPropagation()}
            title="Remove from post"
            style={{ position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%', background: 'rgba(27,27,25,.55)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
          >×</button>
        )}
        {canEdit && !img.is_video && (
          <button
            onClick={e => { e.stopPropagation(); onCrop(); }}
            onPointerDown={e => e.stopPropagation()}
            title={cropKeys.length ? `Crop — ${cropKeys.join(', ')} set` : 'Crop for platforms'}
            style={{ position: 'absolute', bottom: 4, right: 4, width: 20, height: 20, borderRadius: 3, background: cropKeys.length ? GOLD : 'rgba(27,27,25,.55)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
          ><Crop size={11} /></button>
        )}
      </div>
      {canEdit ? (
        <>
          <ImageCaptionField value={img.image_caption || ''} onSave={v => onSaveField('image_caption', v)} />
          <InlineInput value={img.credit || ''} onBlurSave={v => onSaveField('credit', v)} placeholder="Credit…" style={{ fontSize: 10, padding: '3px 6px' }} />
          <InlineInput value={img.alt_text || ''} onBlurSave={v => onSaveField('alt_text', v)} placeholder="Alt text…" style={{ fontSize: 10, padding: '3px 6px' }} />
        </>
      ) : (
        <>
          {img.image_caption && <span style={{ fontSize: 10, color: '#3A3A36', lineHeight: 1.4, display: 'block', marginBottom: 2 }}>{img.image_caption}</span>}
          {img.credit && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: '#9A988F' }}>📸 {img.credit}</span>}
        </>
      )}
    </div>
  );
}

function PostBoard({ post, images, canEdit, refresh, showToast, frames, frame, onLinkFrame, hashtagSets, keywordSets, igAccounts }) {
  const [order, setOrder] = useState(images.map(i => i.comms_id));
  useEffect(() => { setOrder(images.map(i => i.comms_id)); }, [images]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [cropImg, setCropImg] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const byId = Object.fromEntries(images.map(i => [i.comms_id, i]));
  const ordered = order.map(oid => byId[oid]).filter(Boolean);

  const platforms = postPlatforms(post);
  const [activePlatform, setActivePlatform] = useState(platforms[0] || '');
  useEffect(() => {
    if (!platforms.includes(activePlatform)) setActivePlatform(platforms[0] || '');
  }, [platforms.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveField = async fields => {
    try { await commsUpdate('social_cal', 'comms_id', post.comms_id, fields); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const togglePlatform = pl => {
    const next = platforms.includes(pl) ? platforms.filter(x => x !== pl) : [...platforms, pl];
    saveField({ platforms: next });
  };

  const variants = (post.variants && typeof post.variants === 'object') ? post.variants : {};
  const variant = variants[activePlatform] || {};
  const saveVariant = (field, value) => {
    const next = { ...variants, [activePlatform]: { ...(variants[activePlatform] || {}), [field]: value } };
    saveField({ variants: next });
  };

  const handleDragEnd = async event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
    setOrder(next);
    try {
      for (let idx = 0; idx < next.length; idx++) {
        const img = byId[next[idx]];
        if (img && img.sort_order !== idx) await commsUpdate('social_post_images', 'comms_id', img.comms_id, { sort_order: idx });
      }
      refresh();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const removeImage = async imgId => {
    try { await commsDelete('social_post_images', 'comms_id', imgId); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const saveImageField = async (imgId, field, value) => {
    try { await commsUpdate('social_post_images', 'comms_id', imgId, { [field]: value }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const fieldLabel = { ...S.eyebrow, fontSize: 10, marginBottom: 4, display: 'block' };

  return (
    <div style={{ background: '#F1EFE9', border: '1px solid rgba(27,27,25,.1)', borderRadius: 4, padding: '16px 18px', margin: '2px 0 14px' }}>
      {cropImg && (
        <CropModal
          img={cropImg}
          onClose={() => setCropImg(null)}
          onSaved={() => { setCropImg(null); refresh(); showToast('Crops saved'); }}
          showToast={showToast}
        />
      )}
      {exportOpen && (
        <ExportModal post={post} images={ordered} onClose={() => setExportOpen(false)} showToast={showToast} hashtagSets={hashtagSets} />
      )}
      {/* Image board */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={fieldLabel}>Image board — first image is the cover, drag to reorder</span>
        {ordered.length > 0 && (
          <button onClick={() => setExportOpen(true)}
            style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#33473F', color: '#F7F6F2', border: 'none', borderRadius: 3, padding: '6px 11px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Download size={11} /> Export post
          </button>
        )}
      </div>
      {ordered.length === 0
        ? <EmptyNote text="No images yet. Select them in a project's press kit (Projects tab → open project → camera+ button) and assign them to this post." />
        : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={order} strategy={horizontalListSortingStrategy}>
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6 }}>
                {ordered.map(img => (
                  <SortableBoardTile
                    key={img.comms_id}
                    img={img}
                    canEdit={canEdit}
                    onRemove={() => removeImage(img.comms_id)}
                    onSaveField={(field, v) => saveImageField(img.comms_id, field, v)}
                    onCrop={() => setCropImg(img)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

      {/* Post meta */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 14 }}>
        <div>
          <span style={fieldLabel}>Scheduled date</span>
          {canEdit
            ? <input type="date" value={post.post_date || ''} onChange={e => saveField({ post_date: e.target.value })} style={{ ...S.inputStyle, width: 140 }} />
            : <span style={S.meta}>{post.post_date}</span>}
        </div>
        <div>
          <span style={fieldLabel}>Type</span>
          {canEdit
            ? (
              <select value={post.type || ''} onChange={e => saveField({ type: e.target.value || null })} style={{ ...S.inputStyle, width: 170 }}>
                <option value="">— type —</option>
                {POST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            )
            : post.type ? <Chip label={post.type} cls={socialTypeClass(post.type)} /> : null}
        </div>
        <div>
          <span style={fieldLabel}>Assigned to</span>
          {canEdit
            ? <InlineInput value={post.assigned_to || ''} onBlurSave={v => saveField({ assigned_to: v || null })} placeholder="Who…" style={{ width: 130 }} />
            : <span style={S.meta}>{post.assigned_to}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <span style={fieldLabel}>Internal note</span>
          {canEdit
            ? <InlineInput value={post.caption_draft || ''} onBlurSave={v => saveField({ caption_draft: v })} placeholder="Working note for the team — not the caption…" />
            : <span style={S.body}>{post.caption_draft}</span>}
        </div>
      </div>

      {/* Platforms + per-platform variants */}
      <div style={{ marginTop: 16 }}>
        <span style={fieldLabel}>Platforms</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PLATFORMS.map(pl => (
            <FilterBtn key={pl} label={pl} active={platforms.includes(pl)} onClick={canEdit ? () => togglePlatform(pl) : undefined} />
          ))}
        </div>
      </div>

      {platforms.length > 0 && (
        <div style={{ marginTop: 14 }}>
          {platforms.length > 1 && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
              {platforms.map(pl => (
                <button key={pl} onClick={() => setActivePlatform(pl)}
                  style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', background: activePlatform === pl ? '#21302A' : 'none', color: activePlatform === pl ? '#F7F6F2' : '#6E6C66', border: '1px solid rgba(27,27,25,.15)', borderRadius: '3px 3px 0 0', padding: '6px 12px', cursor: 'pointer' }}>
                  {pl}
                </button>
              ))}
            </div>
          )}
          {canEdit ? (
            <>
              <div style={{ marginBottom: 10 }}>
                <span style={fieldLabel}>{activePlatform} caption</span>
                <CaptionField value={variant.caption || ''} onSave={v => saveVariant('caption', v)} platform={activePlatform} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <div>
                  <span style={fieldLabel}>Hashtags <span style={{ color: resolveHashtags(variant, hashtagSets).length > 5 ? '#C0392B' : '#9A988F', fontWeight: 'normal' }}>({resolveHashtags(variant, hashtagSets).length}/5 — Instagram limit)</span></span>
                  {(hashtagSets || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 5 }}>
                      {hashtagSets.map(set => {
                        const selectedIds = variant.hashtag_set_ids || [];
                        const active = selectedIds.includes(set.id);
                        return (
                          <Chip
                            key={set.id}
                            label={set.name}
                            cls={active ? 'act' : 'line'}
                            onClick={() => saveVariant('hashtag_set_ids', active ? selectedIds.filter(id => id !== set.id) : [...selectedIds, set.id])}
                          />
                        );
                      })}
                    </div>
                  )}
                  <TokenInput value={variant.hashtags || []} onSave={v => saveVariant('hashtags', v)} placeholder={(variant.hashtag_set_ids || []).length ? 'Extra #tags…' : '#BRIOarchitecture #…'} />
                  {(() => {
                    const resolved = resolveHashtags(variant, hashtagSets);
                    if (!resolved.length) return null;
                    const over = resolved.length > 5;
                    return (
                      <>
                        <div style={{ ...S.meta, fontSize: 10, marginTop: 4, lineHeight: 1.6, color: over ? '#C0392B' : undefined }}>
                          {over ? `⚠ ${resolved.length} hashtags — only first 5 will be exported` : resolved.join(' ')}
                        </div>
                        {over && <div style={{ ...S.meta, fontSize: 10, lineHeight: 1.6 }}>{resolved.slice(0, 5).join(' ')}</div>}
                      </>
                    );
                  })()}
                </div>
                <div>
                  <span style={fieldLabel}>Keywords <span style={{ color: '#9A988F', fontWeight: 'normal', textTransform: 'none', letterSpacing: 0 }}>(plain terms in caption, comma-separated)</span></span>
                  {(keywordSets || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                      {keywordSets.map(set => {
                        const current = variant.keywords || [];
                        const setTerms = set.tags || [];
                        const allIn = setTerms.length > 0 && setTerms.every(t => current.includes(t));
                        return (
                          <button key={set.id}
                            onClick={() => {
                              const next = allIn
                                ? current.filter(t => !setTerms.includes(t))
                                : [...new Set([...current, ...setTerms])];
                              saveVariant('keywords', next);
                            }}
                            style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', background: allIn ? '#21302A' : 'rgba(27,27,25,.06)', color: allIn ? '#F7F6F2' : '#6E6C66', border: '1px solid rgba(27,27,25,.15)', borderRadius: 2, padding: '3px 7px', cursor: 'pointer' }}>
                            {set.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <TokenInput value={variant.keywords || []} onSave={v => saveVariant('keywords', v)} placeholder="architecture, interior design, sustainability…" />
                  {(variant.keywords || []).length > 0 && (
                    <div style={{ ...S.meta, fontSize: 10, marginTop: 4, lineHeight: 1.6 }}>{(variant.keywords || []).join(', ')}</div>
                  )}
                </div>
                <div>
                  <span style={fieldLabel}>Collaborators / tagged accounts</span>
                  <MultiAccountPicker
                    value={variant.collaborators || []}
                    onSave={v => saveVariant('collaborators', v)}
                    suggestions={igAccounts}
                    listId="comms-ig-accounts"
                    placeholder="@account…"
                  />
                </div>
                <div>
                  <span style={fieldLabel}>Posted URL</span>
                  <InlineInput value={variant.posted_url || ''} onBlurSave={v => saveVariant('posted_url', v)} placeholder="Link to the live post once it's up…" />
                </div>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 13.5, color: '#1B1B19', whiteSpace: 'pre-wrap' }}>
              {variant.caption || <EmptyNote text="No caption yet." />}
              {(variant.keywords || []).length > 0 && <p style={{ ...S.meta, marginTop: 6 }}>Keywords: {variant.keywords.join(', ')}</p>}
              {resolveHashtags(variant, hashtagSets).length > 0 && <p style={{ ...S.meta, marginTop: 2 }}>{resolveHashtags(variant, hashtagSets).slice(0, 5).join(' ')}</p>}
              {(variant.collaborators || []).length > 0 && <p style={{ ...S.meta, marginTop: 2 }}>with {variant.collaborators.join(' ')}</p>}
              {variant.posted_url && <a href={variant.posted_url} target="_blank" rel="noreferrer" style={{ ...S.meta, color: GOLD }}>View post ↗</a>}
            </div>
          )}
        </div>
      )}

      {/* Miro frame link (kept from the previous workflow) */}
      {(frame || (canEdit && frames.length > 0)) && (
        <div style={{ marginTop: 14 }}>
          <span style={fieldLabel}>Miro frame</span>
          {frame ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <a href={frame.deepLink} target="_blank" rel="noreferrer" style={{ ...S.meta, fontSize: 12, color: GOLD, textDecoration: 'none' }}>{frame.title} ↗</a>
              {canEdit && <button onClick={() => onLinkFrame(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: '0 2px', fontSize: 12, lineHeight: 1 }}>×</button>}
            </span>
          ) : (
            <select defaultValue="" onChange={e => { if (e.target.value) onLinkFrame(e.target.value); }} style={{ ...S.inputStyle, width: 200, fontSize: 11, padding: '4px 8px' }}>
              <option value="">Link frame…</option>
              {frames.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

function SocialTab({ data, canEdit, refresh, showToast }) {
  const [newDate, setNewDate] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('');
  const [newPlatforms, setNewPlatforms] = useState([]);
  const [showFrames, setShowFrames] = useState(false);
  const [previewFrame, setPreviewFrame] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [viewMode, setViewMode] = useState(() => { try { return localStorage.getItem('comms_social_view') || 'list'; } catch { return 'list'; } });
  const [setsOpen, setSetsOpen] = useState(false);
  const [kwSetsOpen, setKwSetsOpen] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [platformFilter, setPlatformFilter] = useState('All');

  const changeView = v => { setViewMode(v); try { localStorage.setItem('comms_social_view', v); } catch {} };
  const openPostFromView = id => { changeView('list'); setExpandedId(id); };

  const practice = data.practice || {};
  const miroUrl = practice.miro_board_url || '';
  const hashtagSets = Array.isArray(practice.hashtag_sets) ? practice.hashtag_sets : [];
  const keywordSets = Array.isArray(practice.keyword_sets) ? practice.keyword_sets : [];
  const posts = data.social_cal || [];

  const saveHashtagSets = async sets => {
    try {
      await commsUpsertSetting('practice_settings', 'hashtag_sets', JSON.stringify(sets));
      setSetsOpen(false);
      refresh();
      showToast('Hashtag sets saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const saveKeywordSets = async sets => {
    try {
      await commsUpsertSetting('practice_settings', 'keyword_sets', JSON.stringify(sets));
      setKwSetsOpen(false);
      refresh();
      showToast('Keyword sets saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const todayStr = localDateStr(new Date());
  const soonStr = localDateStr(new Date(Date.now() + 7 * 86400000));
  const needsAttention = posts.filter(p => p.post_date && p.post_date <= soonStr && p.status !== 'posted');
  const postImages = data.post_images || [];
  const imagesByPost = {};
  postImages.forEach(img => { (imagesByPost[img.post_id] = imagesByPost[img.post_id] || []).push(img); });

  const igAccounts = (data.ig_accounts || [])
    .map(v => ({ handle: (v.instagram || '').trim(), name: v.company_name }))
    .filter(v => v.handle && !v.handle.startsWith('http'))
    .map(v => ({ ...v, handle: v.handle.startsWith('@') ? v.handle : '@' + v.handle }));

  const boardId = miroUrl.match(/board\/([^/?#=]+(?:=[^/?#]*)?)/)?.[1] || '';
  const { frames, loading: framesLoading, error: framesError } = useMiroFrames(boardId);
  const frameMap = Object.fromEntries(frames.map(f => [f.id, f]));

  const addPost = async () => {
    if (!newDate) return;
    try {
      const row = await commsInsert('social_cal', {
        comms_id: 'sc-' + Date.now(),
        title: newTitle.trim() || null,
        post_date: newDate,
        type: newType || null,
        platforms: newPlatforms,
        variants: {},
        status: 'draft',
      });
      setNewDate(''); setNewTitle(''); setNewType(''); setNewPlatforms([]);
      refresh();
      setExpandedId(row.comms_id);
      showToast('Post added');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const deletePost = async id => {
    const post = posts.find(x => x.comms_id === id);
    const imgCount = (imagesByPost[id] || []).length;
    if (!window.confirm(`Delete the post "${post?.title || '(untitled)'}"${imgCount ? ` and its ${imgCount} board image${imgCount === 1 ? '' : 's'}` : ''}? This cannot be undone.`)) return;
    try { await commsDelete('social_cal', 'comms_id', id); refresh(); showToast('Deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const cycleStatus = async post => {
    const next = STATUS_CYCLE[post.status || 'draft'] || 'draft';
    try { await commsUpdate('social_cal', 'comms_id', post.comms_id, { status: next }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const linkFrame = async (postId, frameId) => {
    try { await commsUpdate('social_cal', 'comms_id', postId, { miro_frame_id: frameId || null }); refresh(); showToast('Frame linked'); } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...S.eyebrow, marginBottom: 6 }}>Social calendar</div>
        <h1 style={S.h1}>Social</h1>
        <p style={{ ...S.body, marginTop: 10, maxWidth: 560 }}>
          Plan posts, build their image boards, and write per-platform captions. Assign images from a project's press kit (Projects tab → open a project → camera+ button), then open a post here to arrange them.
        </p>
        {miroUrl && (
          <div style={{ marginTop: 14 }}>
            <a href={miroUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <BtnGhost>Open Miro board</BtnGhost>
            </a>
          </div>
        )}
      </div>

      {boardId && (
        <div style={{ marginBottom: 28 }}>
          <div
            style={{ ...S.blockTitle, cursor: 'pointer' }}
            onClick={() => setShowFrames(v => !v)}
          >
            <span>Miro frames</span>
            <span>
              {framesLoading ? 'loading…' : frames.length > 0 ? `${frames.length} frames` : ''}
              {' '}{showFrames ? '▲' : '▼'}
            </span>
          </div>

          {showFrames && (
            <div style={{ marginTop: 12 }}>
              {framesLoading && <EmptyNote text="Loading frames from Miro…" />}
              {framesError && <EmptyNote text={`Could not load frames: ${framesError}`} />}
              {!framesLoading && !framesError && frames.length === 0 && (
                <EmptyNote text="No frames found on this board." />
              )}
              {!framesLoading && frames.length > 0 && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                    {frames.map(f => {
                      const linked = posts.find(p => p.miro_frame_id === f.id);
                      const isActive = previewFrame?.id === f.id;
                      return (
                        <div
                          key={f.id}
                          onClick={() => setPreviewFrame(isActive ? null : f)}
                          style={{ ...S.card, padding: 0, display: 'flex', flexDirection: 'column', cursor: 'pointer', outline: isActive ? `2px solid ${GOLD}` : 'none', overflow: 'hidden', marginBottom: 0 }}
                        >
                          <div style={{ width: '100%', paddingTop: '62%', position: 'relative', background: `hsl(${(frames.indexOf(f) * 47 + 160) % 360}, 18%, 88%)`, flexShrink: 0 }}>
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.35 }}>
                                <rect width="28" height="28" rx="6" fill="#050038"/>
                                <path d="M18.5 7H15l-3 7-3-7H5.5L10 21h1.5l2.5-6 2.5 6H18L22.5 7H18.5z" fill="white"/>
                              </svg>
                            </div>
                          </div>
                          <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 13, color: '#1B1B19', lineHeight: 1.4 }}>{f.title}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              {linked && <Chip label={linked.post_date || 'linked'} cls="pipe" />}
                              <Chip label={isActive ? 'Hide ▲' : 'Preview ↗'} cls="act" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {previewFrame && (
                    <MiroFrameInline frame={previewFrame} boardId={boardId} onClose={() => setPreviewFrame(null)} />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <BlockTitle left="Posting calendar" right="date · title · platforms · status" />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <FilterBtn label="List" active={viewMode === 'list'} onClick={() => changeView('list')} />
            <FilterBtn label="Month" active={viewMode === 'month'} onClick={() => changeView('month')} />
            <FilterBtn label="IG Grid" active={viewMode === 'grid'} onClick={() => changeView('grid')} />
          </div>
          <FilterBtn label={showPast ? 'Showing all' : 'Past hidden'} active={showPast} onClick={() => setShowPast(v => !v)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Platform filter toggle */}
          <div style={{ display: 'flex', gap: 0, border: '1px solid rgba(27,27,25,.15)', borderRadius: 3, overflow: 'hidden' }}>
            {['All', 'Instagram Posts', 'Instagram Stories', 'LinkedIn'].map(opt => (
              <button key={opt}
                onClick={() => setPlatformFilter(opt)}
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', background: platformFilter === opt ? '#21302A' : 'none', color: platformFilter === opt ? '#F7F6F2' : '#6E6C66', border: 'none', borderRight: opt !== 'LinkedIn' ? '1px solid rgba(27,27,25,.12)' : 'none', padding: '5px 10px', cursor: 'pointer' }}>
                {opt === 'Instagram Posts' ? 'IG Posts' : opt === 'Instagram Stories' ? 'IG Stories' : opt}
              </button>
            ))}
          </div>
          {canEdit && (
            <>
              <button onClick={() => setSetsOpen(true)}
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'none', border: '1px solid rgba(27,27,25,.15)', color: '#6E6C66', borderRadius: 2, padding: '5px 10px', cursor: 'pointer' }}>
                Hashtag sets{hashtagSets.length ? ` (${hashtagSets.length})` : ''}
              </button>
              <button onClick={() => setKwSetsOpen(true)}
                style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'none', border: '1px solid rgba(27,27,25,.15)', color: '#6E6C66', borderRadius: 2, padding: '5px 10px', cursor: 'pointer' }}>
                Keyword sets{keywordSets.length ? ` (${keywordSets.length})` : ''}
              </button>
            </>
          )}
        </div>
      </div>

      {setsOpen && <HashtagSetsModal sets={hashtagSets} onSave={saveHashtagSets} onClose={() => setSetsOpen(false)} />}
      {kwSetsOpen && <KeywordSetsModal sets={keywordSets} onSave={saveKeywordSets} onClose={() => setKwSetsOpen(false)} />}

      {needsAttention.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'baseline', columnGap: 8, rowGap: 3, flexWrap: 'wrap', marginBottom: 18, fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#6E6C66' }}>
          <span style={{ color: GOLD, fontSize: 9 }}>●</span>
          <span style={{ letterSpacing: '0.04em' }}>
            {needsAttention.length === 1 ? '1 post due this week, not posted' : `${needsAttention.length} posts due this week, not posted`} —
          </span>
          {needsAttention.map((p, i) => {
            const d = p.post_date ? new Date(p.post_date + 'T00:00:00') : null;
            const when = d ? `${String(d.getDate()).padStart(2, '0')} ${d.toLocaleDateString('en-GB', { month: 'short' })}` : '';
            return (
              <span key={p.comms_id} style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                <button
                  onClick={() => openPostFromView(p.comms_id)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: '#21302A', textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: 'rgba(27,27,25,.35)', textUnderlineOffset: 3 }}
                >
                  {when} {p.title || '(untitled)'}
                </button>
                {i < needsAttention.length - 1 && <span style={{ color: '#C8C4BB' }}>·</span>}
              </span>
            );
          })}
        </div>
      )}

      {(() => {
        const filteredPosts = posts
          .filter(p => showPast || !p.post_date || p.post_date >= todayStr)
          .filter(p => platformFilter === 'All' || postPlatforms(p).includes(platformFilter));
        const pastCount = posts.filter(p => p.post_date && p.post_date < todayStr).length;
        return (
          <>
            {!showPast && pastCount > 0 && (
              <div style={{ ...S.meta, fontSize: 11, marginBottom: 10 }}>
                {pastCount} past post{pastCount !== 1 ? 's' : ''} hidden —{' '}
                <button onClick={() => setShowPast(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: GOLD, textDecoration: 'underline', textUnderlineOffset: 2 }}>show all</button>
              </div>
            )}
            {viewMode === 'month' && <MonthView posts={filteredPosts} imagesByPost={imagesByPost} onOpenPost={openPostFromView} />}
            {viewMode === 'grid' && <IgGridView posts={filteredPosts} imagesByPost={imagesByPost} onOpenPost={openPostFromView} />}
            {viewMode === 'list' && filteredPosts.length === 0 && <EmptyNote text={posts.length === 0 ? 'No posts scheduled yet.' : 'No posts match the current filters.'} />}
            {viewMode === 'list' && filteredPosts.map(p => {
        const frame = frameMap[p.miro_frame_id];
        const images = (imagesByPost[p.comms_id] || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const expanded = expandedId === p.comms_id;
        return (
          <React.Fragment key={p.comms_id}>
            <PostListRow
              p={p}
              images={images}
              canEdit={canEdit}
              expanded={expanded}
              onToggle={() => setExpandedId(expanded ? null : p.comms_id)}
              onSaveTitle={async v => { try { await commsUpdate('social_cal', 'comms_id', p.comms_id, { title: v || null }); refresh(); } catch (e) { showToast(e.message, 'error'); } }}
              onCycleStatus={() => cycleStatus(p)}
              onDelete={() => deletePost(p.comms_id)}
            />
            {expanded && (
              <PostBoard
                post={p}
                images={images}
                canEdit={canEdit}
                refresh={refresh}
                showToast={showToast}
                frames={frames}
                frame={frame}
                onLinkFrame={frameId => linkFrame(p.comms_id, frameId)}
                hashtagSets={hashtagSets}
                keywordSets={keywordSets}
                igAccounts={igAccounts}
              />
            )}
          </React.Fragment>
        );
      })}
          </>
        );
      })()}

      {canEdit && viewMode === 'list' && (
        <AddLine>
          <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} style={{ ...S.inputStyle, width: 140 }} />
          <InlineInput value={newTitle} onChange={setNewTitle} placeholder="Post title…" />
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {PLATFORMS.map(pl => (
              <FilterBtn key={pl} label={pl} active={newPlatforms.includes(pl)}
                onClick={() => setNewPlatforms(cur => cur.includes(pl) ? cur.filter(x => x !== pl) : [...cur, pl])} />
            ))}
          </div>
          <select value={newType} onChange={e => setNewType(e.target.value)} style={{ ...S.inputStyle, flex: 'none', width: 180 }}>
            <option value="">Type…</option>
            {POST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <BtnGhost onClick={addPost}>Add</BtnGhost>
        </AddLine>
      )}

      {igAccounts.length > 0 && (
        <datalist id="comms-ig-accounts">
          {igAccounts.map(a => <option key={a.handle + a.name} value={a.handle}>{a.name}</option>)}
        </datalist>
      )}
    </div>
  );
}

// ── CALENDAR TAB ──────────────────────────────────────────────────────────

function CalendarTab({ data, canEdit, refresh, showToast }) {
  const [sub, setSub] = useState('events');
  const [regionFilter, setRegionFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('All');
  const [newEvent, setNewEvent] = useState({ title: '', type: '' });
  const [newAward, setNewAward] = useState({ award_body: '', category: '' });
  const [newCall, setNewCall] = useState({ title: '' });
  const [editingEventId, setEditingEventId] = useState(null);

  const events = data.events || [];
  const awards = data.awards || [];
  const opencalls = data.opencalls || [];

  const regions = ['All', 'Europe', 'Middle East', 'India & South Asia', 'Global'];
  const eventTypes = ['All', ...Array.from(new Set(events.map(e => e.type).filter(Boolean)))];
  const awardYears = ['All', ...Array.from(new Set(awards.map(a => String(a.year)).filter(Boolean))).sort().reverse()];
  const callYears = ['All', ...Array.from(new Set(opencalls.map(o => String(o.year || (o.deadline ? o.deadline.slice(0,4) : ''))).filter(Boolean))).sort().reverse()];

  const filteredEvents = events
    .filter(e => regionFilter === 'All' || e.region === regionFilter)
    .filter(e => typeFilter === 'All' || e.type === typeFilter);

  const filteredAwards = awards.filter(a => yearFilter === 'All' || String(a.year) === yearFilter)
    .sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0));

  const filteredCalls = opencalls.filter(o => {
    if (yearFilter === 'All') return true;
    const y = o.year || (o.deadline ? o.deadline.slice(0,4) : '');
    return String(y) === yearFilter;
  }).sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0));

  const inHouseTypes = ['Studio visit', 'Visitors', 'Studio talk'];

  // comms.awards has no status column: the outcome lives in `result`, which the
  // website import and the project page also use ('Won', 'Shortlisted', ...).
  const cycleAwardStatus = async (item) => {
    const statuses = ['Enter', 'Submitted', 'Shortlisted', 'Won', 'Not entered'];
    const idx = statuses.indexOf(item.result);
    const next = statuses[(idx + 1) % statuses.length];
    try { await commsUpdate('awards', 'comms_id', item.comms_id, { result: next }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const cycleCallStatus = async (item) => {
    const statuses = ['Consider', 'Enter', 'Submitted', 'Done'];
    const idx = statuses.indexOf(item.status);
    const next = statuses[(idx + 1) % statuses.length];
    try { await commsUpdate('opencalls', 'comms_id', item.comms_id, { status: next }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const addEvent = async () => {
    if (!newEvent.title) return;
    try { await commsInsert('events', { comms_id: 'ev-' + Date.now(), ...newEvent }); setNewEvent({ title: '', type: '' }); refresh(); showToast('Event added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const addAward = async () => {
    if (!newAward.award_body) return;
    try { await commsInsert('awards', { comms_id: 'awd-' + Date.now(), ...newAward }); setNewAward({ award_body: '', category: '' }); refresh(); showToast('Award added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const addCall = async () => {
    if (!newCall.title) return;
    try { await commsInsert('opencalls', { comms_id: 'oc-' + Date.now(), ...newCall }); setNewCall({ title: '' }); refresh(); showToast('Open call added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteCalEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try { await commsDelete('events', 'comms_id', id); refresh(); showToast('Event deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteCalAward = async (id) => {
    if (!window.confirm('Delete this award?')) return;
    try { await commsDelete('awards', 'comms_id', id); refresh(); showToast('Award deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteCalCall = async (id) => {
    if (!window.confirm('Delete this open call?')) return;
    try { await commsDelete('opencalls', 'comms_id', id); refresh(); showToast('Open call deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const draftAi = async (type, context, field, table, itemId) => {
    try {
      const res = await fetch('/api/comms-draft', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ type, context }) });
      const { draft } = await res.json();
      await commsUpdate(table, 'comms_id', itemId, { [field]: draft });
      refresh(); showToast('Draft generated');
    } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...S.eyebrow, marginBottom: 6 }}>Deadlines & opportunities</div>
        <h1 style={S.h1}>Calendar</h1>
        <p style={{ ...S.body, marginTop: 10, maxWidth: 560 }}>What's on — in-house and external events the studio can attend — plus the awards and open calls to enter. RSVP to in-house events, flag priorities, and send anything needing follow-up to the outreach tasks.</p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <FilterBtn label="Events" active={sub === 'events'} onClick={() => setSub('events')} />
          <FilterBtn label="Awards" active={sub === 'awards'} onClick={() => setSub('awards')} />
          <FilterBtn label="Open calls" active={sub === 'opencalls'} onClick={() => setSub('opencalls')} />
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {sub === 'events' && (
              <>
                <InlineInput value={newEvent.title} onChange={v => setNewEvent(x => ({ ...x, title: v }))} placeholder="Event name…" style={{ maxWidth: 160 }} />
                <select value={newEvent.type} onChange={e => setNewEvent(x => ({ ...x, type: e.target.value }))} style={{ ...S.inputStyle, width: 130 }}>
                  <option value="">Type…</option>
                  {['Lecture','Panel','Workshop','Exhibition','Award Ceremony','Studio visit','Other'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <BtnGhost onClick={addEvent}>+ Add event</BtnGhost>
              </>
            )}
            {sub === 'awards' && (
              <>
                <InlineInput value={newAward.award_body} onChange={v => setNewAward(x => ({ ...x, award_body: v }))} placeholder="Award body…" style={{ maxWidth: 160 }} />
                <InlineInput value={newAward.category} onChange={v => setNewAward(x => ({ ...x, category: v }))} placeholder="Category…" style={{ maxWidth: 130 }} />
                <BtnGhost onClick={addAward}>+ Add award</BtnGhost>
              </>
            )}
            {sub === 'opencalls' && (
              <>
                <InlineInput value={newCall.title} onChange={v => setNewCall(x => ({ ...x, title: v }))} placeholder="Open call title…" style={{ maxWidth: 200 }} />
                <BtnGhost onClick={addCall}>+ Add open call</BtnGhost>
              </>
            )}
          </div>
        )}
      </div>

      {sub === 'events' && (
        <div>
          <div style={S.filterRow}>
            {regions.map(r => <FilterBtn key={r} label={r} active={regionFilter === r} onClick={() => setRegionFilter(r)} />)}
          </div>
          <div style={S.filterRow}>
            {eventTypes.map(t => <FilterBtn key={t} label={t} active={typeFilter === t} onClick={() => setTypeFilter(t)} />)}
          </div>
          {filteredEvents.map(ev => (
            <div key={ev.comms_id} style={S.card}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0, width: 70 }}>
                  <p style={S.meta}>{ev.date_start}</p>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 4 }}>
                    {editingEventId === ev.comms_id
                      ? <InlineInput value={ev.title} onBlurSave={async v => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { title: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Event name…" style={{ fontWeight: 600, fontSize: 18, flex: 1 }} />
                      : <h3 style={{ ...S.h3, flex: 1 }}>{ev.title}</h3>
                    }
                    {canEdit && (
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button onClick={() => setEditingEventId(editingEventId === ev.comms_id ? null : ev.comms_id)} title={editingEventId === ev.comms_id ? 'Done editing' : 'Edit'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: editingEventId === ev.comms_id ? GOLD : '#6E6C66', padding: 2 }}><Pencil size={13} /></button>
                        <button onClick={() => deleteCalEvent(ev.comms_id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>
                      </div>
                    )}
                  </div>
                  {editingEventId === ev.comms_id ? (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        <InlineInput value={ev.date_start || ''} onBlurSave={async v => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { date_start: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Date (YYYY-MM-DD)…" style={{ fontSize: 12, width: 150 }} />
                        <select value={ev.type || ''} onChange={async e => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { type: e.target.value }); refresh(); } catch (err) { showToast(err.message, 'error'); } }} style={{ ...S.inputStyle, width: 160 }}>
                          <option value="">Type…</option>
                          {['Lecture','Panel','Workshop','Exhibition','Award Ceremony','Studio visit','Other'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <InlineInput value={ev.venue || ''} onBlurSave={async v => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { venue: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Venue…" style={{ fontSize: 12, width: 160 }} />
                        <select value={ev.region || ''} onChange={async e => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { region: e.target.value }); refresh(); } catch (err) { showToast(err.message, 'error'); } }} style={{ ...S.inputStyle, width: 180 }}>
                          <option value="">Region…</option>
                          {regions.filter(r => r !== 'All').map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <NoteArea value={ev.description || ''} onChange={async v => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { description: v }); } catch {} }} placeholder="Description…" minHeight={60} saveOnBlur />
                    </div>
                  ) : (
                    <>
                      <p style={{ ...S.meta, marginBottom: 6 }}>{[ev.type, ev.venue, ev.region].filter(Boolean).join(' · ')}</p>
                      {ev.description && <p style={S.body}>{ev.description}</p>}
                    </>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip label={ev.attending ? 'Attending' : 'Not attending'} cls={ev.attending ? 'pub' : 'line'} onClick={async () => {
                      try { await commsUpdate('events', 'comms_id', ev.comms_id, { attending: !ev.attending }); refresh(); } catch (e) { showToast(e.message, 'error'); }
                    }} />
                    {ev.status && <Chip label={ev.status} cls={statusClass(ev.status)} />}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                      <span style={{ ...S.eyebrow, marginRight: 2 }}>RSVP</span>
                      <button onClick={async () => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { rsvp_count: Math.max(0, (ev.rsvp_count || 0) - 1) }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} style={{ background: 'none', border: '1px solid rgba(27,27,25,.15)', borderRadius: 2, width: 20, height: 20, cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, lineHeight: '18px', color: '#6E6C66', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                      <span style={{ ...S.meta, minWidth: 18, textAlign: 'center' }}>{ev.rsvp_count || 0}</span>
                      <button onClick={async () => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { rsvp_count: (ev.rsvp_count || 0) + 1 }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} style={{ background: 'none', border: '1px solid rgba(27,27,25,.15)', borderRadius: 2, width: 20, height: 20, cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 13, lineHeight: '18px', color: '#6E6C66', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                    </div>
                  </div>
                  <NoteArea value={ev.note || ''} onChange={async v => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { note: v }); } catch {} }} placeholder="Notes…" minHeight={40} />
                  {inHouseTypes.some(t => ev.type?.includes(t)) && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ ...S.eyebrow, marginBottom: 4 }}>Behind the scenes</div>
                      <NoteArea value={ev.behind || ''} onChange={async v => { try { await commsUpdate('events', 'comms_id', ev.comms_id, { behind: v }); } catch {} }} placeholder="Behind-the-scenes notes…" minHeight={40} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {filteredEvents.length === 0 && <EmptyNote text="No events for this filter." />}
        </div>
      )}

      {sub === 'awards' && (
        <div>
          <div style={S.filterRow}>
            {awardYears.map(y => <FilterBtn key={y} label={y} active={yearFilter === y} onClick={() => setYearFilter(y)} />)}
          </div>
          {filteredAwards.map(a => (
            <div key={a.comms_id} style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {a.priority && <Star size={13} color={GOLD} fill={GOLD} />}
                    {canEdit
                      ? <InlineInput value={a.award_body} onBlurSave={async v => { try { await commsUpdate('awards', 'comms_id', a.comms_id, { award_body: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Award name…" style={{ fontWeight: 600 }} />
                      : <h3 style={S.h3}>{a.award_body}</h3>
                    }
                  </div>
                  {canEdit
                    ? <InlineInput value={a.category || ''} onBlurSave={async v => { try { await commsUpdate('awards', 'comms_id', a.comms_id, { category: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Category…" style={{ fontSize: 12, color: '#6E6C66' }} />
                    : a.category ? <p style={S.meta}>{a.category}</p> : null
                  }
                  {canEdit
                    ? <InlineInput value={a.deadline || ''} onBlurSave={async v => { try { await commsUpdate('awards', 'comms_id', a.comms_id, { deadline: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Deadline (YYYY-MM-DD)…" style={{ fontSize: 12, color: '#6E6C66', marginTop: 4 }} />
                    : a.deadline ? <p style={{ ...S.meta, marginTop: 4 }}>Deadline: {a.deadline}</p> : null
                  }
                  <div style={{ marginTop: 6 }}>
                    <ProjectCodesPicker codes={a.project_codes} projects={data.projects || []} canEdit={canEdit} onSave={async codes => { try { await commsUpdate('awards', 'comms_id', a.comms_id, { project_codes: codes }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip label={a.result || 'Enter'} cls={statusClass(a.result)} onClick={() => cycleAwardStatus(a)} />
                  <button onClick={async () => { try { await commsUpdate('awards', 'comms_id', a.comms_id, { priority: !a.priority }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', color: '#6E6C66' }}>
                    {a.priority ? 'Unpin' : 'Priority'}
                  </button>
                  {canEdit && <button onClick={() => deleteCalAward(a.comms_id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>}
                </div>
              </div>
              {a.url !== undefined && <InlineInput value={a.url || ''} onBlurSave={async v => { try { await commsUpdate('awards', 'comms_id', a.comms_id, { url: v }); } catch {} }} placeholder="Award website link…" style={{ marginTop: 8 }} />}
              {canEdit && (
                <div style={{ marginTop: 10 }}>
                  <BtnGhost onClick={() => draftAi('award-submission', { award: a.award_body, category: a.category }, 'draft', 'awards', a.comms_id)}>
                    Draft from projects (AI)
                  </BtnGhost>
                </div>
              )}
              {a.draft !== undefined && <NoteArea value={a.draft || ''} onChange={async v => { try { await commsUpdate('awards', 'comms_id', a.comms_id, { draft: v }); } catch {} }} placeholder="AI draft…" minHeight={80} saveOnBlur />}
            </div>
          ))}
          {filteredAwards.length === 0 && <EmptyNote text="No awards for this year." />}
        </div>
      )}

      {sub === 'opencalls' && (
        <div>
          <div style={S.filterRow}>
            {callYears.map(y => <FilterBtn key={y} label={y} active={yearFilter === y} onClick={() => setYearFilter(y)} />)}
          </div>
          {filteredCalls.map(oc => (
            <div key={oc.comms_id} style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {oc.priority && <Star size={13} color={GOLD} fill={GOLD} />}
                    {canEdit
                      ? <InlineInput value={oc.title} onBlurSave={async v => { try { await commsUpdate('opencalls', 'comms_id', oc.comms_id, { title: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Open call title…" style={{ fontWeight: 600 }} />
                      : <h3 style={S.h3}>{oc.title}</h3>
                    }
                  </div>
                  {canEdit
                    ? <InlineInput value={oc.organiser || ''} onBlurSave={async v => { try { await commsUpdate('opencalls', 'comms_id', oc.comms_id, { organiser: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Organiser…" style={{ fontSize: 12, color: '#6E6C66' }} />
                    : oc.organiser ? <p style={S.meta}>{oc.organiser}</p> : null
                  }
                  {canEdit
                    ? <InlineInput value={oc.deadline || ''} onBlurSave={async v => { try { await commsUpdate('opencalls', 'comms_id', oc.comms_id, { deadline: v }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} placeholder="Deadline (YYYY-MM-DD)…" style={{ fontSize: 12, color: '#6E6C66', marginTop: 4 }} />
                    : oc.deadline ? <p style={{ ...S.meta, marginTop: 4 }}>Deadline: {oc.deadline}</p> : null
                  }
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip label={oc.status || 'Consider'} cls={statusClass(oc.status)} onClick={() => cycleCallStatus(oc)} />
                  <button onClick={async () => { try { await commsUpdate('opencalls', 'comms_id', oc.comms_id, { priority: !oc.priority }); refresh(); } catch (e) { showToast(e.message, 'error'); } }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', color: '#6E6C66' }}>
                    {oc.priority ? 'Unpin' : 'Priority'}
                  </button>
                  {canEdit && <button onClick={() => deleteCalCall(oc.comms_id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B0A9A0', padding: 2 }}><Trash2 size={13} /></button>}
                </div>
              </div>
              {canEdit && (
                <div style={{ marginTop: 10 }}>
                  <BtnGhost onClick={() => draftAi('opencall-application', { title: oc.title, organiser: oc.organiser }, 'draft', 'opencalls', oc.comms_id)}>
                    What do we need? (AI)
                  </BtnGhost>
                </div>
              )}
              {oc.draft !== undefined && <NoteArea value={oc.draft || ''} onChange={async v => { try { await commsUpdate('opencalls', 'comms_id', oc.comms_id, { draft: v }); } catch {} }} placeholder="AI draft…" minHeight={80} saveOnBlur />}
              <NoteArea value={oc.notes || ''} onChange={async v => { try { await commsUpdate('opencalls', 'comms_id', oc.comms_id, { notes: v }); } catch {} }} placeholder="Notes…" minHeight={40} saveOnBlur />
            </div>
          ))}
          {filteredCalls.length === 0 && <EmptyNote text="No open calls for this filter." />}
        </div>
      )}
    </div>
  );
}

// ── CONTACTS TAB ──────────────────────────────────────────────────────────

// Derives a display category from specialism text (for filter chips)
// Derives display category from comms profile type, falling back to vendor specialism
function catOf(type, specialism) {
  const t = ((type || specialism) || '').toLowerCase();
  if (/book/.test(t)) return 'Books';
  if (/writer|critic|journalist/.test(t)) return 'Writers';
  if (/photograph/.test(t)) return 'Photographers';
  if (/maker|fabricat|landscape|joiner|mason|craft/.test(t)) return 'Collaborators';
  if (/consult|agency|strategy|graphic|creative|brand/.test(t)) return 'Creatives';
  if (/\bpr\b|press|platform|publicist/.test(t)) return 'Press';
  return 'Publications';
}

function mediumOf(profile) { return profile?.medium || ''; }

// Returns the primary email for a vendor: first vendor_contact email, then company_email
function vendorEmail(vendor, vendorContacts) {
  const vc = (vendorContacts || []).find(c => c.vendor_id === vendor.id && c.email);
  return vc?.email || vendor.company_email || '';
}

// Merge vendor + comms profile into a single display object
function mergeContact(vendor, profiles) {
  const prof = (profiles || []).find(p => p.vendor_id === vendor.id) || {};
  return { vendor, prof, id: vendor.id };
}

const CFILTERS = ['All', 'Priority', 'Publications', 'Books', 'Writers', 'Photographers', 'Collaborators', 'Creatives', 'Press'];

function ContactsTab({ data, canEdit, refresh, showToast, onOpenContact }) {
  const [catFilter, setCatFilter] = useState('All');
  const [xref, setXref] = useState('');
  const [xrefResults, setXrefResults] = useState(null);

  const vendors = data.contacts || [];
  const profiles = data.comms_profiles || [];
  const themes = data.themes || [];
  const projects = data.projects || [];

  // Merge and sort: priority first then alpha
  const merged = vendors
    .map(v => mergeContact(v, profiles))
    .sort((a, b) => (b.prof.priority ? 1 : 0) - (a.prof.priority ? 1 : 0) || a.vendor.company_name.localeCompare(b.vendor.company_name));

  const filtered = merged.filter(({ vendor, prof }) => {
    const cat = catOf(prof.type, vendor.specialism);
    if (catFilter === 'Priority') return !!prof.priority;
    if (catFilter !== 'All') return cat === catFilter;
    return true;
  });

  const doCrossRef = (q) => {
    setXref(q);
    if (!q.trim()) { setXrefResults(null); return; }
    const ql = q.toLowerCase();
    const proj = projects.find(p => p.name?.toLowerCase().includes(ql) || p.project_code?.toLowerCase().includes(ql));
    const theme = themes.find(t => t.label?.toLowerCase().includes(ql) || t.name?.toLowerCase().includes(ql));
    const keys = proj ? (proj.themes || []) : theme ? (theme.keys || [theme.label || theme.name]) : null;
    if (!keys) { setXrefResults([]); return; }
    // Filter to press contacts with matching themes in their profile tags
    const results = merged
      .filter(({ prof }) => {
        const tags = prof.tags || [];
        return keys.some(k => tags.some(tag => tag?.toLowerCase().includes(k?.toLowerCase())));
      })
      .slice(0, 6);
    setXrefResults(results);
  };

  const togglePriority = async ({ vendor, prof }) => {
    try {
      if (prof.comms_id) {
        await commsUpdate('contacts', 'comms_id', prof.comms_id, { priority: !prof.priority });
      } else {
        await commsInsert('contacts', { comms_id: 'cv-' + vendor.id, name: vendor.company_name, vendor_id: vendor.id, priority: true });
      }
      refresh();
    } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'inline-block', fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', background: '#33473F', color: '#fff', padding: '3px 10px', borderRadius: 3, marginBottom: 10 }}>Shared register</div>
        <h1 style={S.h1}>Contacts</h1>
        <p style={{ ...S.body, marginTop: 10, maxWidth: 560 }}>Everyone BRIO works with across press and the wider family — publications, books, writers, photographers, collaborators and creatives. Filter by category, star the ones that matter, and open a contact for its history and published pieces.</p>
      </div>

      {/* Cross-reference search */}
      <div style={{ ...S.card, marginBottom: 20 }}>
        <label style={{ ...S.eyebrow, display: 'block', marginBottom: 8 }}>Find who fits — type a project or theme</label>
        <input
          value={xref}
          onChange={e => doCrossRef(e.target.value)}
          placeholder="e.g. House on a Stream, or climate, or materials…"
          style={{ ...S.inputStyle, width: '100%' }}
        />
        {xrefResults !== null && (
          <div style={{ marginTop: 12 }}>
            {xrefResults.length === 0
              ? <EmptyNote text="No project or theme matches that — try adding theme tags to contacts." />
              : xrefResults.map(({ vendor, prof }) => {
                  const cat = catOf(prof.type, vendor.specialism);
                  return (
                    <button key={vendor.id} onClick={() => onOpenContact(vendor.id)} style={{ display: 'block', width: '100%', textAlign: 'left', background: '#E8EDE9', border: 'none', borderRadius: 4, padding: '10px 14px', marginBottom: 8, cursor: 'pointer' }}>
                      <div style={{ fontSize: 15, fontFamily: "'Spectral', Georgia, serif", color: '#1B1B19' }}>{prof.priority ? '★ ' : ''}{vendor.company_name}</div>
                      <div style={S.meta}>{cat}</div>
                    </button>
                  );
                })
            }
          </div>
        )}
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {CFILTERS.map(f => (
          <FilterBtn key={f} label={f + (f === 'Priority' ? ' ★' : '')} active={catFilter === f} onClick={() => setCatFilter(f)} />
        ))}
      </div>

      {/* Contact rows */}
      {filtered.map(({ vendor, prof }) => {
        const cat = catOf(prof.type, vendor.specialism);
        const medium = mediumOf(prof);
        const region = prof.region || vendor.location || '';
        const hasPublished = Array.isArray(prof.published) && prof.published.length > 0;
        const isPriority = !!prof.priority;
        return (
          <div key={vendor.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '11px 14px', marginBottom: 6, background: '#F7F6F2', borderRadius: 4, border: '1px solid rgba(27,27,25,.08)' }}>
            <button
              onClick={() => togglePriority({ vendor, prof })}
              title="Toggle priority"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: isPriority ? GOLD : '#CCC', flexShrink: 0 }}
            >
              <Star size={15} fill={isPriority ? GOLD : 'none'} />
            </button>
            <button
              onClick={() => onOpenContact(vendor.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', flex: 1 }}
            >
              <span style={{ fontFamily: "'Spectral', Georgia, serif", fontSize: 17, color: '#1B1B19' }}>{vendor.company_name}</span>
            </button>
            {hasPublished && (
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#33473F', color: '#fff', padding: '2px 8px', borderRadius: 3 }}>Published</span>
            )}
            <span style={S.meta}>{[cat, cat === 'Publications' ? medium : null, region].filter(Boolean).join(' · ')}</span>
          </div>
        );
      })}
      {filtered.length === 0 && <EmptyNote text="No contacts in this category yet." />}
    </div>
  );
}

// ── CONTACT DETAIL ────────────────────────────────────────────────────────

function ContactDetail({ id, data, onBack, canEdit, refresh, showToast }) {
  const contact = (data.contacts || []).find(c => c.id === id);
  const vendorContacts = data.vendor_contacts || [];
  // comms_profiles: comms.contacts rows linked to vendors via vendor_id
  const profile = (data.comms_profiles || []).find(p => p.vendor_id === id) || null;

  const [newPubTitle, setNewPubTitle] = useState('');
  const [saving, setSaving] = useState(false);

  if (!contact) return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All contacts" />
      <EmptyNote text="Contact not found." />
    </div>
  );

  const email = vendorEmail(contact, vendorContacts);
  const social = contact.instagram ? (contact.instagram.startsWith('@') ? contact.instagram : `@${contact.instagram}`) : null;
  const persons = vendorContacts.filter(c => c.vendor_id === contact.id);
  const category = catOf(profile?.type, contact.specialism);
  const region = profile?.region || contact.location || '';
  const medium = mediumOf(profile);
  const published = Array.isArray(profile?.published) ? profile.published : [];
  const linkedPubs = (data.publications || []).filter(p => Array.isArray(p.contact_ids) && p.contact_ids.map(Number).includes(id));

  // Auto-upsert a comms.contacts profile row if one doesn't exist yet, then save field
  const saveProfile = async (field, value) => {
    setSaving(true);
    try {
      if (profile) {
        await commsUpdate('contacts', 'comms_id', profile.comms_id, { [field]: value });
      } else {
        const pid = 'cv-' + id + '-' + Date.now();
        await commsInsert('contacts', { comms_id: pid, name: contact.company_name, vendor_id: id, [field]: value });
      }
      refresh();
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(false);
  };

  const togglePriority = () => saveProfile('priority', !profile?.priority);

  const addPublished = async () => {
    if (!newPubTitle.trim()) return;
    const next = [...published, { id: Date.now(), what: newPubTitle.trim(), when: '', link: '', images: [] }];
    await saveProfile('published', next);
    setNewPubTitle('');
  };

  const updatePublished = async (pubId, changes) => {
    const next = published.map(p => p.id === pubId ? { ...p, ...changes } : p);
    await saveProfile('published', next);
  };

  const deletePublished = async (pubId) => {
    const next = published.filter(p => p.id !== pubId);
    await saveProfile('published', next);
  };

  const sendToOutreach = async () => {
    try {
      await commsInsert('rtasks', { comms_id: 'rt-' + Date.now(), title: contact.company_name, status: 'New' });
      showToast('Added to outreach tasks');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const isPriority = !!profile?.priority;
  const primaryId = profile?.primary_contact_id || null;
  const secondaryId = profile?.secondary_contact_id || null;
  const primaryPerson = persons.find(p => p.id === primaryId) || null;

  // Cycle: unselected → primary → secondary → unselected
  // If clicking an already-primary person: promote secondary to primary (if set), clear secondary; or just clear primary
  const cycleContact = async (personId) => {
    if (personId === primaryId) {
      // Was primary: clear it (and shift secondary up if set)
      if (secondaryId) {
        await saveProfile('primary_contact_id', secondaryId);
        await saveProfile('secondary_contact_id', null);
      } else {
        await saveProfile('primary_contact_id', null);
      }
    } else if (personId === secondaryId) {
      // Was secondary: clear it
      await saveProfile('secondary_contact_id', null);
    } else if (!primaryId) {
      // No primary set: make this primary
      await saveProfile('primary_contact_id', personId);
    } else {
      // Primary exists, this is unselected: make it secondary (replacing any previous secondary)
      await saveProfile('secondary_contact_id', personId);
    }
  };

  return (
    <div style={S.page}>
      <BackBtn onClick={onBack} label="All contacts" />
      <div style={{ ...S.eyebrow, marginBottom: 6 }}>
        {[category, category === 'Publications' ? medium : null, region, isPriority ? '● Priority' : null].filter(Boolean).join(' · ')}
      </div>
      <h1 style={{ ...S.h1, marginBottom: 20 }}>{contact.company_name}</h1>

      {/* Header row: Main Contact | Email | Website | Social | Format */}
      <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', borderBottom: '1px solid rgba(27,27,25,.15)', paddingBottom: 20, marginBottom: 20 }}>
        {primaryPerson && (
          <div>
            <div style={{ ...S.eyebrow, marginBottom: 6 }}>Main contact</div>
            <p style={S.body}>{primaryPerson.name}{primaryPerson.designation ? `, ${primaryPerson.designation}` : ''}</p>
          </div>
        )}
        <div>
          <div style={{ ...S.eyebrow, marginBottom: 6 }}>Email</div>
          {email
            ? <a href={`mailto:${email}`} style={{ fontSize: 13, color: '#21302A' }}>{email}</a>
            : <p style={S.body}>—</p>}
        </div>
        {contact.website && (
          <div>
            <div style={{ ...S.eyebrow, marginBottom: 6 }}>Website</div>
            <a
              href={contact.website.startsWith('http') ? contact.website : 'https://' + contact.website}
              target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: '#21302A', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <ExternalLink size={12} />{contact.website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        )}
        <div>
          <div style={{ ...S.eyebrow, marginBottom: 6 }}>Social</div>
          {social
            ? <a href={`https://instagram.com/${social.replace(/^@/, '')}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#21302A' }}>{social}</a>
            : <p style={S.body}>—</p>}
        </div>
        {category === 'Publications' && (
          <div>
            <div style={{ ...S.eyebrow, marginBottom: 6 }}>Format</div>
            <select
              value={medium}
              onChange={e => saveProfile('medium', e.target.value)}
              style={{ ...S.inputStyle, width: 'auto', minWidth: 100 }}
            >
              <option value="">—</option>
              {['Print', 'Digital', 'Both'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Priority + Send to outreach */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 28, alignItems: 'center' }}>
        <button
          onClick={togglePriority}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: isPriority ? '#F3EFD6' : 'transparent', border: '1px solid ' + (isPriority ? GOLD : 'rgba(27,27,25,.2)'), borderRadius: 4, cursor: 'pointer', fontSize: 13, color: isPriority ? GOLD : '#6E6C66', fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          <Star size={13} fill={isPriority ? GOLD : 'none'} color={isPriority ? GOLD : '#6E6C66'} />
          {isPriority ? 'Priority' : 'Mark as priority'}
        </button>
        <BtnGhost onClick={sendToOutreach}>Send to outreach →</BtnGhost>
      </div>

      {/* Rich notes sections */}
      {[
        ['first_contact', 'First contact', 'Who & how — the start of the relationship'],
        ['last_touch', 'Touchpoints', 'A running history — when & about what'],
        ['ideas', 'Ideas & pitches', 'A record and ideas bank'],
      ].map(([field, label, hint]) => (
        <div key={field} style={{ marginBottom: 28 }}>
          <BlockTitle left={label} right={hint} />
          <NoteArea
            value={profile?.[field] || ''}
            onChange={v => saveProfile(field, v)}
            placeholder={hint}
            minHeight={80}
            saveOnBlur
          />
        </div>
      ))}

      {/* Press files sent to this contact. Below the notes because it is a
          record rather than something you write: what went out, when, and what
          was in it — kept after the Dropbox folder itself is deleted. */}
      <div style={{ marginBottom: 28 }}>
        <BlockTitle left="Press files shared" right="folders sent to this contact — the record outlives the link" />
        <ContactSharesPanel contactVendorId={contact.id} showToast={showToast} />
      </div>

      {/* Published pieces */}
      <div style={{ marginBottom: 28 }}>
        <BlockTitle left="Published pieces" right="link or attach the actual coverage" />
        {linkedPubs.map(p => {
          const pubAuthor = p.author_contact_id != null ? (data.pub_people || []).find(c => c.id === Number(p.author_contact_id)) : null;
          return (
          <div key={p.comms_id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(27,27,25,.08)' }}>
            <span style={S.meta}>{p.date || p.year || ''}</span>
            {p.magazine && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9F902A', flexShrink: 0 }}>{p.magazine}</span>}
            <span style={{ flex: 1, fontSize: 14, color: '#1B1B19' }}>{p.title}{pubAuthor && <span style={{ ...S.meta, marginLeft: 8 }}>by {pubAuthor.name}</span>}</span>
            {Array.isArray(p.project_codes) && p.project_codes.map(code => {
              const proj = (data.projects || []).find(pr => pr.comms_id === code);
              return proj ? <span key={code} style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: '#6E6C66', flexShrink: 0 }}>{(proj.project_code || proj.comms_id) + ' — ' + proj.name}</span> : null;
            })}
            {p.is_interview && <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', background: '#33473F', color: '#fff', borderRadius: 2, padding: '1px 5px' }}>Interview</span>}
            {p.url && <a href={p.url} target="_blank" rel="noreferrer" style={{ color: '#21302A', flexShrink: 0 }} title="Open publication"><ExternalLink size={12} /></a>}
          </div>
          );
        })}
        {linkedPubs.length > 0 && published.length > 0 && <div style={{ height: 8 }} />}
        {published.map(p => (
          <div key={p.id} style={{ marginBottom: 8, padding: '10px 12px', background: '#E8EDE9', borderRadius: 3 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
              {canEdit
                ? <InlineInput value={p.what || p.title || ''} onBlurSave={v => updatePublished(p.id, { what: v })} placeholder="Publication or piece title…" style={{ flex: 1, fontWeight: 500 }} />
                : <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: '#1B1B19' }}>{p.what || p.title || 'Untitled piece'}</span>
              }
              {canEdit
                ? <InlineInput value={p.when || p.year || ''} onBlurSave={v => updatePublished(p.id, { when: v })} placeholder="Year…" style={{ width: 70, fontSize: 12 }} />
                : (p.when || p.year) ? <span style={S.meta}>· {p.when || p.year}</span> : null
              }
              {p.link && <a href={p.link} target="_blank" rel="noreferrer" style={{ color: '#21302A', flexShrink: 0 }}><ExternalLink size={12} /></a>}
              {canEdit && <button onClick={() => deletePublished(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 2, flexShrink: 0 }}><Trash2 size={12} /></button>}
            </div>
            <InlineInput
              value={p.link || ''}
              onBlurSave={v => updatePublished(p.id, { link: v })}
              placeholder="Link to the published piece…"
            />
          </div>
        ))}
        {canEdit && (
          <AddLine>
            <InlineInput value={newPubTitle} onChange={setNewPubTitle} placeholder="Add a published piece — title…" />
            <BtnGhost onClick={addPublished}>Add</BtnGhost>
          </AddLine>
        )}
      </div>

      {/* Notes */}
      <div style={{ marginBottom: 28 }}>
        <BlockTitle left="Notes" />
        <NoteArea value={profile?.notes || ''} onChange={v => saveProfile('notes', v)} placeholder="Notes…" minHeight={64} saveOnBlur />
      </div>

      {/* Contact persons list with Primary / Secondary marking */}
      {persons.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <BlockTitle left="Contacts" right={canEdit ? 'click to set primary / secondary' : ''} />
          {persons.map(p => {
            const isPrimary = p.id === primaryId;
            const isSecondary = p.id === secondaryId;
            return (
              <div
                key={p.id}
                style={{
                  display: 'flex', gap: 12, padding: '10px 14px', marginBottom: 6,
                  background: isPrimary ? '#E8EDE9' : '#F7F6F2',
                  borderRadius: 4,
                  border: isPrimary ? '1px solid #33473F' : isSecondary ? '1px solid #6E6C66' : '1px solid rgba(27,27,25,.08)',
                  alignItems: 'center',
                  cursor: canEdit ? 'pointer' : 'default',
                }}
                onClick={() => canEdit && cycleContact(p.id)}
              >
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 14, color: '#1B1B19', fontWeight: isPrimary ? 600 : 400 }}>{p.name}</span>
                  {p.designation && <span style={{ ...S.meta, marginLeft: 8 }}>{p.designation}</span>}
                  {p.email && <div style={{ fontSize: 12, color: '#21302A', marginTop: 2 }}>{p.email}</div>}
                </div>
                {isPrimary && (
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', background: '#33473F', color: '#fff', padding: '2px 8px', borderRadius: 3, flexShrink: 0 }}>Primary</span>
                )}
                {isSecondary && (
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6E6C66', border: '1px solid #6E6C66', padding: '2px 8px', borderRadius: 3, flexShrink: 0 }}>Secondary</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {saving && <div style={{ position: 'fixed', bottom: 16, right: 16, background: '#33473F', color: '#fff', padding: '6px 14px', borderRadius: 4, fontSize: 12 }}>Saving…</div>}
    </div>
  );
}

// ── INBOX TAB ─────────────────────────────────────────────────────────────

function InboxTab({ data, canEdit, refresh, showToast }) {
  const [sub, setSub] = useState('inbox');
  const [newTask, setNewTask] = useState('');

  const inbox = data.inbox || [];
  const rtasks = data.rtasks || [];
  const practice = data.practice || {};

  const pressInboxUrl = practice.press_inbox_url || '#';

  const cycleTaskStatus = async (task) => {
    const statuses = ['New', 'In progress', 'Done'];
    const idx = statuses.indexOf(task.status);
    const next = statuses[(idx + 1) % statuses.length];
    try { await commsUpdate('rtasks', 'comms_id', task.comms_id, { status: next }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  const deleteTask = async id => {
    try { await commsDelete('rtasks', 'comms_id', id); refresh(); showToast('Deleted'); } catch (e) { showToast(e.message, 'error'); }
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    try { await commsInsert('rtasks', { comms_id: 'rt-' + Date.now(), title: newTask.trim(), status: 'New' }); setNewTask(''); refresh(); showToast('Task added'); } catch (e) { showToast(e.message, 'error'); }
  };

  const cycleInboxStatus = async (item) => {
    const statuses = ['New', 'In progress', 'Done'];
    const idx = statuses.indexOf(item.tracked_status);
    const next = statuses[(idx + 1) % statuses.length];
    try { await commsUpdate('inbox', 'comms_id', item.comms_id, { tracked_status: next }); refresh(); } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ ...S.eyebrow, marginBottom: 6 }}>Inbox & tasks</div>
        <h1 style={S.h1}>Inbox</h1>
        <p style={{ ...S.body, marginTop: 10, maxWidth: 560 }}>A tracker for managing connections. <strong>Press inbox</strong> turns incoming enquiries into trackable tasks; <strong>Outreach tasks</strong> collects who we need to reach out to, fed from contacts and the calendar.</p>
      </div>
      <div style={S.filterRow}>
        <FilterBtn label="Press inbox" active={sub === 'inbox'} onClick={() => setSub('inbox')} />
        <FilterBtn label={`Outreach tasks (${rtasks.length})`} active={sub === 'tasks'} onClick={() => setSub('tasks')} />
      </div>

      {sub === 'inbox' && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <a href={pressInboxUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              <BtnGhost>Open the press inbox</BtnGhost>
            </a>
          </div>
          {inbox.length === 0 && <EmptyNote text="No inbox items tracked." />}
          {inbox.map(item => (
            <details key={item.comms_id} style={{ ...S.card, cursor: 'pointer' }}>
              <summary style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', listStyle: 'none', cursor: 'pointer' }}>
                <span style={S.meta}>{[item.from_name, item.from_email].filter(Boolean).join(' · ')}</span>
                <span style={{ fontSize: 14, color: '#1B1B19', flex: 1 }}>{item.subject || '(no subject)'}</span>
                <span style={S.meta}>{item.received_at}</span>
              </summary>
              <div style={{ marginTop: 16 }}>
                {item.body && <p style={{ ...S.body, marginBottom: 16 }}>{item.body}</p>}
                <div style={{ borderTop: '1px solid rgba(27,27,25,.08)', paddingTop: 12 }}>
                  <div style={{ ...S.eyebrow, marginBottom: 8 }}>Tracked as task</div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span style={{ flex: 1, fontSize: 13 }}>{item.action_text || '—'}</span>
                    <Chip label={item.tracked_status || 'New'} cls={statusClass(item.tracked_status)} onClick={() => cycleInboxStatus(item)} />
                  </div>
                </div>
              </div>
            </details>
          ))}
        </div>
      )}

      {sub === 'tasks' && (
        <div>
          <p style={{ ...S.body, marginBottom: 20 }}>Who we need to reach out to — fed from contacts, events, awards and open calls.</p>
          {rtasks.length === 0 && <EmptyNote text="No outreach tasks yet. Use 'Send to outreach' on a contact, event, award or open call." />}
          {rtasks.map(task => (
            <div key={task.comms_id} style={S.card}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                  {task.cid && <span style={{ ...S.meta, marginBottom: 4, display: 'block' }}>Contact linked</span>}
                  <span style={{ fontSize: 15, color: '#1B1B19' }}>{task.title}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Chip label={task.status || 'New'} cls={statusClass(task.status)} onClick={() => cycleTaskStatus(task)} />
                  <button onClick={() => deleteTask(task.comms_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9A988F', padding: 2 }}>×</button>
                </div>
              </div>
              <InlineInput value={task.notes || ''} onBlurSave={async v => { try { await commsUpdate('rtasks', 'comms_id', task.comms_id, { notes: v }); } catch {} }} placeholder="Why / what to say…" style={{ marginTop: 10 }} />
            </div>
          ))}
          <AddLine>
            <InlineInput value={newTask} onChange={setNewTask} placeholder="Who to reach out to…" />
            <BtnGhost onClick={addTask}>+ Add</BtnGhost>
          </AddLine>
        </div>
      )}
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────

export default function CommsPage({ currentUser, isAdmin, subPage, setSubPage, openForInquiry, onOpenVendorRegister }) {
  const tab = subPage || 'comms-practice';
  // Comms content editing (every comms.* table) is open to any authenticated
  // user by RLS (USING (true) WITH CHECK (true)), and the whole page is already
  // access-gated upstream in App.js — so anyone who can open Comms may edit
  // content. Global configuration that writes public.app_settings (press-kit
  // folder schema, practice folder tree, asset-hashtag vocabulary, approved
  // subfolders) is admin-only at the RLS layer, so it keeps a tighter gate.
  const canEdit = !!currentUser;
  const canConfigure = isAdmin;
  const [rev, setRev] = useState(0);
  const refresh = useCallback(() => setRev(r => r + 1), []);
  const { data, loading } = useCommsData(tab, rev);
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => setToast({ msg }), []);
  const [confirm, setConfirm] = useState(null);

  const [detailView, setDetailView] = useState(null);

  useEffect(() => { setDetailView(null); }, [tab]);

  if (detailView?.type === 'project') {
    return (
      <>
        <ProjectDetail id={detailView.id} data={data} onBack={() => setDetailView(null)} canEdit={canEdit} canConfigure={canConfigure} refresh={refresh} showToast={showToast} currentUser={currentUser} onOpenVendorRegister={onOpenVendorRegister} />
        {toast && <Toast message={toast.msg} onClose={() => setToast(null)} />}
      </>
    );
  }
  if (detailView?.type === 'contact') {
    return (
      <>
        <ContactDetail id={detailView.id} data={data} onBack={() => setDetailView(null)} canEdit={canEdit} refresh={refresh} showToast={showToast} />
        {toast && <Toast message={toast.msg} onClose={() => setToast(null)} />}
      </>
    );
  }
  if (detailView?.type === 'theme') {
    return (
      <>
        <ThemeDetail id={detailView.id} data={data} onBack={() => setDetailView(null)} canEdit={canEdit} canConfigure={canConfigure} refresh={refresh} showToast={showToast} currentUser={currentUser} />
        {toast && <Toast message={toast.msg} onClose={() => setToast(null)} />}
      </>
    );
  }
  if (detailView?.type === 'programme') {
    return (
      <>
        <ProgrammeDetail id={detailView.id} data={data} onBack={() => setDetailView(null)} canEdit={canEdit} refresh={refresh} showToast={showToast} />
        {toast && <Toast message={toast.msg} onClose={() => setToast(null)} />}
      </>
    );
  }

  return (
    <div style={S.page}>
      {loading
        ? <div style={{ padding: 80, color: '#9A988F', fontSize: 13, textAlign: 'center' }}>Loading…</div>
        : <>
          {tab === 'comms-practice'  && <PracticeTab  data={data} canEdit={canEdit} refresh={refresh} showToast={showToast} onOpenTheme={id => setDetailView({ type: 'theme', id })} onOpenProgramme={id => setDetailView({ type: 'programme', id })} />}
          {tab === 'comms-projects'  && <ProjectsTab  data={data} canEdit={canEdit} rev={rev} refresh={refresh} showToast={showToast} onOpenProject={id => setDetailView({ type: 'project', id })} />}
          {tab === 'comms-social'    && <SocialTab    data={data} canEdit={canEdit} refresh={refresh} showToast={showToast} />}
          {tab === 'comms-calendar'  && <CalendarTab  data={data} canEdit={canEdit} refresh={refresh} showToast={showToast} />}
          {tab === 'comms-contacts'  && <ContactsTab  data={data} canEdit={canEdit} refresh={refresh} showToast={showToast} onOpenContact={id => setDetailView({ type: 'contact', id })} />}
          {tab === 'comms-inbox'     && <InboxTab     data={data} canEdit={canEdit} refresh={refresh} showToast={showToast} />}
          {tab === 'comms-pitches'   && <PitchesTab   data={data} canEdit={canEdit} refresh={refresh} showToast={showToast} openForInquiry={openForInquiry} />}
        </>
      }
      {toast && <Toast message={toast.msg} onClose={() => setToast(null)} />}
      {confirm && <ConfirmDialog message={confirm.message} onConfirm={() => { confirm.onConfirm(); setConfirm(null); }} onCancel={() => setConfirm(null)} />}
    </div>
  );
}
